import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import * as $3Dmol from '3dmol';
import { Routes, Route, Navigate, useNavigate, useParams } from 'react-router';
import { useAuth } from './context/AuthContext';
import LoginPage from './views/LoginPage';
import './App.css';
import AppProvider from './context/AppProvider';
import { useAppContext } from './context/AppContext';
import Header from './components/layout/Header';
import PilotExpirationBanner from './components/PilotExpirationBanner';
import Footer from './components/layout/Footer';
import LandingPage from './views/LandingPage';
import SearchPage from './views/SearchPage';
import ResultsPage from './views/ResultsPage';
import { buildPatentPrintHTML } from './pdf/builders';
import { authedFetch, authHeadersForUrl } from './ai/authedFetch';
import { patentSearchStatus, patentPriorArtContext } from './ai/patentStatus';
import { PATENT_PACKET_SECTIONS, planPatentPacketGeneration, reuseSectionResult, buildPartialPacket, assemblePatentPacket } from './ai/patentPacketAssembly';
import { AnalyzeDropdownItem } from './components/AnalyzeDropdownItem';
import AdminPortal from './views/AdminPortal';
import EnrollPage from './views/EnrollPage';
import DocsPage from './views/DocsPage';
import PublicLanding from './views/public/PublicLanding';
import HowItWorksPage from './views/public/HowItWorksPage';
import PricingPage from './views/public/PricingPage';
import SignupCompletePage from './views/public/SignupCompletePage';
import useOrgRole from './hooks/useOrgRole';
import MattergenRunner from './components/mattergen/MattergenRunner';
import FM4MRunner from './components/fm4m/FM4MRunner';
import GT4SDRunner from './components/gt4sd/GT4SDRunner';
import LoadingOverlay from './components/LoadingOverlay';
import Modal from './components/Modal';
import CodeJson from './components/CodeJson';
import CrystalViewer3D from './components/CrystalViewer3D';
import MoleculeTable from './components/MoleculeTable';
import FeedbackWidget from './components/FeedbackWidget';
import { CandidateNotes } from './components/CandidateNotes';
import { askLLM } from './ai/askLLM';
import { loadAnalyses, loadCandidateByFingerprint as dbLoadCandidateByFingerprint } from './firebase/db';
import { downloadCrystalAll } from './pdf/download';
import { saveAs } from 'file-saver';
import { extxyzRemountKey } from './utils/extxyzRemountKey';
import LatticeWizard from './components/LatticeWizard';
import { buildLatticeProps } from './utils/latticeReadiness';
import { registerJob, getJob, clearJob } from './utils/dftJobRegistry';
import { findJobsForCandidate } from './utils/dftJobLookup';
import { useElapsed } from './hooks/useElapsed';
import { useCapability, useJobLookupScope } from './hooks/useCapability';
import { capabilityForAnalysisKind } from './config/capabilities';
import { capabilityDeniedMessage, deniedMessageFor } from './utils/capabilityErrors';
import { phaseStabilityEnergy, phaseStabilitySourceLabel } from './utils/phaseStabilityInput';

// Atomic masses for density calculation (subset covering common elements)
const _AM = {H:1.008,He:4.003,Li:6.941,Be:9.012,B:10.81,C:12.01,N:14.01,O:16.00,F:19.00,Ne:20.18,Na:22.99,Mg:24.31,Al:26.98,Si:28.09,P:30.97,S:32.07,Cl:35.45,K:39.10,Ca:40.08,Sc:44.96,Ti:47.87,V:50.94,Cr:52.00,Mn:54.94,Fe:55.85,Co:58.93,Ni:58.69,Cu:63.55,Zn:65.38,Ga:69.72,Ge:72.63,As:74.92,Se:78.97,Br:79.90,Rb:85.47,Sr:87.62,Y:88.91,Zr:91.22,Nb:92.91,Mo:95.95,Ru:101.1,Rh:102.9,Pd:106.4,Ag:107.9,Cd:112.4,In:114.8,Sn:118.7,Sb:121.8,Te:127.6,I:126.9,Cs:132.9,Ba:137.3,La:138.9,Ce:140.1,Hf:178.5,Ta:180.9,W:183.8,Re:186.2,Os:190.2,Ir:192.2,Pt:195.1,Au:197.0,Pb:207.2,Bi:209.0,Th:232.0,U:238.0};
// Empirical atomic radii (Å) for packing fraction
const _AR = {H:0.25,Li:1.45,Be:1.05,B:0.85,C:0.70,N:0.65,O:0.60,F:0.50,Na:1.80,Mg:1.50,Al:1.25,Si:1.10,P:1.00,S:1.00,Cl:1.00,K:2.20,Ca:1.80,Sc:1.60,Ti:1.40,V:1.35,Cr:1.40,Mn:1.40,Fe:1.40,Co:1.35,Ni:1.35,Cu:1.35,Zn:1.35,Ga:1.30,Ge:1.25,As:1.15,Se:1.15,Br:1.15,Rb:2.35,Sr:2.00,Y:1.80,Zr:1.55,Nb:1.45,Mo:1.45,Ru:1.30,Rh:1.35,Pd:1.40,Ag:1.60,Cd:1.55,In:1.55,Sn:1.45,Sb:1.45,Te:1.40,I:1.40,Cs:2.60,Ba:2.15,La:1.95,Ce:1.85,Hf:1.55,Ta:1.45,W:1.35,Re:1.35,Os:1.30,Ir:1.35,Pt:1.35,Au:1.35,Pb:1.80,Bi:1.60,Th:1.80,U:1.75};

function _cellVolume(mat) {
  const [a, b, c] = mat;
  const cx = b[1]*c[2]-b[2]*c[1], cy = b[2]*c[0]-b[0]*c[2], cz = b[0]*c[1]-b[1]*c[0];
  return Math.abs(a[0]*cx + a[1]*cy + a[2]*cz);
}

function _minDist(positions) {
  if (!positions || positions.length < 2) return null;
  let min = Infinity;
  const n = Math.min(positions.length, 100);
  for (let i = 0; i < n; i++) for (let j = i+1; j < n; j++) {
    const dx = positions[i][0]-positions[j][0], dy = positions[i][1]-positions[j][1], dz = positions[i][2]-positions[j][2];
    const d = Math.sqrt(dx*dx+dy*dy+dz*dz);
    if (d > 0 && d < min) min = d;
  }
  return Number.isFinite(min) ? Math.round(min*1000)/1000 : null;
}

// Gaussian quality: how close val is to ideal center, with spread sigma
function _gauss(val, center, sigma) {
  if (val == null) return 0;
  const d = (val - center) / sigma;
  return Math.exp(-0.5 * d * d);
}

function _scoreFrames(frames) {
  // Absolute quality scores per metric (Gaussian around ideal values).
  // Each metric yields 0–1 based on proximity to the ideal; the weighted
  // composite is an absolute quality indicator independent of batch size.
  // minDist: ideal ~1.8Å for inorganic bonds; packing: ideal ~0.68 (close-packed); density: broad range centered ~5 g/cm³
  frames.forEach((f) => {
    const md = _gauss(f.minDist, 1.8, 0.7);
    const pk = _gauss(f.packingFraction, 0.68, 0.15);
    const dn = _gauss(f.density, 5.0, 4.0);
    // Weighted composite: minDist 40%, packing 35%, density 25%
    f.score = md * 0.40 + pk * 0.35 + dn * 0.25;
  });
}

function parseExtxyzFrames(text) {
  const lines = text.split(/\r?\n/);
  const frames = [];
  let i = 0;
  while (i < lines.length) {
    const n = parseInt(lines[i], 10);
    if (!Number.isFinite(n) || n <= 0) { i++; continue; }
    const commentIdx = i + 1;
    const endIdx = commentIdx + 1 + n;
    if (endIdx > lines.length) break;
    const comment = lines[commentIdx] || '';
    const atomLines = lines.slice(commentIdx + 1, endIdx);
    const comp = {};
    const elems = [];
    const positions = [];
    for (const al of atomLines) {
      const toks = al.trim().split(/\s+/);
      const m = (toks[0] || '').match(/^[A-Z][a-z]?/);
      if (m) {
        comp[m[0]] = (comp[m[0]] || 0) + 1;
        elems.push(m[0]);
        const x = Number(toks[1]), y = Number(toks[2]), z = Number(toks[3]);
        if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)) positions.push([x,y,z]);
      }
    }
    const formula = Object.keys(comp).sort().map(k => `${k}${comp[k] > 1 ? comp[k] : ''}`).join('');

    // Parse lattice matrix from comment
    let lattice = null;
    try {
      const lm = comment.match(/Lattice\s*=\s*"([^"]+)"/);
      if (lm && lm[1]) {
        const p = lm[1].trim().split(/\s+/).map(Number);
        if (p.length === 9 && p.every(Number.isFinite)) lattice = [[p[0],p[1],p[2]],[p[3],p[4],p[5]],[p[6],p[7],p[8]]];
      }
    } catch (_) {}

    // Parse space group from comment
    let spaceGroup = null;
    try {
      const sg1 = comment.match(/space[_ ]?group\s*=\s*"([^"]+)"/i);
      if (sg1 && sg1[1]) spaceGroup = sg1[1].trim();
      if (!spaceGroup) { const sg2 = comment.match(/\bSpaceGroup\b\s*=\s*"([^"]+)"/); if (sg2 && sg2[1]) spaceGroup = sg2[1].trim(); }
    } catch (_) {}

    // Compute structural metrics
    let density = null, packingFraction = null, vol = null;
    if (lattice) {
      vol = _cellVolume(lattice);
      if (vol > 0) {
        let totalMass = 0, sphereVol = 0;
        for (const el of elems) { totalMass += _AM[el] || 0; const r = _AR[el]; if (r) sphereVol += (4/3)*Math.PI*r*r*r; }
        density = Math.round((totalMass * 1.66054e-24) / (vol * 1e-24) * 1000) / 1000;
        if (sphereVol > 0) packingFraction = Math.round((sphereVol / vol) * 10000) / 10000;
      }
    }
    const minDist = _minDist(positions);
    // Lattice vector magnitudes for legacy fingerprint reconstruction
    let lattice_abc = null;
    if (lattice) {
      lattice_abc = lattice.map(v => Math.round(Math.sqrt(v[0]*v[0]+v[1]*v[1]+v[2]*v[2]) * 100) / 100);
    }
    frames.push({ index: frames.length, n, comment, atomLines, formula, spaceGroup, lattice_matrix: lattice, lattice_abc, density, packingFraction, minDist, score: 0 });
    i = endIdx;
  }
  // Absolute structural quality scoring (Gaussian proximity to ideal values)
  _scoreFrames(frames);
  // Sort by score descending (best first) and assign rank
  frames.sort((a, b) => b.score - a.score);
  frames.forEach((f, idx) => { f.rank = idx + 1; });
  return frames;
}

/**
 * Convert DB candidate documents to ExtxyzTable-compatible frames.
 * Reconstructs atomLines, comment, and structural metrics from persisted data.
 */
function candidatesToFrames(candidates) {
  const frames = candidates.map((c) => {
    const fr = c.frame || {};
    const elements = c.elements || fr.elements || [];
    const positions = c.positions || fr.positions || [];
    const lattice_matrix = c.lattice_matrix || fr.lattice_matrix || null;
    const n = c.n || fr.n || elements.length;

    // Reconstruct atomLines from elements + positions
    const atomLines = elements.map((el, j) => {
      const pos = positions[j] || [0, 0, 0];
      return `${el}  ${pos[0]}  ${pos[1]}  ${pos[2]}`;
    });

    // Reconstruct comment with Lattice string
    let comment = '';
    if (lattice_matrix && lattice_matrix.length === 3) {
      const flat = lattice_matrix.flat();
      comment = `Lattice="${flat.join(' ')}" Properties=species:S:1:pos:R:3`;
    }

    // Compute structural metrics (same logic as parseExtxyzFrames)
    let density = null, packingFraction = null;
    if (lattice_matrix) {
      const vol = _cellVolume(lattice_matrix);
      if (vol > 0) {
        let totalMass = 0, sphereVol = 0;
        for (const el of elements) {
          totalMass += _AM[el] || 0;
          const r = _AR[el];
          if (r) sphereVol += (4 / 3) * Math.PI * r * r * r;
        }
        density = Math.round((totalMass * 1.66054e-24) / (vol * 1e-24) * 1000) / 1000;
        if (sphereVol > 0) packingFraction = Math.round((sphereVol / vol) * 10000) / 10000;
      }
    }
    const minDist = _minDist(positions);
    const comp = c.composition || fr.composition || {};
    const formula = c.formula || Object.keys(comp).sort()
      .map(k => `${k}${comp[k] > 1 ? comp[k] : ''}`).join('');

    return {
      index: 0, // assigned below after sort
      n,
      comment,
      atomLines,
      formula,
      spaceGroup: c.space_group || fr.space_group || c.source?.space_group || null,
      lattice_matrix,
      lattice_abc: c.lattice_abc || fr.lattice_abc || null,
      density,
      packingFraction,
      minDist,
      score: c.score || 0,
      rank: 0,
      // Carry CIF text for the 3D viewer (avoids a separate fetch)
      cif: c.cif || fr.cif || null,
      // Spec 0.7: this candidate's own CIF / trajectory URLs (bulk capture stores them in
      // source). The frames are re-sorted by score below, so the caller's job-order file
      // lists cannot be indexed by position.
      cifUrl: c.source?.cif_url || null,
      trajUrl: c.source?.traj_url || null,
      // Legacy: carry the old DB fingerprint for backward-compat analysis lookups only
      _legacyFp: c.fingerprint || null,
      // Materials Project match metadata (stamped by enrichment)
      mp_match: c.mp_match || null,
    };
  });

  // Compute structural quality scores for display/sorting only.
  // Fingerprints use formula+spaceGroup, so scores don't affect identity.
  _scoreFrames(frames);
  frames.sort((a, b) => b.score - a.score);
  frames.forEach((f, idx) => { f.index = idx; f.rank = idx + 1; });
  return frames;
}

// downloadCrystalAll is imported from src/pdf/download.js

function CrystalDownloadBtn({ frame, cifUrl, trajUrl, cachedAnalyses, cifText, fetchFn }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      className="btn btn-outline-light btn-sm"
      disabled={busy}
      onClick={async () => { setBusy(true); try { await downloadCrystalAll(frame, cifUrl, trajUrl, cachedAnalyses, cifText, fetchFn); } catch (_) {} setBusy(false); }}
    >{busy ? 'Zipping\u2026' : 'Download All'}</button>
  );
}

function SymmetryPanel({ data, loading }) {
  if (loading) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#0f1923' }}>Running symmetry analysis…</div>;
  if (!data) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#0f1923' }}>No data.</div>;
  if (!data.ok) return <div style={{ padding: '12px 16px', color: '#f87171', background: '#0f1923' }}>Error: {data.error || 'Unknown'}</div>;
  const s = { fontSize: 13, padding: '12px 16px', background: '#0f1923', color: '#e2e8f0' };
  return (
    <div style={s}>
      <div className="d-flex flex-wrap gap-4 mb-3">
        <div>
          <div style={{ color: '#94a3b8', fontSize: 12 }}>Space Group</div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{data.space_group_symbol} <span style={{ color: '#94a3b8' }}>(#{data.space_group_number})</span></div>
        </div>
        <div>
          <div style={{ color: '#94a3b8', fontSize: 12 }}>Crystal System</div>
          <div style={{ fontWeight: 600 }}>{data.crystal_system}</div>
        </div>
        <div>
          <div style={{ color: '#94a3b8', fontSize: 12 }}>Point Group</div>
          <div style={{ fontWeight: 600 }}>{data.point_group}</div>
        </div>
        <div>
          <div style={{ color: '#94a3b8', fontSize: 12 }}>Hall Symbol</div>
          <div style={{ fontWeight: 600 }}>{data.hall_symbol}</div>
        </div>
        <div>
          <div style={{ color: '#94a3b8', fontSize: 12 }}>Symmetry Ops</div>
          <div style={{ fontWeight: 600 }}>{data.n_symmetry_ops}</div>
        </div>
        <div>
          <div style={{ color: '#94a3b8', fontSize: 12 }}>Formula</div>
          <div style={{ fontWeight: 600 }}>{data.formula}</div>
        </div>
        <div>
          <div style={{ color: '#94a3b8', fontSize: 12 }}>Atoms</div>
          <div style={{ fontWeight: 600 }}>{data.n_atoms}</div>
        </div>
        {data.is_laue != null && (
          <div>
            <div style={{ color: '#94a3b8', fontSize: 12 }}>Laue</div>
            <div style={{ fontWeight: 600 }}>{data.is_laue ? 'Yes' : 'No'}</div>
          </div>
        )}
      </div>
      {data.lattice && (
        <div className="mb-3">
          <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 4 }}>Lattice Parameters</div>
          <div className="d-flex gap-3 flex-wrap" style={{ fontSize: 12 }}>
            <span><strong>a</strong> = {data.lattice.a} Å</span>
            <span><strong>b</strong> = {data.lattice.b} Å</span>
            <span><strong>c</strong> = {data.lattice.c} Å</span>
            <span><strong>α</strong> = {data.lattice.alpha}°</span>
            <span><strong>β</strong> = {data.lattice.beta}°</span>
            <span><strong>γ</strong> = {data.lattice.gamma}°</span>
            <span><strong>V</strong> = {data.lattice.volume} ų</span>
          </div>
        </div>
      )}
      {data.wyckoff_sites && data.wyckoff_sites.length > 0 && (
        <div>
          <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 4 }}>Wyckoff Sites</div>
          <table className="table table-dark table-sm mb-0" style={{ fontSize: 12, maxWidth: 600 }}>
            <thead><tr><th>Element</th><th>Wyckoff</th><th>Mult.</th><th>Fractional Coords</th></tr></thead>
            <tbody>
              {data.wyckoff_sites.map((w, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 600 }}>{w.element}</td>
                  <td>{w.wyckoff_letter}</td>
                  <td>{w.multiplicity}</td>
                  <td style={{ fontFamily: 'monospace' }}>{w.frac_coords?.map(c => c.toFixed(4)).join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function VoronoiPanel({ data, loading }) {
  if (loading) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#0f1923' }}>Running Voronoi analysis…</div>;
  if (!data) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#0f1923' }}>No data.</div>;
  if (!data.ok) return <div style={{ padding: '12px 16px', color: '#f87171', background: '#0f1923' }}>Error: {data.error || 'Unknown'}</div>;
  const s = { fontSize: 13, padding: '12px 16px', background: '#0f1923', color: '#e2e8f0' };
  return (
    <div style={s}>
      <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}><strong>{data.formula}</strong> — {data.n_atoms} atoms</div>
      {data.coordination_summary && data.coordination_summary.length > 0 && (
        <div className="mb-3">
          <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 4 }}>Coordination Summary</div>
          <table className="table table-dark table-sm mb-0" style={{ fontSize: 12, maxWidth: 700 }}>
            <thead><tr><th>#</th><th>Element</th><th>CN</th><th>Neighbors</th></tr></thead>
            <tbody>
              {data.coordination_summary.map((c, i) => (
                <tr key={i}>
                  <td>{c.index}</td>
                  <td style={{ fontWeight: 600 }}>{c.element}</td>
                  <td style={{ fontWeight: 700, color: '#38bdf8' }}>{c.coordination_number}</td>
                  <td>{Object.entries(c.neighbor_counts || {}).map(([el, n]) => `${el}×${n}`).join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data.neighbors && data.neighbors.length > 0 && (
        <details>
          <summary style={{ color: '#94a3b8', fontSize: 12, cursor: 'pointer' }}>All neighbor pairs ({data.neighbors.length})</summary>
          <table className="table table-dark table-sm mb-0 mt-1" style={{ fontSize: 11, maxWidth: 700 }}>
            <thead><tr><th>Center</th><th>Neighbor</th><th>Distance (Å)</th><th>Solid Angle</th></tr></thead>
            <tbody>
              {data.neighbors.map((n, i) => (
                <tr key={i}>
                  <td>{n.center_element}[{n.center_index}]</td>
                  <td>{n.neighbor_element}[{n.neighbor_index}]</td>
                  <td style={{ fontFamily: 'monospace' }}>{n.distance}</td>
                  <td style={{ fontFamily: 'monospace' }}>{n.solid_angle}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}

function BondDistPanel({ data, loading }) {
  if (loading) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#0f1923' }}>Running bond distribution analysis…</div>;
  if (!data) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#0f1923' }}>No data.</div>;
  if (!data.ok) return <div style={{ padding: '12px 16px', color: '#f87171', background: '#0f1923' }}>Error: {data.error || 'Unknown'}</div>;
  const s = { fontSize: 13, padding: '12px 16px', background: '#0f1923', color: '#e2e8f0' };
  const maxCount = data.histogram ? Math.max(...data.histogram.map(h => h.count), 1) : 1;
  return (
    <div style={s}>
      <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}><strong>{data.formula}</strong> — {data.n_atoms} atoms</div>
      {data.stats && (
        <div className="d-flex flex-wrap gap-4 mb-3">
          <div><div style={{ color: '#94a3b8', fontSize: 12 }}>Bonds</div><div style={{ fontWeight: 700 }}>{data.stats.count}</div></div>
          <div><div style={{ color: '#94a3b8', fontSize: 12 }}>Min</div><div style={{ fontWeight: 600 }}>{data.stats.min} Å</div></div>
          <div><div style={{ color: '#94a3b8', fontSize: 12 }}>Max</div><div style={{ fontWeight: 600 }}>{data.stats.max} Å</div></div>
          <div><div style={{ color: '#94a3b8', fontSize: 12 }}>Mean</div><div style={{ fontWeight: 600 }}>{data.stats.mean} Å</div></div>
          <div><div style={{ color: '#94a3b8', fontSize: 12 }}>Std</div><div style={{ fontWeight: 600 }}>{data.stats.std} Å</div></div>
        </div>
      )}
      {data.bond_pairs && data.bond_pairs.length > 0 && (
        <div className="mb-3">
          <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 4 }}>Per-Pair Statistics</div>
          <table className="table table-dark table-sm mb-0" style={{ fontSize: 12, maxWidth: 600 }}>
            <thead><tr><th>Pair</th><th>Count</th><th>Min (Å)</th><th>Max (Å)</th><th>Mean (Å)</th></tr></thead>
            <tbody>
              {data.bond_pairs.map((bp, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 600 }}>{bp.pair}</td>
                  <td>{bp.count}</td>
                  <td style={{ fontFamily: 'monospace' }}>{bp.min}</td>
                  <td style={{ fontFamily: 'monospace' }}>{bp.max}</td>
                  <td style={{ fontFamily: 'monospace' }}>{bp.mean}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data.histogram && data.histogram.length > 0 && (
        <div>
          <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 4 }}>Bond Length Histogram</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 80 }}>
            {data.histogram.map((h, i) => (
              <div key={i} title={`${h.bin_start}–${h.bin_end} Å: ${h.count}`}
                style={{ flex: 1, background: '#38bdf8', minWidth: 4, borderRadius: '2px 2px 0 0',
                  height: `${Math.max(2, (h.count / maxCount) * 100)}%`, opacity: h.count ? 1 : 0.2 }} />
            ))}
          </div>
          <div className="d-flex justify-content-between" style={{ fontSize: 10, color: '#94a3b8' }}>
            <span>{data.histogram[0]?.bin_start} Å</span>
            <span>{data.histogram[data.histogram.length - 1]?.bin_end} Å</span>
          </div>
        </div>
      )}
    </div>
  );
}

const POWERED_FOOTER = `<div style="margin-top:28pt;padding-top:8pt;border-top:0.5pt solid #ccc;text-align:center;font-size:8pt;color:#999">
  <span class="cs-brand" style="font-weight:700;letter-spacing:0.04em;color:#e11d48">Powered by CrimsonScan</span>
  <span style="margin-left:4pt">· crimsonscan.com · AI-Driven Materials Discovery</span>
</div>`;

function printPanel(ref) {
  if (!ref?.current) return;
  const html = ref.current.innerHTML;
  const w = window.open('', '_blank');
  w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><style>
    body{font-family:Georgia,'Times New Roman',serif;font-size:11pt;color:#111;background:#fff;padding:0.75in 1in}
    table{width:100%;border-collapse:collapse;font-size:9.5pt;margin:8pt 0}
    th{background:#f2f2f2;padding:4pt 8pt;text-align:left;border:0.5pt solid #bbb;font-weight:700}
    td{padding:4pt 8pt;border:0.5pt solid #ccc}
    /* strip dark-theme colours */
    *{color:#111!important;background:#fff!important;border-color:#ccc!important}
    .cs-brand{color:#e11d48!important}
    .no-print{display:none!important}
  </style></head><body>${html}${POWERED_FOOTER}</body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => { w.print(); }, 400);
}

function EconomicReportPanel({ data, loading }) {
  const printRef = useRef(null);
  if (loading) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#0f1923' }}>Generating economic report…</div>;
  if (!data) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#0f1923' }}>No data.</div>;
  // If LLM failed but we have quantitative metrics, fall through to render them
  if (!data.ok && !data.economic_metrics) return <div style={{ padding: '12px 16px', color: '#f87171', background: '#0f1923' }}>Error: {data.error || 'Unknown'}</div>;
  const s = { fontSize: 13, padding: '12px 16px', background: '#0f1923', color: '#e2e8f0' };
  const complexityColor = { low: '#4ade80', medium: '#facc15', high: '#f87171' };
  const riskColor = { low: '#4ade80', medium: '#facc15', high: '#f87171' };
  const em = data.economic_metrics;
  const volColor = { High: '#f87171', Medium: '#facc15', Low: '#4ade80' };
  const recColor = { High: '#4ade80', Medium: '#facc15', Low: '#f87171' };
  const sciNum = em ? parseFloat(em.supply_concentration_index) : null;
  const sciColor = sciNum != null ? (sciNum > 0.65 ? '#f87171' : sciNum > 0.40 ? '#facc15' : '#4ade80') : '#94a3b8';

  return (
    <div style={s} ref={printRef}>
      <div className="d-flex justify-content-end mb-2 no-print">
        <button onClick={() => printPanel(printRef)} style={{ background: 'transparent', color: '#a78bfa', border: '1px solid #7c3aed', borderRadius: 4, padding: '3px 10px', fontSize: 11, cursor: 'pointer' }}>Print / Export PDF ↗</button>
      </div>
      {em && (
        <div className="mb-3" style={{ padding: '12px 16px', background: '#0a1120', border: '1px solid #1e293b', borderRadius: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: '#94a3b8', textTransform: 'uppercase', marginBottom: 10 }}>
            Economic Metrics <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(live data)</span>
          </div>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <tbody>
              <tr>
                <td style={{ color: '#94a3b8', fontSize: 12, paddingBottom: 7, paddingRight: 20, whiteSpace: 'nowrap', width: '38%' }}>Estimated BOM Cost</td>
                <td style={{ fontSize: 12, fontFamily: 'monospace', paddingBottom: 7, fontWeight: 600, color: '#e2e8f0' }}>
                  {em.cost_low} – {em.cost_high} <span style={{ color: '#94a3b8', fontWeight: 400 }}>/ kg</span>
                </td>
              </tr>
              <tr>
                <td style={{ color: '#94a3b8', fontSize: 12, paddingBottom: 7, paddingRight: 20 }}>Energy Intensity</td>
                <td style={{ fontSize: 12, paddingBottom: 7 }}>
                  <span style={{ color: volColor[em.energy_label] || '#facc15', fontWeight: 600 }}>{em.energy_label}</span>
                  <span style={{ color: '#94a3b8', marginLeft: 6 }}>({em.energy_range})</span>
                </td>
              </tr>
              <tr>
                <td style={{ color: '#94a3b8', fontSize: 12, paddingBottom: 7, paddingRight: 20 }}>Cost Volatility</td>
                <td style={{ fontSize: 12, paddingBottom: 7 }}>
                  <span style={{ color: volColor[em.volatility_level] || '#94a3b8', fontWeight: 600 }}>{em.volatility_level}</span>
                  {em.volatility_dominant && <span style={{ color: '#94a3b8', marginLeft: 6 }}>({em.volatility_dominant} dominant contributor)</span>}
                </td>
              </tr>
              <tr>
                <td style={{ color: '#94a3b8', fontSize: 12, paddingBottom: 7, paddingRight: 20 }}>Recyclability Index</td>
                <td style={{ fontSize: 12, paddingBottom: 7 }}>
                  <span style={{ color: recColor[em.recyclability_level] || '#94a3b8', fontWeight: 600 }}>{em.recyclability_level}</span>
                  <span style={{ color: '#94a3b8', marginLeft: 6 }}>(score {em.recyclability_score})</span>
                </td>
              </tr>
              <tr>
                <td style={{ color: '#94a3b8', fontSize: 12, paddingRight: 20 }}>Supply Concentration</td>
                <td style={{ fontSize: 12 }}>
                  <span style={{ color: sciColor, fontWeight: 600, fontFamily: 'monospace' }}>{em.supply_concentration_index}</span>
                  <span style={{ color: '#94a3b8', marginLeft: 6 }}>— {em.supply_concentration_level} risk</span>
                </td>
              </tr>
            </tbody>
          </table>
          {em.element_details && Object.keys(em.element_details).length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, marginBottom: 4 }}>Per-element price breakdown</div>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                  <tr>
                    {['Element', 'Price / kg', 'Mass frac.', 'Source', 'Date'].map(h => (
                      <th key={h} style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, paddingBottom: 4, paddingRight: 12, textAlign: 'left' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(em.element_details).map(([el, d]) => (
                    <tr key={el}>
                      <td style={{ fontWeight: 700, fontSize: 11, paddingBottom: 3, paddingRight: 12, color: '#e2e8f0' }}>{el}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 11, paddingBottom: 3, paddingRight: 12 }}>{d.price_label ?? 'N/A'}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 11, paddingBottom: 3, paddingRight: 12, color: '#94a3b8' }}>{d.mass_fraction != null ? (d.mass_fraction * 100).toFixed(1) + '%' : '—'}</td>
                      <td style={{ fontSize: 10, paddingBottom: 3, paddingRight: 12, color: '#94a3b8' }}>{d.source ?? '—'}</td>
                      <td style={{ fontSize: 10, paddingBottom: 3, color: '#94a3b8' }}>{d.date ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 8 }}>
            BOM cost derived from live World Bank commodity prices and USGS MCS 2024 data, weighted by elemental mass fraction.
            {em.data_sources?.length > 0 && <span> Sources: {em.data_sources.join(' · ')}</span>}
          </div>
        </div>
      )}
      {!data.ok && data.economic_metrics && (
        <div className="mb-2" style={{ fontSize: 12, color: '#f87171' }}>LLM qualitative analysis unavailable — quantitative metrics shown above are still valid.</div>
      )}
      {data.executive_summary && (
        <div className="mb-3" style={{ padding: '10px 14px', background: '#1e293b', borderRadius: 6, borderLeft: '3px solid #38bdf8' }}>
          <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 4 }}>Executive Summary</div>
          <div style={{ lineHeight: 1.6 }}>{data.executive_summary}</div>
        </div>
      )}
      <div className="row g-3 mb-3">
        {data.elemental_economics && (
          <div className="col-12 col-md-6">
            <div style={{ padding: '10px 14px', background: '#1e293b', borderRadius: 6, height: '100%' }}>
              <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}>Elemental Economics</div>
              {data.elemental_economics.assessment && <div style={{ marginBottom: 8, lineHeight: 1.5 }}>{data.elemental_economics.assessment}</div>}
              {data.elemental_economics.advantages?.length > 0 && (
                <div className="mb-2">
                  {data.elemental_economics.advantages.map((a, i) => (
                    <span key={i} style={{ display: 'inline-block', background: 'rgba(74,222,128,0.15)', color: '#4ade80', border: '1px solid rgba(74,222,128,0.3)', borderRadius: 4, padding: '2px 8px', fontSize: 11, marginRight: 4, marginBottom: 4 }}>+ {a}</span>
                  ))}
                </div>
              )}
              {data.elemental_economics.concerns?.length > 0 && (
                <div>
                  {data.elemental_economics.concerns.map((c, i) => (
                    <span key={i} style={{ display: 'inline-block', background: 'rgba(248,113,113,0.15)', color: '#f87171', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 4, padding: '2px 8px', fontSize: 11, marginRight: 4, marginBottom: 4 }}>− {c}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {data.synthesis_viability && (
          <div className="col-12 col-md-6">
            <div style={{ padding: '10px 14px', background: '#1e293b', borderRadius: 6, height: '100%' }}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <div style={{ color: '#94a3b8', fontSize: 12 }}>Synthesis Viability</div>
                {data.synthesis_viability.complexity && (
                  <span style={{ background: `${complexityColor[data.synthesis_viability.complexity] || '#94a3b8'}22`, color: complexityColor[data.synthesis_viability.complexity] || '#94a3b8', border: `1px solid ${complexityColor[data.synthesis_viability.complexity] || '#94a3b8'}44`, borderRadius: 4, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>
                    {data.synthesis_viability.complexity}
                  </span>
                )}
              </div>
              {data.synthesis_viability.assessment && <div style={{ lineHeight: 1.5 }}>{data.synthesis_viability.assessment}</div>}
            </div>
          </div>
        )}
      </div>
      <div className="row g-3">
        {data.market_potential && (
          <div className="col-12 col-md-6">
            <div style={{ padding: '10px 14px', background: '#1e293b', borderRadius: 6, height: '100%' }}>
              <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}>Market Potential</div>
              {data.market_potential.applications?.length > 0 && (
                <div className="mb-2">
                  {data.market_potential.applications.map((app, i) => (
                    <span key={i} style={{ display: 'inline-block', background: 'rgba(56,189,248,0.1)', color: '#38bdf8', border: '1px solid rgba(56,189,248,0.2)', borderRadius: 4, padding: '2px 8px', fontSize: 11, marginRight: 4, marginBottom: 4 }}>{app}</span>
                  ))}
                </div>
              )}
              {data.market_potential.assessment && <div style={{ lineHeight: 1.5, fontSize: 12 }}>{data.market_potential.assessment}</div>}
            </div>
          </div>
        )}
        {data.risk_assessment && (
          <div className="col-12 col-md-6">
            <div style={{ padding: '10px 14px', background: '#1e293b', borderRadius: 6, height: '100%' }}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <div style={{ color: '#94a3b8', fontSize: 12 }}>Risk Assessment</div>
                {data.risk_assessment.overall && (
                  <span style={{ background: `${riskColor[data.risk_assessment.overall] || '#94a3b8'}22`, color: riskColor[data.risk_assessment.overall] || '#94a3b8', border: `1px solid ${riskColor[data.risk_assessment.overall] || '#94a3b8'}44`, borderRadius: 4, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>
                    {data.risk_assessment.overall} risk
                  </span>
                )}
              </div>
              {data.risk_assessment.factors?.length > 0 && (
                <ul style={{ margin: 0, padding: '0 0 0 16px', fontSize: 12, lineHeight: 1.6 }}>
                  {data.risk_assessment.factors.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ScientificDocPanel({ data, loading }) {
  const printRef = useRef(null);
  if (loading) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#0f1923' }}>Building scientific document…</div>;
  if (!data) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#0f1923' }}>No data.</div>;
  if (!data.ok) return <div style={{ padding: '12px 16px', color: '#f87171', background: '#0f1923' }}>Error: {data.error || 'Unknown'}</div>;

  const { summary, structural_properties: sp, integrated_analyses: ia } = data;
  const qualityColor = summary.quality_class === 'Excellent' ? '#4ade80' : summary.quality_class === 'Good' ? '#facc15' : '#f87171';

  // Auto-generated abstract paragraph
  const crystalSystem = ia?.symmetry?.crystal_system || 'crystalline';
  const abstractParts = [
    `We report the computational discovery of ${summary.formula}, a ${crystalSystem} inorganic compound generated via generative diffusion modelling (MatterGen).`,
  ];
  let structPart = `The unit cell comprises ${summary.atom_count} atom${summary.atom_count !== 1 ? 's' : ''}`;
  if (sp.density != null) structPart += ` with an estimated density of ${sp.density} g cm⁻³`;
  if (sp.packing_fraction != null) structPart += ` and a packing fraction of ${(sp.packing_fraction * 100).toFixed(2)}%`;
  abstractParts.push(structPart + '.');
  if (ia?.symmetry) abstractParts.push(`Symmetry analysis identifies the space group as ${ia.symmetry.space_group} (No. ${ia.symmetry.space_group_number}), belonging to the ${ia.symmetry.crystal_system} crystal system with point group ${ia.symmetry.point_group}.`);
  if (sp.min_interatomic_distance != null) abstractParts.push(`The shortest interatomic contact measures ${sp.min_interatomic_distance} Å.`);
  if (ia?.bond_stats) abstractParts.push(`Bond length analysis reveals ${ia.bond_stats.count} bonds spanning ${ia.bond_stats.min}–${ia.bond_stats.max} Å (mean ${ia.bond_stats.mean} ± ${ia.bond_stats.std} Å).`);
  if (ia?.dft_surrogate) {
    const d = ia.dft_surrogate;
    let dftPart = `CHGNet DFT surrogate predicts a formation energy of ${d.formation_energy_per_atom} eV/atom`;
    if (d.stability_label) dftPart += ` (${d.stability_label})`;
    if (d.magnetic_character && d.magnetic_character !== 'non-magnetic') dftPart += ` with ${d.magnetic_character} character`;
    abstractParts.push(dftPart + '.');
  }
  abstractParts.push(`The structure achieved an absolute structural quality score of ${summary.score_pct}% (${summary.quality_class}), ranking #${summary.rank} in the current set.`);

  const rule = <hr style={{ borderColor: '#1e293b', margin: '14px 0' }} />;
  const sectionHead = (n, label) => (
    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: '#94a3b8', textTransform: 'uppercase', marginBottom: 10 }}>
      {n}. {label}
    </div>
  );
  const pending = (msg) => (
    <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
      ⚠ {msg} — run the corresponding analysis from the Analyze menu to populate this section.
    </div>
  );
  const tdL = { color: '#94a3b8', paddingBottom: 5, paddingRight: 16, fontSize: 12, verticalAlign: 'top', whiteSpace: 'nowrap' };
  const tdR = { fontSize: 12, fontFamily: 'monospace', paddingBottom: 5 };

  return (
    <div style={{ fontSize: 13, padding: '16px 20px', background: '#0a1120', color: '#e2e8f0', lineHeight: 1.6 }} ref={printRef}>
      <div className="d-flex justify-content-end mb-2 no-print">
        <button onClick={() => printPanel(printRef)} style={{ background: 'transparent', color: '#a78bfa', border: '1px solid #7c3aed', borderRadius: 4, padding: '3px 10px', fontSize: 11, cursor: 'pointer' }}>Print / Export PDF ↗</button>
      </div>

      {/* Header */}
      <div style={{ borderBottom: '1px solid #1e293b', paddingBottom: 12, marginBottom: 12 }}>
        <div style={{ fontSize: 10, letterSpacing: '0.12em', color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>
          CrimsonScan · Computational Materials Discovery Report
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#f1f5f9', marginBottom: 4 }}>{data.title}</div>
        <div style={{ fontSize: 11, color: '#94a3b8' }}>
          Rank #{summary.rank} · Score {summary.score_pct}%
          <span style={{ marginLeft: 8, background: `${qualityColor}22`, color: qualityColor, border: `1px solid ${qualityColor}44`, borderRadius: 3, padding: '1px 7px', fontSize: 10, fontWeight: 600 }}>{summary.quality_class}</span>
          <span style={{ marginLeft: 12 }}>Generated {new Date(data.generated_at).toLocaleString()}</span>
        </div>
      </div>

      {/* Abstract */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: '#94a3b8', textTransform: 'uppercase', marginBottom: 8 }}>Abstract</div>
        <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic', lineHeight: 1.8, padding: '8px 12px', borderLeft: '2px solid #1e293b' }}>
          {abstractParts.join(' ')}
        </div>
      </div>

      {rule}

      {/* 1. Compound Identification */}
      {sectionHead(1, 'Compound Identification')}
      <table style={{ borderCollapse: 'collapse', marginBottom: 4 }}>
        <tbody>
          <tr><td style={tdL}>Empirical formula</td><td style={{ ...tdR, fontWeight: 700 }}>{summary.formula}</td></tr>
          <tr><td style={tdL}>Atom count</td><td style={tdR}>{summary.atom_count}</td></tr>
          <tr><td style={tdL}>Quality classification</td>
            <td style={tdR}>
              <span style={{ color: qualityColor, fontWeight: 600 }}>{summary.quality_class}</span>
              <span style={{ color: '#94a3b8', marginLeft: 8 }}>(score {summary.score_pct}%, rank #{summary.rank})</span>
            </td>
          </tr>
          <tr><td style={tdL}>Density (est.)</td><td style={tdR}>{sp.density != null ? `${sp.density} g cm⁻³` : '—'}</td></tr>
          <tr><td style={tdL}>Packing fraction</td><td style={tdR}>{sp.packing_fraction != null ? `${(sp.packing_fraction * 100).toFixed(2)} %` : '—'}</td></tr>
          <tr><td style={tdL}>Min. interatomic dist.</td><td style={tdR}>{sp.min_interatomic_distance != null ? `${sp.min_interatomic_distance} Å` : '—'}</td></tr>
        </tbody>
      </table>

      {rule}

      {/* 2. Crystal Structure */}
      {sectionHead(2, 'Crystal Structure')}
      {ia?.symmetry ? (
        <>
          <table style={{ borderCollapse: 'collapse', marginBottom: 12 }}>
            <tbody>
              <tr><td style={tdL}>Space group</td><td style={{ ...tdR, fontWeight: 700 }}>{ia.symmetry.space_group} <span style={{ color: '#94a3b8', fontWeight: 400 }}>(No. {ia.symmetry.space_group_number})</span></td></tr>
              <tr><td style={tdL}>Crystal system</td><td style={tdR}>{ia.symmetry.crystal_system}</td></tr>
              <tr><td style={tdL}>Point group</td><td style={tdR}>{ia.symmetry.point_group}</td></tr>
              <tr><td style={tdL}>Hall symbol</td><td style={tdR}>{ia.symmetry.hall_symbol}</td></tr>
              <tr><td style={tdL}>Symmetry operations</td><td style={tdR}>{ia.symmetry.n_symmetry_ops}</td></tr>
              {ia.symmetry.is_laue != null && <tr><td style={tdL}>Laue class</td><td style={tdR}>{ia.symmetry.is_laue ? 'Yes' : 'No'}</td></tr>}
            </tbody>
          </table>
          {ia.symmetry.lattice && (
            <>
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6, fontWeight: 600 }}>2a. Lattice Parameters</div>
              <table style={{ borderCollapse: 'collapse', marginBottom: 12 }}>
                <thead>
                  <tr>
                    {['a (Å)', 'b (Å)', 'c (Å)', 'α (°)', 'β (°)', 'γ (°)', 'V (Å³)'].map(h => (
                      <th key={h} style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, paddingRight: 20, paddingBottom: 4 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {[ia.symmetry.lattice.a, ia.symmetry.lattice.b, ia.symmetry.lattice.c,
                      ia.symmetry.lattice.alpha, ia.symmetry.lattice.beta, ia.symmetry.lattice.gamma,
                      ia.symmetry.lattice.volume].map((v, i) => (
                      <td key={i} style={{ fontFamily: 'monospace', fontSize: 12, paddingRight: 20 }}>{v ?? '—'}</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </>
          )}
          {ia.symmetry.wyckoff_sites?.length > 0 && (
            <>
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6, fontWeight: 600 }}>2b. Wyckoff Positions</div>
              <table className="table table-dark table-sm mb-0" style={{ fontSize: 11, maxWidth: 560 }}>
                <thead><tr><th>Element</th><th>Site</th><th>Mult.</th><th>Fractional coordinates</th></tr></thead>
                <tbody>
                  {ia.symmetry.wyckoff_sites.map((w, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{w.element}</td>
                      <td>{w.wyckoff_letter}</td>
                      <td>{w.multiplicity}</td>
                      <td style={{ fontFamily: 'monospace' }}>{w.frac_coords?.map(c => c.toFixed(4)).join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>
      ) : pending('Symmetry data not available')}

      {rule}

      {/* 3. Bonding Environment */}
      {sectionHead(3, 'Bonding Environment')}
      {ia?.bond_stats ? (
        <>
          <table style={{ borderCollapse: 'collapse', marginBottom: 12 }}>
            <tbody>
              <tr><td style={tdL}>Total bonds</td><td style={tdR}>{ia.bond_stats.count}</td></tr>
              <tr><td style={tdL}>Bond length range</td><td style={tdR}>{ia.bond_stats.min} – {ia.bond_stats.max} Å</td></tr>
              <tr><td style={tdL}>Mean bond length</td><td style={tdR}>{ia.bond_stats.mean} Å</td></tr>
              <tr><td style={tdL}>Std. deviation</td><td style={tdR}>{ia.bond_stats.std} Å</td></tr>
            </tbody>
          </table>
          {ia.bond_pairs?.length > 0 && (
            <>
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6, fontWeight: 600 }}>3a. Per-Pair Bond Statistics</div>
              <table className="table table-dark table-sm mb-0" style={{ fontSize: 11, maxWidth: 500 }}>
                <thead><tr><th>Pair</th><th>Count</th><th>Min (Å)</th><th>Max (Å)</th><th>Mean (Å)</th></tr></thead>
                <tbody>
                  {ia.bond_pairs.map((bp, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{bp.pair}</td>
                      <td>{bp.count}</td>
                      <td style={{ fontFamily: 'monospace' }}>{bp.min}</td>
                      <td style={{ fontFamily: 'monospace' }}>{bp.max}</td>
                      <td style={{ fontFamily: 'monospace' }}>{bp.mean}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>
      ) : pending('Bond distribution data not available')}

      {rule}

      {/* 4. Coordination Environment */}
      {sectionHead(4, 'Coordination Environment')}
      {ia?.voronoi_summary?.length > 0 ? (
        <>
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>
            Average coordination number: <strong style={{ color: '#e2e8f0' }}>
              {(ia.voronoi_summary.reduce((acc, c) => acc + c.coordination_number, 0) / ia.voronoi_summary.length).toFixed(2)}
            </strong>
          </div>
          <table className="table table-dark table-sm mb-0" style={{ fontSize: 11, maxWidth: 560 }}>
            <thead><tr><th>#</th><th>Element</th><th>CN</th><th>Neighbor environment</th></tr></thead>
            <tbody>
              {ia.voronoi_summary.map((c, i) => (
                <tr key={i}>
                  <td>{c.index}</td>
                  <td style={{ fontWeight: 600 }}>{c.element}</td>
                  <td style={{ color: '#38bdf8', fontWeight: 700 }}>{c.coordination_number}</td>
                  <td>{Object.entries(c.neighbor_counts || {}).map(([el, n]) => `${el}×${n}`).join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : pending('Voronoi neighbor data not available')}

      {rule}

      {/* 5. DFT Surrogate (CHGNet) */}
      {sectionHead(5, 'DFT Surrogate (CHGNet)')}
      {ia?.dft_surrogate ? (() => {
        const d = ia.dft_surrogate;
        const stabColor = d.stability_label === 'stable' ? '#4ade80' : d.stability_label === 'metastable' ? '#fbbf24' : '#f87171';
        return (
          <>
            <div style={{ marginBottom: 10 }}>
              <span style={{ color: '#94a3b8', fontSize: 12, marginRight: 8 }}>Stability</span>
              <span style={{ background: stabColor, color: '#000', borderRadius: 4, padding: '2px 8px', fontWeight: 700, fontSize: 12 }}>{d.stability_label}</span>
            </div>
            <table style={{ borderCollapse: 'collapse', marginBottom: 4 }}>
              <tbody>
                <tr><td style={tdL}>Formation energy</td><td style={tdR}>{d.formation_energy_per_atom != null ? `${d.formation_energy_per_atom} eV/atom` : '—'}</td></tr>
                <tr><td style={tdL}>Total energy</td><td style={tdR}>{d.energy_per_atom != null ? `${d.energy_per_atom} eV/atom` : '—'}</td></tr>
                <tr><td style={tdL}>Max force</td><td style={tdR}>{d.forces_max_eV_A != null ? `${d.forces_max_eV_A} eV/Å` : '—'}</td></tr>
                <tr><td style={tdL}>Max stress</td><td style={tdR}>{d.stress_GPa != null ? `${d.stress_GPa} GPa` : '—'}</td></tr>
                <tr><td style={tdL}>Magnetic character</td><td style={tdR}>{d.magnetic_character || '—'}</td></tr>
                <tr><td style={tdL}>Avg |magmom|</td><td style={tdR}>{d.magmom_per_atom != null ? `${d.magmom_per_atom} μB` : '—'}</td></tr>
              </tbody>
            </table>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
              CHGNet universal neural-network potential. Formation energy relative to DFT elemental references (Materials Project, PBE).
            </div>
          </>
        );
      })() : pending('DFT surrogate data not available')}

      {rule}

      {/* 6. Computational Details */}
      {sectionHead(6, 'Computational Details')}
      <table style={{ borderCollapse: 'collapse' }}>
        <tbody>
          <tr><td style={tdL}>Generation method</td><td style={{ ...tdR, fontFamily: 'inherit' }}>MatterGen generative diffusion model</td></tr>
          <tr><td style={tdL}>Scoring metric</td><td style={{ ...tdR, fontFamily: 'inherit' }}>Absolute structural quality composite</td></tr>
          <tr><td style={tdL}>Score weights</td><td style={{ ...tdR, fontFamily: 'inherit' }}>Min. interatomic distance 40%, packing fraction 35%, density 25%</td></tr>
          <tr><td style={tdL}>Ranking method</td><td style={{ ...tdR, fontFamily: 'inherit' }}>Gaussian proximity to ideal structural values → weighted sum</td></tr>
          <tr><td style={tdL}>Property estimation</td><td style={{ ...tdR, fontFamily: 'inherit' }}>Derived from lattice geometry and atomic radii (geometric estimates)</td></tr>
        </tbody>
      </table>

      <div style={{ marginTop: 16, fontSize: 10, color: '#94a3b8', borderTop: '1px solid #1e293b', paddingTop: 8 }}>
        CrimsonScan · Computational report generated {new Date(data.generated_at).toUTCString()} · Properties are computational estimates; experimental validation required.
      </div>
    </div>
  );
}

function PatentPacketPanel({ data, loading, frame }) {
  const crystal3dRef = useRef(null);
  if (loading) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#060d18' }}>Generating patent packet…</div>;
  if (!data) return <div style={{ padding: '12px 16px', color: '#94a3b8', background: '#060d18' }}>No data.</div>;
  if (!data.ok) return <div style={{ padding: '12px 16px', color: '#f87171', background: '#060d18' }}>Error: {data.error || 'Unknown'}</div>;

  const exportPDF = () => {
    const crystalImg = crystal3dRef.current ? crystal3dRef.current() : null;
    const ap = data.appendix || {};
    const html = buildPatentPrintHTML(data, ap.structural || {}, ap.symmetry, ap.bonding, ap.coordination, ap.economic, crystalImg);
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 600);
  };

  const ap = data.appendix || {};
  const st = ap.structural || {};
  const sy = ap.symmetry;
  const bo = ap.bonding;
  const co = ap.coordination;
  const ec = ap.economic;

  const rule = <hr style={{ borderColor: '#1a2035', margin: '14px 0' }} />;
  const secHead = (label) => (
    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: '#7c3aed', textTransform: 'uppercase', marginBottom: 8 }}>{label}</div>
  );
  const tdL = { color: '#94a3b8', fontSize: 11, paddingBottom: 4, paddingRight: 16, whiteSpace: 'nowrap', verticalAlign: 'top' };
  const tdR = { fontSize: 11, fontFamily: 'monospace', paddingBottom: 4 };
  const prose = { fontSize: 12, color: '#94a3b8', lineHeight: 1.75 };

  return (
    <div style={{ fontSize: 13, padding: '20px 24px', background: '#060d18', color: '#cbd5e1', lineHeight: 1.6 }}>

      {/* Header */}
      <div style={{ borderBottom: '1px solid #1a2035', paddingBottom: 14, marginBottom: 14, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 9, letterSpacing: '0.18em', color: '#94a3b8', textTransform: 'uppercase', marginBottom: 6 }}>
            Patent Disclosure · CrimsonScan · {new Date(data.generated_at).toLocaleDateString()}
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9', lineHeight: 1.4, marginBottom: 4 }}>{data.title || `Novel Crystalline Compound: ${st.formula}`}</div>
          <div style={{ fontSize: 10, color: '#94a3b8' }}>Status: PENDING · Filing Date: {new Date(data.generated_at).toLocaleDateString()} · Formula: {st.formula}</div>
        </div>
        <button
          onClick={exportPDF}
          style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 5, padding: '6px 14px', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
        >Export PDF ↗</button>
      </div>

      {/* Incomplete-disclosure warning — surfaced on screen AND in the exported PDF */}
      {(() => {
        const missing = [];
        if (!sy) missing.push('Crystallographic / symmetry (Appendix B)');
        if (!bo && !(co && co.length)) missing.push('Bonding & coordination (Appendix C)');
        if (!ec) missing.push('Economic indicators (Appendix D)');
        if (!missing.length) return null;
        return (
          <div style={{ border: '1px solid #b91c1c', background: 'rgba(185,28,28,0.12)', color: '#fca5a5', padding: '8px 12px', borderRadius: 5, fontSize: 11, lineHeight: 1.5, marginBottom: 14 }}>
            <b style={{ color: '#f87171' }}>⚠ Incomplete disclosure.</b> Missing: {missing.join('; ')}. Run all structural analyses and the Economic Report before relying on this packet for a filing.
          </div>
        );
      })()}

      {/* 3D Crystal Structure */}
      {frame && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: '#7c3aed', textTransform: 'uppercase', marginBottom: 8 }}>Figure 1 — Crystal Structure</div>
          <CrystalViewer3D frame={frame} customBonds captureRef={crystal3dRef} style={{ height: 300 }} />
        </div>
      )}

      {/* Abstract */}
      {data.abstract && (
        <>
          {secHead('Abstract')}
          <div style={{ ...prose, fontStyle: 'italic', padding: '8px 12px', borderLeft: '2px solid #7c3aed', marginBottom: 14 }}>{data.abstract}</div>
          {rule}
        </>
      )}

      {/* §1 Field */}
      {data.field_of_invention && (
        <div style={{ marginBottom: 14 }}>
          {secHead('1. Field of the Invention')}
          <div style={prose}>{data.field_of_invention}</div>
        </div>
      )}

      {/* §2 Background */}
      {data.background && (
        <div style={{ marginBottom: 14 }}>
          {secHead('2. Background')}
          <div style={prose}>{data.background}</div>
        </div>
      )}

      {/* §3 Summary */}
      {data.summary_of_invention && (
        <div style={{ marginBottom: 14 }}>
          {secHead('3. Summary of the Invention')}
          <div style={prose}>{data.summary_of_invention}</div>
        </div>
      )}

      {/* §4 Detailed Description */}
      {data.detailed_description && (
        <div style={{ marginBottom: 14 }}>
          {secHead('4. Detailed Description')}
          <div style={prose}>{data.detailed_description}</div>
        </div>
      )}

      {/* §5 Claims */}
      {data.claims?.length > 0 && (
        <>
          {rule}
          {secHead('5. Claims')}
          <ol style={{ margin: 0, padding: '0 0 0 20px' }}>
            {data.claims.map((claim, i) => (
              <li key={i} style={{ fontSize: 12, color: i === 0 ? '#e2e8f0' : '#94a3b8', fontWeight: i === 0 ? 600 : 400, lineHeight: 1.75, marginBottom: 6 }}>
                {/* Strip leading "1. " number if LLM included it */}
                {claim.replace(/^\d+\.\s*/, '')}
              </li>
            ))}
          </ol>
        </>
      )}

      {rule}

      {/* Appendix A — Structural */}
      {secHead('Appendix A — Structural Data')}
      <table style={{ borderCollapse: 'collapse', marginBottom: 14 }}>
        <tbody>
          <tr><td style={tdL}>Formula</td><td style={{ ...tdR, fontWeight: 700 }}>{st.formula}</td></tr>
          <tr><td style={tdL}>Atom count</td><td style={tdR}>{st.atom_count}</td></tr>
          <tr><td style={tdL}>Density (est.)</td><td style={tdR}>{st.density != null ? `${st.density} g cm⁻³` : '—'}</td></tr>
          <tr><td style={tdL}>Packing fraction</td><td style={tdR}>{st.packing_fraction != null ? `${(st.packing_fraction * 100).toFixed(2)} %` : '—'}</td></tr>
          <tr><td style={tdL}>Min. interatomic dist.</td><td style={tdR}>{st.min_interatomic_distance != null ? `${st.min_interatomic_distance} Å` : '—'}</td></tr>
          <tr><td style={tdL}>Quality score</td><td style={tdR}>{st.score != null ? `${(st.score * 100).toFixed(1)} % (rank #${st.rank})` : '—'}</td></tr>
        </tbody>
      </table>

      {/* Appendix B — Crystallographic */}
      {sy ? (
        <>
          {secHead('Appendix B — Crystallographic Data')}
          <table style={{ borderCollapse: 'collapse', marginBottom: 8 }}>
            <tbody>
              <tr><td style={tdL}>Space group</td><td style={{ ...tdR, fontWeight: 700 }}>{sy.space_group} <span style={{ color: '#94a3b8', fontWeight: 400 }}>(No. {sy.space_group_number})</span></td></tr>
              <tr><td style={tdL}>Crystal system</td><td style={tdR}>{sy.crystal_system}</td></tr>
              <tr><td style={tdL}>Point group</td><td style={tdR}>{sy.point_group}</td></tr>
              <tr><td style={tdL}>Hall symbol</td><td style={tdR}>{sy.hall_symbol}</td></tr>
              <tr><td style={tdL}>Symmetry operations</td><td style={tdR}>{sy.n_symmetry_ops}</td></tr>
            </tbody>
          </table>
          {sy.lattice && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4 }}>Lattice parameters</div>
              <div className="d-flex gap-3 flex-wrap" style={{ fontSize: 11, fontFamily: 'monospace' }}>
                {['a','b','c'].map(k => <span key={k}><strong>{k}</strong> = {sy.lattice[k]} Å</span>)}
                {[['α','alpha'],['β','beta'],['γ','gamma']].map(([s,k]) => <span key={k}><strong>{s}</strong> = {sy.lattice[k]}°</span>)}
                <span><strong>V</strong> = {sy.lattice.volume} Å³</span>
              </div>
            </div>
          )}
          {sy.wyckoff_sites?.length > 0 && (
            <table className="table table-dark table-sm mb-3" style={{ fontSize: 10, maxWidth: 520 }}>
              <thead><tr><th>Element</th><th>Wyckoff</th><th>Mult.</th><th>Fractional coords</th></tr></thead>
              <tbody>
                {sy.wyckoff_sites.map((w, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600 }}>{w.element}</td>
                    <td>{w.wyckoff_letter}</td>
                    <td>{w.multiplicity}</td>
                    <td style={{ fontFamily: 'monospace' }}>{w.frac_coords?.map(c => c.toFixed(4)).join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      ) : (
        <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic', marginBottom: 14 }}>Appendix B — run Symmetry analysis to populate crystallographic data.</div>
      )}

      {/* Appendix C — Bonding & Coordination */}
      {(bo || co) ? (
        <>
          {secHead('Appendix C — Bonding & Coordination')}
          {bo?.stats && (
            <table style={{ borderCollapse: 'collapse', marginBottom: 8 }}>
              <tbody>
                <tr><td style={tdL}>Total bonds</td><td style={tdR}>{bo.stats.count}</td></tr>
                <tr><td style={tdL}>Bond length range</td><td style={tdR}>{bo.stats.min} – {bo.stats.max} Å</td></tr>
                <tr><td style={tdL}>Mean bond length</td><td style={tdR}>{bo.stats.mean} Å (σ = {bo.stats.std} Å)</td></tr>
              </tbody>
            </table>
          )}
          {bo?.bond_pairs?.length > 0 && (
            <table className="table table-dark table-sm mb-3" style={{ fontSize: 10, maxWidth: 480 }}>
              <thead><tr><th>Pair</th><th>Count</th><th>Min (Å)</th><th>Max (Å)</th><th>Mean (Å)</th></tr></thead>
              <tbody>
                {bo.bond_pairs.map((bp, i) => <tr key={i}><td style={{ fontWeight: 600 }}>{bp.pair}</td><td>{bp.count}</td><td>{bp.min}</td><td>{bp.max}</td><td>{bp.mean}</td></tr>)}
              </tbody>
            </table>
          )}
          {co?.length > 0 && (
            <>
              <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4 }}>Avg. coordination number: {(co.reduce((a, c) => a + c.coordination_number, 0) / co.length).toFixed(2)}</div>
              <table className="table table-dark table-sm mb-3" style={{ fontSize: 10, maxWidth: 480 }}>
                <thead><tr><th>#</th><th>Element</th><th>CN</th><th>Neighbors</th></tr></thead>
                <tbody>
                  {co.map((c, i) => <tr key={i}><td>{c.index}</td><td style={{ fontWeight: 600 }}>{c.element}</td><td style={{ color: '#38bdf8' }}>{c.coordination_number}</td><td>{Object.entries(c.neighbor_counts || {}).map(([el, n]) => `${el}×${n}`).join(', ')}</td></tr>)}
                </tbody>
              </table>
            </>
          )}
        </>
      ) : (
        <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic', marginBottom: 14 }}>Appendix C — run Bond Distribution and Voronoi analyses to populate bonding data.</div>
      )}

      {/* Appendix D — Economic */}
      {ec ? (
        <>
          {secHead('Appendix D — Economic Indicators')}
          <table style={{ borderCollapse: 'collapse', marginBottom: 8 }}>
            <tbody>
              <tr><td style={tdL}>Estimated BOM cost</td><td style={{ ...tdR, fontWeight: 600 }}>{ec.cost_low} – {ec.cost_high} / kg</td></tr>
              <tr><td style={tdL}>Energy intensity</td><td style={tdR}>{ec.energy_label} ({ec.energy_range})</td></tr>
              <tr><td style={tdL}>Cost volatility</td><td style={tdR}>{ec.volatility_level}{ec.volatility_dominant ? ` — ${ec.volatility_dominant} dominant` : ''}</td></tr>
              <tr><td style={tdL}>Recyclability</td><td style={tdR}>{ec.recyclability_level} (score {ec.recyclability_score})</td></tr>
              <tr><td style={tdL}>Supply concentration</td><td style={tdR}>{ec.supply_concentration_index} — {ec.supply_concentration_level} risk</td></tr>
            </tbody>
          </table>
          {ec.data_sources?.length > 0 && <div style={{ fontSize: 9, color: '#94a3b8' }}>Sources: {ec.data_sources.join(' · ')}</div>}
        </>
      ) : (
        <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic', marginBottom: 14 }}>Appendix D — generate Economic Report to populate economic indicators.</div>
      )}

      <div style={{ marginTop: 16, fontSize: 9, color: '#1e293b', borderTop: '1px solid #1a2035', paddingTop: 8 }}>
        CrimsonScan · Patent disclosure generated {new Date(data.generated_at).toUTCString()} · Computationally derived — not a filed patent application. Requires legal review before submission.
      </div>
    </div>
  );
}

function PatentSearchStatusPanel({ data, loading, onProceed, onCancel, onRetry }) {
  const violet = '#7c3aed';
  const spinnerRow = (msg, sub) => (
    <div style={{ padding: '32px 20px', textAlign: 'center', color: '#94a3b8' }}>
      <div className="spinner-border spinner-border-sm text-light" role="status" style={{ marginBottom: 8 }} />
      <div style={{ marginTop: 8, fontSize: 14 }}>{msg}</div>
      {sub && <div style={{ marginTop: 4, fontSize: 12, color: '#475569' }}>{sub}</div>}
    </div>
  );

  if (loading) return spinnerRow('Generating patent packet…');
  if (data?.status === 'searching') return spinnerRow('Searching for prior art…', 'Querying patent databases (USPTO, PubChem)');
  // D7 (durability audit 2026-08-20): a partial packet loaded from Firestore
  // on a fresh mount, before the user has re-clicked Patent Packet to
  // trigger the resume fetch (which flips `loading` true almost
  // immediately), defensive only: this window is not normally observable
  // in the live UI (see AppShell.jsx's toggleAnalysis), but a blank panel
  // is a worse failure mode than a brief spinner if it ever is.
  if (data?.status === 'generating') return spinnerRow('Resuming patent packet generation…', "Regenerating any sections that didn't finish before the browser closed");

  if (data?.status === 'search_error') {
    return (
      <div style={{ padding: '24px', color: '#e2e8f0', background: '#0a1628', borderRadius: 8, border: '1px solid #f8717155' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <span style={{ fontSize: 18, color: '#f87171' }}>⚠</span>
          <span style={{ fontWeight: 700, fontSize: 15, color: '#f87171' }}>Prior-art search failed</span>
        </div>
        <p style={{ color: '#94a3b8', fontSize: 14, marginBottom: 20, lineHeight: 1.6 }}>
          {data.search_error_message ? `Reason: ${data.search_error_message}. ` : ''}
          The prior-art database search could not be completed. You may retry, or proceed anyway —
          the generated packet will explicitly note that prior-art search was unavailable rather
          than claiming none was found.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn-outline-secondary btn-sm" onClick={onCancel}>Cancel</button>
          <button className="btn btn-outline-light btn-sm" onClick={onRetry}>Retry</button>
          <button className="btn btn-sm" style={{ background: violet, color: '#fff', border: 'none' }} onClick={onProceed}>
            Proceed anyway
          </button>
        </div>
      </div>
    );
  }

  if (data?.status === 'clear') {
    return (
      <div style={{ padding: '24px', color: '#e2e8f0', background: '#0a1628', borderRadius: 8, border: '1px solid #1e293b' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <span style={{ fontSize: 18, color: '#4ade80' }}>✓</span>
          <span style={{ fontWeight: 700, fontSize: 15, color: '#4ade80' }}>No prior art found</span>
        </div>
        <p style={{ color: '#94a3b8', fontSize: 14, marginBottom: 20, lineHeight: 1.6 }}>
          No matching patents were found{data.sources_queried?.length ? ` in ${data.sources_queried.join(', ')}` : ''}. You may proceed to generate the patent packet.
          {data.mp_context?.is_known_material && (
            <span style={{ display: 'block', marginTop: 8, color: '#f59e0b', fontSize: 13 }}>
              Note: This composition matches Materials Project entry <strong>{data.mp_context.mp_material_id}</strong> — the material is catalogued in scientific literature.
            </span>
          )}
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn-outline-secondary btn-sm" onClick={onCancel}>Cancel</button>
          <button className="btn btn-sm" style={{ background: violet, color: '#fff', border: 'none' }} onClick={onProceed}>
            Generate Patent Packet
          </button>
        </div>
      </div>
    );
  }

  if (data?.status === 'warning') {
    const riskColor = data.risk === 'high' ? '#ef4444' : '#f59e0b';
    return (
      <div style={{ padding: '24px', color: '#e2e8f0', background: '#0a1628', borderRadius: 8, border: `1px solid ${riskColor}55` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <span style={{ background: riskColor, color: '#fff', fontWeight: 700, fontSize: 11, padding: '2px 8px', borderRadius: 4 }}>
            {data.risk.toUpperCase()} RISK
          </span>
          <span style={{ color: '#94a3b8', fontSize: 13 }}>Prior art detected — review before proceeding</span>
        </div>
        <p style={{ color: '#94a3b8', fontSize: 14, marginBottom: 4, lineHeight: 1.6 }}>
          {data.hits.length} patent{data.hits.length !== 1 ? 's' : ''} found{data.sources_queried?.length ? ` across ${data.sources_queried.join(', ')}` : ''}.
        </p>
        {data.risk_detail && (
          <p style={{ color: '#cbd5e1', fontSize: 13, marginBottom: 12, lineHeight: 1.5 }}>{data.risk_detail}</p>
        )}
        {data.mp_context?.is_known_material && (
          <div style={{ padding: '8px 12px', marginBottom: 12, background: '#1a1a2e', borderRadius: 6, border: '1px solid #f59e0b44', fontSize: 13, color: '#f59e0b' }}>
            This composition matches Materials Project entry <strong>{data.mp_context.mp_material_id}</strong>
            {data.mp_context.mp_is_stable ? ' (thermodynamically stable)' : ''} — the material is already catalogued in scientific literature.
          </div>
        )}
        {data.hits.length > 0 && (
          <div style={{ marginBottom: 16, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #1e293b' }}>
                  <th style={{ textAlign: 'left', padding: '4px 8px', color: '#94a3b8', fontWeight: 600 }}>Patent ID</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px', color: '#94a3b8', fontWeight: 600 }}>Title</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px', color: '#94a3b8', fontWeight: 600 }}>Source</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px', color: '#94a3b8', fontWeight: 600 }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {data.hits.map((h, i) => {
                  const srcColors = { pubchem: '#1e3a5f', uspto_odp: '#3a2d1b' };
                  const srcLabels = { pubchem: 'PubChem', uspto_odp: 'USPTO' };
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #0f172a' }}>
                      <td style={{ padding: '5px 8px', fontFamily: 'monospace' }}>
                        {(h.google_patents_url || h.url)
                          ? <a href={h.google_patents_url || h.url} target="_blank" rel="noopener noreferrer" style={{ color: '#38bdf8' }}>{h.patent_id}</a>
                          : <span style={{ color: '#94a3b8' }}>{h.patent_id}</span>}
                      </td>
                      <td style={{ padding: '5px 8px', color: '#e2e8f0', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {h.title || '—'}
                        {h.assignee && <span style={{ display: 'block', fontSize: 10, color: '#64748b' }}>{h.assignee}</span>}
                      </td>
                      <td style={{ padding: '5px 8px' }}>
                        <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, background: srcColors[h.source] || '#1e293b', color: '#94a3b8' }}>
                          {srcLabels[h.source] || h.source}
                        </span>
                      </td>
                      <td style={{ padding: '5px 8px', color: '#64748b', fontSize: 11 }}>{h.filing_date || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16, lineHeight: 1.5 }}>
          <strong style={{ color: '#94a3b8' }}>Disclaimer:</strong> AI-assisted screening only. Consult a licensed patent professional before filing.
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn-outline-secondary btn-sm" onClick={onCancel}>Cancel</button>
          <button className="btn btn-warning btn-sm" onClick={onProceed}>Proceed Anyway</button>
        </div>
      </div>
    );
  }

  return null;
}

function DftSurrogatePanel({ data, loading }) {
  if (loading) return <div style={{ color: '#94a3b8', padding: 24 }}>Computing CHGNet prediction…</div>;
  if (!data || data.error) return <div style={{ color: '#f87171', padding: 24 }}>{data?.error || 'No data'}</div>;

  const stab = data.stability_label;
  const stabColor = stab === 'stable' ? '#4ade80' : stab === 'metastable' ? '#fbbf24' : '#f87171';

  return (
    <div style={{ padding: 20, color: '#e2e8f0' }}>
      <div style={{ marginBottom: 16 }}>
        <span style={{ color: '#94a3b8', fontSize: 12, marginRight: 8 }}>Stability</span>
        <span style={{ background: stabColor, color: '#000', borderRadius: 4, padding: '2px 8px', fontWeight: 700, fontSize: 12 }}>
          {stab}
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
        {[
          { label: 'Formation Energy', value: data.formation_energy_per_atom != null ? `${data.formation_energy_per_atom} eV/atom` : '—' },
          { label: 'Total Energy',     value: data.energy_per_atom != null ? `${data.energy_per_atom} eV/atom` : '—' },
          { label: 'Max Force',        value: data.forces_max_eV_A != null ? `${data.forces_max_eV_A} eV/Å` : '—' },
          { label: 'Max Stress',       value: data.stress_GPa != null ? `${data.stress_GPa} GPa` : '—' },
          { label: 'Magnetic',         value: data.magnetic_character || '—' },
          { label: 'Avg |Magmom|',     value: data.magmom_per_atom != null ? `${data.magmom_per_atom} μB` : '—' },
        ].map(({ label, value }) => (
          <div key={label} style={{ background: '#0a1120', borderRadius: 8, padding: '12px 16px' }}>
            <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4 }}>{label}</div>
            <div style={{ color: '#4ade80', fontSize: 15, fontWeight: 600 }}>{value}</div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 12, fontSize: 11, color: '#64748b' }}>
        CHGNet universal neural-network potential. Formation energy relative to DFT elemental references (Materials Project, PBE).
      </div>
    </div>
  );
}

// ── Phase Stability Panel ────────────────────────────────────────────────
function PhaseStabilityPanel({ data, loading }) {
  if (loading) return <div style={{ color: '#94a3b8', padding: 24 }}>Querying Materials Project convex hull…</div>;
  if (!data || data.error) return <div style={{ color: '#f87171', padding: 24 }}>{data?.error || 'No data'}</div>;

  const stabLabel = data.stability_label;
  const stabColor = stabLabel === 'stable' ? '#4ade80' : stabLabel === 'metastable' ? '#fbbf24' : '#f87171';

  const card = (label, value) => (
    <div key={label} style={{ background: '#0a1120', borderRadius: 8, padding: '12px 16px' }}>
      <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4 }}>{label}</div>
      <div style={{ color: '#4ade80', fontSize: 15, fontWeight: 600 }}>{value}</div>
    </div>
  );

  return (
    <div style={{ padding: 20, color: '#e2e8f0' }}>
      {/* Stability badge */}
      {stabLabel && (
        <div style={{ marginBottom: 16 }}>
          <span style={{ color: '#94a3b8', fontSize: 12, marginRight: 8 }}>Thermodynamic Stability</span>
          <span style={{ background: stabColor, color: '#000', borderRadius: 4, padding: '2px 10px', fontWeight: 700, fontSize: 12 }}>
            {stabLabel}
          </span>
        </div>
      )}

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
        {card('Energy Above Hull', data.energy_above_hull_eV != null ? `${data.energy_above_hull_eV} eV/atom` : '—')}
        {card('Formation Energy', data.candidate_formation_energy != null ? `${data.candidate_formation_energy} eV/atom` : '—')}
        {card('Energy Source', phaseStabilitySourceLabel(data.formation_energy_source))}
        {card('Phases in System', data.n_phases_in_system || 0)}
        {card('Stable Phases', data.hull_stable_phases?.length || 0)}
        {card('Formula', data.formula)}
      </div>

      {/* Decomposition products */}
      {data.decomposition?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
            Decomposition Products {stabLabel === 'stable' ? '(already on hull)' : ''}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {data.decomposition.map((d, i) => (
              <span key={i} style={{ background: '#0a1120', borderRadius: 6, padding: '4px 10px', fontSize: 12, color: '#cbd5e1' }}>
                {d.formula} <span style={{ color: '#64748b' }}>({(d.amount * 100).toFixed(1)}%)</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Competing stable phases on hull */}
      {data.hull_stable_phases?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Stable Phases on Convex Hull</div>
          <div style={{ maxHeight: 180, overflowY: 'auto' }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: '#94a3b8', borderBottom: '1px solid #1e293b' }}>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Formula</th>
                  <th style={{ textAlign: 'right', padding: '4px 8px' }}>E_form (eV/atom)</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>MP ID</th>
                </tr>
              </thead>
              <tbody>
                {data.hull_stable_phases.map((p, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #0f1923' }}>
                    <td style={{ padding: '4px 8px', color: '#e2e8f0' }}>{p.formula}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', color: '#4ade80' }}>{p.formation_energy_per_atom}</td>
                    <td style={{ padding: '4px 8px', color: '#38bdf8', fontSize: 11 }}>{p.material_id || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!stabLabel && (
        <div style={{ color: '#fbbf24', fontSize: 12, marginBottom: 12 }}>
          No formation energy available — run DFT Surrogate (CHGNet) first to place this candidate on the convex hull.
        </div>
      )}

      <div style={{ marginTop: 8, fontSize: 11, color: '#64748b' }}>
        Convex hull constructed from Materials Project PBE data via pymatgen PhaseDiagram.
      </div>
    </div>
  );
}

// ── MP Nearest Neighbors Panel ────────────────────────────────────────────
function MpNeighborsPanel({ data, loading }) {
  if (loading) return <div style={{ color: '#94a3b8', padding: 24 }}>Querying Materials Project for similar materials…</div>;
  if (!data || data.error) return <div style={{ color: '#f87171', padding: 24 }}>{data?.error || 'No data'}</div>;

  const neighbors = data.neighbors || [];
  const candidate = data.candidate || {};
  const propKeys = data.property_keys || [];

  return (
    <div style={{ padding: 20, color: '#e2e8f0' }}>
      {/* Candidate summary */}
      <div style={{ marginBottom: 16 }}>
        <span style={{ color: '#94a3b8', fontSize: 12, marginRight: 8 }}>Candidate</span>
        <span style={{ color: '#f59e0b', fontWeight: 700, fontSize: 14 }}>{candidate.formula || '—'}</span>
        {candidate.space_group && <span style={{ color: '#94a3b8', fontSize: 12, marginLeft: 12 }}>Space group: {candidate.space_group}</span>}
      </div>

      {neighbors.length === 0 ? (
        <div style={{ color: '#94a3b8', fontSize: 13 }}>No similar materials found in Materials Project for this chemical system.</div>
      ) : (
        <>
          <div style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
            {neighbors.length} Nearest Neighbor{neighbors.length !== 1 ? 's' : ''} in Materials Project
          </div>
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: '#94a3b8', borderBottom: '1px solid #1e293b' }}>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>MP ID</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Formula</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Space Group</th>
                  {propKeys.map(pk => (
                    <th key={pk} style={{ textAlign: 'right', padding: '4px 8px' }}>{pk.replace(/_/g, ' ')}</th>
                  ))}
                  <th style={{ textAlign: 'center', padding: '4px 8px' }}>Stable</th>
                </tr>
              </thead>
              <tbody>
                {neighbors.map((n, i) => (
                  <tr key={n.material_id || i} style={{ borderBottom: '1px solid #0f1923' }}>
                    <td style={{ padding: '4px 8px', color: '#38bdf8', fontSize: 11 }}>{n.material_id || '—'}</td>
                    <td style={{ padding: '4px 8px', color: '#e2e8f0', fontWeight: 600 }}>{n.formula || '—'}</td>
                    <td style={{ padding: '4px 8px', color: '#cbd5e1' }}>{n.space_group || '—'}</td>
                    {propKeys.map(pk => (
                      <td key={pk} style={{ padding: '4px 8px', textAlign: 'right', color: '#4ade80' }}>
                        {n[pk] != null ? (typeof n[pk] === 'number' ? n[pk].toFixed(3) : String(n[pk])) : '—'}
                      </td>
                    ))}
                    <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                      <span style={{ color: n.is_stable ? '#4ade80' : '#f87171', fontWeight: 600 }}>{n.is_stable ? 'Yes' : 'No'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div style={{ marginTop: 12, fontSize: 11, color: '#64748b' }}>
        Comparison data sourced from Materials Project PBE database. Properties shown are DFT-computed reference values for known materials in the same chemical system.
      </div>
    </div>
  );
}

// ── DOS SVG plot helpers ──────────────────────────────────────────────────
const DOS_COLORS = ['#38bdf8', '#4ade80', '#fbbf24', '#f87171', '#a78bfa', '#fb923c', '#f472b6', '#34d399'];
const ORB_COLORS = { s: '#38bdf8', p: '#4ade80', d: '#f87171', f: '#fbbf24' };

function DosPlot({ title, lines, energies, showLegend, height = 220 }) {
  const W = 520, H = height, PAD = { top: 30, right: 20, bottom: 35, left: 55 };
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;

  if (!energies || energies.length === 0) return null;

  // Determine data range
  let yMin = 0, yMax = 0;
  for (const l of lines) {
    for (const v of l.data) {
      if (v > yMax) yMax = v;
      if (v < yMin) yMin = v;
    }
  }
  if (yMax === 0 && yMin === 0) yMax = 1;
  const yPad = (yMax - yMin) * 0.08;
  yMin -= yPad; yMax += yPad;

  const eMin = energies[0], eMax = energies[energies.length - 1];
  const xRange = eMax - eMin || 1; // #28 — prevent division by zero
  const xScale = (e) => PAD.left + ((e - eMin) / xRange) * plotW;
  const yScale = (d) => PAD.top + plotH - ((d - yMin) / (yMax - yMin)) * plotH;

  // E_F = 0 line position
  const efX = xScale(0);

  // X-axis ticks
  const xTicks = [];
  const xStep = Math.max(1, Math.ceil((eMax - eMin) / 8)); // #27 — prevent infinite loop
  for (let v = Math.ceil(eMin); v <= eMax; v += xStep) xTicks.push(v);

  // Y-axis ticks
  const yTicks = [];
  const yRange = yMax - yMin;
  const yStep = Math.pow(10, Math.floor(Math.log10(yRange))) || 1;
  for (let v = Math.ceil(yMin / yStep) * yStep; v <= yMax; v += yStep) yTicks.push(v);

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{title}</div>
      <svg width={W} height={H} style={{ background: '#0a1120', borderRadius: 8 }}>
        {/* Grid lines */}
        {yTicks.map(v => (
          <line key={`yg${v}`} x1={PAD.left} x2={W - PAD.right} y1={yScale(v)} y2={yScale(v)} stroke="#1e293b" strokeWidth="1" />
        ))}

        {/* Fermi level dashed line */}
        {eMin <= 0 && eMax >= 0 && (
          <line x1={efX} x2={efX} y1={PAD.top} y2={PAD.top + plotH} stroke="#94a3b8" strokeWidth="1" strokeDasharray="4,3" />
        )}
        {eMin <= 0 && eMax >= 0 && (
          <text x={efX + 3} y={PAD.top + 12} fill="#94a3b8" fontSize="9">E_F</text>
        )}

        {/* Data lines */}
        {lines.map((l, i) => {
          const pts = energies.map((e, j) => `${xScale(e)},${yScale(l.data[j])}`).join(' ');
          return <polyline key={i} points={pts} fill="none" stroke={l.color} strokeWidth="1.5" opacity="0.9" />;
        })}

        {/* X-axis */}
        <line x1={PAD.left} x2={W - PAD.right} y1={PAD.top + plotH} y2={PAD.top + plotH} stroke="#334155" strokeWidth="1" />
        {xTicks.map(v => (
          <text key={`xt${v}`} x={xScale(v)} y={H - 5} fill="#64748b" fontSize="9" textAnchor="middle">{v}</text>
        ))}
        <text x={PAD.left + plotW / 2} y={H - 5} fill="#94a3b8" fontSize="10" textAnchor="middle">Energy (eV rel. E_F)</text>

        {/* Y-axis */}
        {yTicks.map(v => (
          <text key={`yt${v}`} x={PAD.left - 6} y={yScale(v) + 3} fill="#64748b" fontSize="9" textAnchor="end">{v.toFixed(1)}</text>
        ))}
        <text x={14} y={PAD.top + plotH / 2} fill="#94a3b8" fontSize="10" textAnchor="middle" transform={`rotate(-90,14,${PAD.top + plotH / 2})`}>
          DOS (states/eV)
        </text>
      </svg>

      {showLegend && lines.length > 1 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginTop: 6 }}>
          {lines.map((l, i) => (
            <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#cbd5e1' }}>
              <span style={{ width: 12, height: 3, background: l.color, borderRadius: 1, display: 'inline-block' }} />
              {l.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function DftDosResultView({ data }) {
  const card = (label, value) => (
    <div key={label} style={{ background: '#0a1120', borderRadius: 8, padding: '12px 16px' }}>
      <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4 }}>{label}</div>
      <div style={{ color: '#4ade80', fontSize: 15, fontWeight: 600 }}>{value}</div>
    </div>
  );

  const bandGap = data.dft_is_metal
    ? <span style={{ background: '#1e3a5f', color: '#38bdf8', borderRadius: 4, padding: '2px 8px', fontSize: 13 }}>Metal</span>
    : (data.dft_band_gap_eV != null ? `${data.dft_band_gap_eV} eV` : '—');

  // ── Total DOS plot ──────────────────────────────────────────────────────
  const totalLines = [];
  if (data.dos_total) {
    if (data.is_spin_polarized && data.dos_total.dos_down) {
      totalLines.push({ label: 'Spin ↑', data: data.dos_total.dos_up, color: '#38bdf8' });
      totalLines.push({ label: 'Spin ↓', data: data.dos_total.dos_down.map(v => -v), color: '#f472b6' });
    } else {
      totalLines.push({ label: 'Total', data: data.dos_total.dos_up, color: '#4ade80' });
    }
  }

  // ── Element-projected DOS ───────────────────────────────────────────────
  const elemLines = [];
  if (data.dos_element_projected?.elements) {
    const elements = Object.keys(data.dos_element_projected.elements);
    elements.forEach((el, i) => {
      const elData = data.dos_element_projected.elements[el];
      elemLines.push({ label: el, data: elData.dos_up, color: DOS_COLORS[i % DOS_COLORS.length] });
    });
  }

  // ── Orbital-projected DOS ───────────────────────────────────────────────
  const orbLines = [];
  if (data.dos_orbital_projected?.orbitals) {
    for (const [orb, orbData] of Object.entries(data.dos_orbital_projected.orbitals)) {
      orbLines.push({ label: orb, data: orbData.dos_up, color: ORB_COLORS[orb] || '#94a3b8' });
    }
  }

  const energies = data.dos_total?.energies;

  return (
    <div style={{ padding: 20, color: '#e2e8f0' }}>
      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 20 }}>
        {card('DFT Band Gap', bandGap)}
        {card('Fermi Level', data.fermi_level_eV != null ? `${data.fermi_level_eV} eV` : '—')}
        {card('Spin-Polarized', data.is_spin_polarized ? 'Yes' : 'No')}
        {card('Magnetic', data.magnetic_character || '—')}
        {card('Total Magmom', data.total_magmom != null ? `${data.total_magmom} μB` : '—')}
      </div>

      {/* Total DOS */}
      {totalLines.length > 0 && (
        <DosPlot
          title={data.is_spin_polarized ? 'Total DOS (spin-resolved)' : 'Total DOS'}
          lines={totalLines}
          energies={energies}
          showLegend={data.is_spin_polarized}
        />
      )}

      {/* Element-projected */}
      {elemLines.length > 0 && (
        <DosPlot
          title="Element-Projected DOS"
          lines={elemLines}
          energies={data.dos_element_projected?.energies || energies}
          showLegend
        />
      )}

      {/* Orbital-projected */}
      {orbLines.length > 0 && (
        <DosPlot
          title="Orbital-Projected DOS (s/p/d/f)"
          lines={orbLines}
          energies={data.dos_orbital_projected?.energies || energies}
          showLegend
        />
      )}

      <div style={{ marginTop: 12, fontSize: 11, color: '#64748b' }}>
        GPAW PBE — Gaussian-broadened density of states. Fermi smearing + Pulay mixer for magnetic convergence.
      </div>
    </div>
  );
}

function BandStructureResultView({ data }) {
  const bs = data.band_structure;
  const card = (label, value) => (
    <div key={label} style={{ background: '#0a1120', borderRadius: 8, padding: '12px 16px' }}>
      <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4 }}>{label}</div>
      <div style={{ color: '#4ade80', fontSize: 15, fontWeight: 600 }}>{value}</div>
    </div>
  );

  const bandGap = data.bs_is_metal
    ? <span style={{ background: '#1e3a5f', color: '#38bdf8', borderRadius: 4, padding: '2px 8px', fontSize: 13 }}>Metal</span>
    : (data.bs_band_gap_eV != null ? `${data.bs_band_gap_eV} eV` : '—');

  const gapType = data.bs_gap_type
    ? <span style={{ background: data.bs_gap_type === 'direct' ? '#064e3b' : '#3b0764', color: data.bs_gap_type === 'direct' ? '#4ade80' : '#a78bfa', borderRadius: 4, padding: '2px 8px', fontSize: 13 }}>{data.bs_gap_type}</span>
    : '—';

  if (!bs) {
    return (
      <div style={{ padding: 20, color: '#e2e8f0' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
          {card('Band Gap', bandGap)}
          {card('Gap Type', gapType)}
          {card('Fermi Level', data.fermi_level_eV != null ? `${data.fermi_level_eV} eV` : '—')}
        </div>
      </div>
    );
  }

  // ── SVG Band structure plot ─────────────────────────────────────────────
  const W = 560, H = 320, PAD = { top: 20, right: 20, bottom: 40, left: 55 };
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;

  const kDist = bs.k_distances;
  const kMin = kDist[0], kMax = kDist[kDist.length - 1];

  // Energy range: clamp to ±6 eV around Fermi level for readability
  let eMin = -6, eMax = 6;
  // Check if all bands are within range; if band gap is large, expand
  if (data.bs_band_gap_eV && data.bs_band_gap_eV > 10) {
    eMax = data.bs_band_gap_eV / 2 + 3;
    eMin = -eMax;
  }

  const xScale = (k) => PAD.left + ((k - kMin) / (kMax - kMin)) * plotW;
  const yScale = (e) => PAD.top + plotH - ((e - eMin) / (eMax - eMin)) * plotH;

  // Draw bands
  const bandLines = [];
  const drawBands = (bands, color, opacity = 0.7) => {
    if (!bands) return;
    bands.forEach((band, bIdx) => {
      const pts = [];
      for (let i = 0; i < kDist.length; i++) {
        const e = band[i];
        if (e >= eMin && e <= eMax) {
          pts.push(`${xScale(kDist[i])},${yScale(e)}`);
        } else if (pts.length > 0) {
          // Clamp to boundary
          pts.push(`${xScale(kDist[i])},${yScale(Math.max(eMin, Math.min(eMax, e)))}`);
        }
      }
      if (pts.length > 1) {
        bandLines.push(
          <polyline key={`${color}_${bIdx}`} points={pts.join(' ')} fill="none" stroke={color} strokeWidth="1.2" opacity={opacity} />
        );
      }
    });
  };

  drawBands(bs.bands_up, data.is_spin_polarized ? '#38bdf8' : '#4ade80');
  if (data.is_spin_polarized && bs.bands_down) {
    drawBands(bs.bands_down, '#f472b6', 0.6);
  }

  // Y-axis ticks
  const yTicks = [];
  for (let v = Math.ceil(eMin); v <= eMax; v += 2) yTicks.push(v);

  // High-symmetry labels
  const labelTicks = (bs.labels || []).map(l => ({
    x: xScale(l.k_dist),
    label: l.label,
  }));

  return (
    <div style={{ padding: 20, color: '#e2e8f0' }}>
      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 20 }}>
        {card('Band Gap', bandGap)}
        {card('Gap Type', gapType)}
        {card('Fermi Level', data.fermi_level_eV != null ? `${data.fermi_level_eV} eV` : '—')}
        {card('Spin-Polarized', data.is_spin_polarized ? 'Yes' : 'No')}
        {card('Magnetic', data.magnetic_character || '—')}
        {card('Bands', bs.n_bands || '—')}
      </div>

      {/* Band structure plot */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
          Band Structure {data.is_spin_polarized ? '(spin-resolved)' : ''}
        </div>
        <svg width={W} height={H} style={{ background: '#0a1120', borderRadius: 8 }}>
          {/* Horizontal grid */}
          {yTicks.map(v => (
            <line key={`yg${v}`} x1={PAD.left} x2={W - PAD.right} y1={yScale(v)} y2={yScale(v)} stroke="#1e293b" strokeWidth="1" />
          ))}

          {/* Fermi level at E=0 */}
          <line x1={PAD.left} x2={W - PAD.right} y1={yScale(0)} y2={yScale(0)} stroke="#94a3b8" strokeWidth="1" strokeDasharray="4,3" />
          <text x={W - PAD.right + 3} y={yScale(0) + 3} fill="#94a3b8" fontSize="9">E_F</text>

          {/* High-symmetry vertical lines */}
          {labelTicks.map((t, i) => (
            <React.Fragment key={i}>
              <line x1={t.x} x2={t.x} y1={PAD.top} y2={PAD.top + plotH} stroke="#334155" strokeWidth="1" />
              <text x={t.x} y={H - 8} fill="#cbd5e1" fontSize="11" textAnchor="middle" fontWeight="600">{t.label}</text>
            </React.Fragment>
          ))}

          {/* Band lines */}
          {bandLines}

          {/* Y-axis labels */}
          {yTicks.map(v => (
            <text key={`yt${v}`} x={PAD.left - 6} y={yScale(v) + 3} fill="#64748b" fontSize="9" textAnchor="end">{v}</text>
          ))}
          <text x={14} y={PAD.top + plotH / 2} fill="#94a3b8" fontSize="10" textAnchor="middle" transform={`rotate(-90,14,${PAD.top + plotH / 2})`}>
            Energy (eV)
          </text>
        </svg>

        {data.is_spin_polarized && (
          <div style={{ display: 'flex', gap: 14, marginTop: 6 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#cbd5e1' }}>
              <span style={{ width: 12, height: 3, background: '#38bdf8', borderRadius: 1, display: 'inline-block' }} /> Spin ↑
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#cbd5e1' }}>
              <span style={{ width: 12, height: 3, background: '#f472b6', borderRadius: 1, display: 'inline-block' }} /> Spin ↓
            </span>
          </div>
        )}
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: '#64748b' }}>
        GPAW PBE — non-self-consistent band structure along pymatgen HighSymmKpath.
      </div>
    </div>
  );
}

function SpinDensityResultView({ data }) {
  const viewerRef = useRef(null);
  const glviewerRef = useRef(null);
  const [isovalue, setIsovalue] = useState(0.005);

  const meta = data.spin_density_metadata || {};
  const cubeText = data.spin_density_cube;

  const card = (label, value) => (
    <div key={label} style={{ background: '#0a1120', borderRadius: 8, padding: '12px 16px' }}>
      <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4 }}>{label}</div>
      <div style={{ color: '#4ade80', fontSize: 15, fontWeight: 600 }}>{value}</div>
    </div>
  );

  const renderViewer = useCallback((iso) => {
    if (!viewerRef.current || !cubeText) return;
    const el = viewerRef.current;
    // #39 — Dispose old viewer before creating a new one to prevent WebGL context leak
    if (glviewerRef.current) {
      try { glviewerRef.current.clear(); glviewerRef.current = null; } catch (_) {}
    }
    el.innerHTML = '';
    const viewer = $3Dmol.createViewer(el, { backgroundColor: '#0a1120' });
    glviewerRef.current = viewer;

    // Parse cube and add atomic model
    const voldata = new $3Dmol.VolumeData(cubeText, 'cube');
    viewer.addModel(cubeText, 'cube');
    viewer.setStyle({}, { stick: { radius: 0.12, colorscheme: 'Jmol' }, sphere: { scale: 0.25, colorscheme: 'Jmol' } });

    // Positive spin density (spin-up excess) — blue
    viewer.addIsosurface(voldata, {
      isoval: iso,
      color: '#38bdf8',
      alpha: 0.6,
      smoothness: 5,
    });

    // Negative spin density (spin-down excess) — red/pink
    viewer.addIsosurface(voldata, {
      isoval: -iso,
      color: '#f472b6',
      alpha: 0.6,
      smoothness: 5,
    });

    viewer.zoomTo();
    viewer.render();
  }, [cubeText]);

  useEffect(() => {
    renderViewer(isovalue);
  }, [renderViewer, isovalue]);

  const handleDownloadCube = () => {
    if (!cubeText) return;
    const blob = new Blob([cubeText], { type: 'chemical/x-gaussian-cube' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'spin_density.cube';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Determine sensible isovalue range from metadata
  const absMax = Math.max(Math.abs(meta.min || 0), Math.abs(meta.max || 0));
  const sliderMax = absMax > 0 ? +(absMax * 0.8).toFixed(6) : 0.05;
  const sliderStep = sliderMax > 0.01 ? 0.001 : 0.0001;

  return (
    <div style={{ padding: 20, color: '#e2e8f0' }}>
      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 20 }}>
        {card('Magnetic Character', data.magnetic_character || '—')}
        {card('Total Magmom', data.total_magmom != null ? `${data.total_magmom} μB` : '—')}
        {card('Spin-Polarized', data.is_spin_polarized ? 'Yes' : 'No')}
        {card('Spin Density Min', meta.min != null ? `${meta.min} e/ų` : '—')}
        {card('Spin Density Max', meta.max != null ? `${meta.max} e/ų` : '—')}
        {card('Grid Shape', meta.grid_shape ? meta.grid_shape.join(' × ') : '—')}
      </div>

      {!meta.is_magnetic && (
        <div style={{ background: '#1e293b', borderRadius: 8, padding: '14px 18px', marginBottom: 16, color: '#fbbf24', fontSize: 13, border: '1px solid #854d0e' }}>
          Non-magnetic structure — spin density is zero everywhere. No isosurface to display.
          {meta.note && <div style={{ marginTop: 4, color: '#94a3b8', fontSize: 12 }}>{meta.note}</div>}
        </div>
      )}

      {cubeText && meta.is_magnetic && (
        <>
          {/* Isovalue slider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <label style={{ color: '#94a3b8', fontSize: 12, whiteSpace: 'nowrap' }}>Isovalue (e/ų):</label>
            <input
              type="range"
              min={sliderStep}
              max={sliderMax}
              step={sliderStep}
              value={isovalue}
              onChange={e => setIsovalue(parseFloat(e.target.value))}
              style={{ flex: 1, accentColor: '#38bdf8' }}
            />
            <span style={{ color: '#e2e8f0', fontSize: 12, fontFamily: 'monospace', minWidth: 72, textAlign: 'right' }}>
              {isovalue.toFixed(4)}
            </span>
          </div>

          {/* Legend */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 8, fontSize: 12 }}>
            <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#38bdf8', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} /> Spin ↑ excess</span>
            <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#f472b6', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} /> Spin ↓ excess</span>
          </div>

          {/* 3D viewer */}
          <div
            ref={viewerRef}
            style={{ width: '100%', height: 400, borderRadius: 8, border: '1px solid #1e293b', position: 'relative' }}
          />
        </>
      )}

      {/* Download cube button */}
      {cubeText && (
        <div style={{ marginTop: 12 }}>
          <button
            onClick={handleDownloadCube}
            style={{ background: '#1e293b', color: '#38bdf8', border: '1px solid #334155', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 12 }}
          >
            Download .cube file
          </button>
        </div>
      )}

      <div style={{ marginTop: 12, fontSize: 11, color: '#64748b' }}>
        GPAW PBE — spin density (ρ↑ − ρ↓) on real-space grid. Fermi smearing + Pulay mixer for magnetic convergence.
      </div>
    </div>
  );
}

function OpticalPlot({ title, lines, energies, xLabel, yLabel, height = 220 }) {
  const W = 520, H = height, PAD = { top: 30, right: 20, bottom: 35, left: 65 };
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;

  if (!energies || energies.length === 0) return null;

  let yMin = 0, yMax = 0;
  for (const l of lines) {
    for (const v of l.data) {
      if (v > yMax) yMax = v;
      if (v < yMin) yMin = v;
    }
  }
  if (yMax === 0 && yMin === 0) yMax = 1;
  const yPad = (yMax - yMin) * 0.08;
  yMin -= yPad; yMax += yPad;

  const eMin = energies[0], eMax = energies[energies.length - 1];
  const xRange = eMax - eMin || 1; // #28 — prevent division by zero
  const xScale = (e) => PAD.left + ((e - eMin) / xRange) * plotW;
  const yScale = (d) => PAD.top + plotH - ((d - yMin) / (yMax - yMin)) * plotH;

  const xTicks = [];
  const xStep = Math.max(1, Math.ceil((eMax - eMin) / 8));
  for (let v = Math.ceil(eMin); v <= eMax; v += xStep) xTicks.push(v);

  const yTicks = [];
  const yRange = yMax - yMin;
  const yStep = Math.pow(10, Math.floor(Math.log10(yRange))) || 1;
  for (let v = Math.ceil(yMin / yStep) * yStep; v <= yMax; v += yStep) yTicks.push(v);
  if (yTicks.length > 10) {
    const coarser = yStep * 2;
    yTicks.length = 0;
    for (let v = Math.ceil(yMin / coarser) * coarser; v <= yMax; v += coarser) yTicks.push(v);
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{title}</div>
      <svg width={W} height={H} style={{ background: '#0a1120', borderRadius: 8 }}>
        {yTicks.map(v => (
          <line key={`yg${v}`} x1={PAD.left} x2={W - PAD.right} y1={yScale(v)} y2={yScale(v)} stroke="#1e293b" strokeWidth="1" />
        ))}
        {lines.map((l, i) => {
          const pts = energies.map((e, j) => `${xScale(e)},${yScale(l.data[j])}`).join(' ');
          return <polyline key={i} points={pts} fill="none" stroke={l.color} strokeWidth="1.5" opacity="0.9" />;
        })}
        <line x1={PAD.left} x2={W - PAD.right} y1={PAD.top + plotH} y2={PAD.top + plotH} stroke="#334155" strokeWidth="1" />
        {xTicks.map(v => (
          <text key={`xt${v}`} x={xScale(v)} y={H - 5} fill="#64748b" fontSize="9" textAnchor="middle">{v}</text>
        ))}
        <text x={PAD.left + plotW / 2} y={H - 5} fill="#94a3b8" fontSize="10" textAnchor="middle">{xLabel}</text>
        {yTicks.map(v => (
          <text key={`yt${v}`} x={PAD.left - 6} y={yScale(v) + 3} fill="#64748b" fontSize="9" textAnchor="end">
            {Math.abs(v) >= 1000 ? v.toExponential(1) : v.toFixed(1)}
          </text>
        ))}
        <text x={14} y={PAD.top + plotH / 2} fill="#94a3b8" fontSize="10" textAnchor="middle" transform={`rotate(-90,14,${PAD.top + plotH / 2})`}>
          {yLabel}
        </text>
      </svg>
      {lines.length > 1 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginTop: 6 }}>
          {lines.map((l, i) => (
            <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#cbd5e1' }}>
              <span style={{ width: 12, height: 3, background: l.color, borderRadius: 1, display: 'inline-block' }} />
              {l.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function OpticalResultView({ data }) {
  const card = (label, value) => (
    <div key={label} style={{ background: '#0a1120', borderRadius: 8, padding: '12px 16px' }}>
      <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4 }}>{label}</div>
      <div style={{ color: '#4ade80', fontSize: 15, fontWeight: 600 }}>{value}</div>
    </div>
  );

  const df = data.dielectric_function;
  const abs = data.absorption_coefficient;

  return (
    <div style={{ padding: 20, color: '#e2e8f0' }}>
      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 20 }}>
        {card('Optical Band Gap', data.optical_band_gap_eV != null ? `${data.optical_band_gap_eV} eV` : 'Metal / none')}
        {card('Static ε (electronic)', data.static_dielectric_constant != null ? `${data.static_dielectric_constant}` : '—')}
        {card('Magnetic Character', data.magnetic_character || '—')}
        {card('Total Magmom', data.total_magmom != null ? `${data.total_magmom} μB` : '—')}
        {card('Fermi Level', data.fermi_level_eV != null ? `${data.fermi_level_eV} eV` : '—')}
        {card('Spin-Polarized', data.is_spin_polarized ? 'Yes' : 'No')}
      </div>

      {/* Dielectric function plot */}
      {df && df.energies && (
        <OpticalPlot
          title="Dielectric Function"
          lines={[
            { label: 'ε₁ (real)', data: df.eps1, color: '#38bdf8' },
            { label: 'ε₂ (imag)', data: df.eps2, color: '#f472b6' },
          ]}
          energies={df.energies}
          xLabel="Energy (eV)"
          yLabel="ε(ω)"
        />
      )}

      {/* Absorption coefficient plot */}
      {abs && abs.energies && (
        <OpticalPlot
          title="Optical Absorption Coefficient"
          lines={[
            { label: 'α', data: abs.alpha_cm_inv, color: '#4ade80' },
          ]}
          energies={abs.energies}
          xLabel="Energy (eV)"
          yLabel="α (cm⁻¹)"
        />
      )}

      <div style={{ marginTop: 12, fontSize: 11, color: '#64748b' }}>
        GPAW PBE — macroscopic dielectric function via linear response (RPA). Polycrystalline average over x/y/z directions. Fermi smearing + Pulay mixer.
      </div>
    </div>
  );
}

function EffectiveMassResultView({ data }) {
  const card = (label, value) => (
    <div key={label} style={{ background: '#0a1120', borderRadius: 8, padding: '12px 16px' }}>
      <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4 }}>{label}</div>
      <div style={{ color: '#4ade80', fontSize: 15, fontWeight: 600 }}>{value}</div>
    </div>
  );

  const em = data.effective_masses || {};

  if (em.is_metal) {
    return (
      <div style={{ padding: 20, color: '#e2e8f0' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 20 }}>
          {card('Magnetic Character', data.magnetic_character || '—')}
          {card('Total Magmom', data.total_magmom != null ? `${data.total_magmom} μB` : '—')}
          {card('Fermi Level', data.fermi_level_eV != null ? `${data.fermi_level_eV} eV` : '—')}
        </div>
        <div style={{ background: '#1e293b', borderRadius: 8, padding: '14px 18px', color: '#fbbf24', fontSize: 13, border: '1px solid #854d0e' }}>
          Material is metallic — effective masses are not defined. Effective masses require a finite band gap (semiconductor or insulator).
        </div>
        <div style={{ marginTop: 12, fontSize: 11, color: '#64748b' }}>
          GPAW PBE — band structure along pymatgen HighSymmKpath. Parabolic fit at band extrema.
        </div>
      </div>
    );
  }

  const me = em.electron || {};
  const mh = em.hole || {};

  const fmtMass = (m, r2) => {
    if (m == null) return '— (fit failed)';
    const quality = r2 != null && r2 < 0.9 ? ' ⚠' : '';
    return `${m} m_e${quality}`;
  };

  return (
    <div style={{ padding: 20, color: '#e2e8f0' }}>
      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 20 }}>
        {card('Band Gap', em.band_gap_eV != null ? `${em.band_gap_eV} eV (${em.gap_type || '—'})` : '—')}
        {card('Electron m*', fmtMass(me.m_star, me.fit_r_squared))}
        {card('Hole m*', fmtMass(mh.m_star, mh.fit_r_squared))}
        {card('CBM Location', me.k_point_label || '—')}
        {card('VBM Location', mh.k_point_label || '—')}
        {card('Magnetic Character', data.magnetic_character || '—')}
      </div>

      {/* Detailed fit info */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div style={{ background: '#0a1120', borderRadius: 8, padding: 16 }}>
          <div style={{ color: '#38bdf8', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Electron (CBM)</div>
          <div style={{ fontSize: 12, lineHeight: 1.8 }}>
            <div><span style={{ color: '#94a3b8' }}>Effective mass:</span> {me.m_star != null ? `${me.m_star} m_e` : '—'}</div>
            <div><span style={{ color: '#94a3b8' }}>k-point:</span> {me.k_point_label || '—'}</div>
            <div><span style={{ color: '#94a3b8' }}>Fit R²:</span> {me.fit_r_squared != null ? me.fit_r_squared : '—'}</div>
            {me.fit_r_squared != null && me.fit_r_squared < 0.9 && (
              <div style={{ color: '#fbbf24', fontSize: 11, marginTop: 4 }}>Low R² — band may be non-parabolic at this k-point. Consider increasing k-path density.</div>
            )}
          </div>
        </div>
        <div style={{ background: '#0a1120', borderRadius: 8, padding: 16 }}>
          <div style={{ color: '#f472b6', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Hole (VBM)</div>
          <div style={{ fontSize: 12, lineHeight: 1.8 }}>
            <div><span style={{ color: '#94a3b8' }}>Effective mass:</span> {mh.m_star != null ? `${mh.m_star} m_e` : '—'}</div>
            <div><span style={{ color: '#94a3b8' }}>k-point:</span> {mh.k_point_label || '—'}</div>
            <div><span style={{ color: '#94a3b8' }}>Fit R²:</span> {mh.fit_r_squared != null ? mh.fit_r_squared : '—'}</div>
            {mh.fit_r_squared != null && mh.fit_r_squared < 0.9 && (
              <div style={{ color: '#fbbf24', fontSize: 11, marginTop: 4 }}>Low R² — band may be non-parabolic at this k-point. Consider increasing k-path density.</div>
            )}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: '#64748b' }}>
        GPAW PBE — effective masses from parabolic fit of band curvature at CBM/VBM along high-symmetry k-path. m* = ℏ²/(d²E/dk²).
      </div>
    </div>
  );
}

// ── QE Phonon Result View ─────────────────────────────────────────────────
function PhononResultView({ data }) {
  if (!data) return null;
  const disp = data.phonon_dispersion || {};
  const phdos = data.phonon_dos || {};
  const branches = disp.branches || [];
  const qDist = disp.q_distances || [];
  const labels = disp.labels || [];
  const freqs = phdos.frequencies || [];
  const dos = phdos.dos || [];

  // SVG phonon dispersion plot
  const W = 600, H = 300, pad = { t: 20, r: 20, b: 40, l: 60 };
  const allFreqs = branches.flat();
  const fMin = allFreqs.length ? Math.min(...allFreqs) : 0;
  const fMax = allFreqs.length ? Math.max(...allFreqs) : 1;
  const qMax = qDist.length ? qDist[qDist.length - 1] : 1;
  const fRange = fMax - fMin || 1;
  const sx = (W - pad.l - pad.r) / (qMax || 1);

  // Phonon DOS mini-plot
  const DW = 150;
  const dosMax = dos.length ? Math.max(...dos) : 1;
  const dsx = (DW - 10) / (dosMax || 1);

  const card = (label, value) => (
    <div key={label} style={{ background: '#0a1120', borderRadius: 8, padding: '12px 16px' }}>
      <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4 }}>{label}</div>
      <div style={{ color: '#4ade80', fontSize: 15, fontWeight: 600 }}>{value}</div>
    </div>
  );

  return (
    <div style={{ padding: 20, color: '#e2e8f0' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 20 }}>
        {card('Dynamically Stable', data.is_dynamically_stable ? '✓ Yes' : '✗ Unstable')}
        {data.debye_temperature_K != null && card('Debye Temperature', `${data.debye_temperature_K} K`)}
        {data.max_phonon_freq_THz != null && card('Max Phonon Freq', `${data.max_phonon_freq_THz} THz`)}
      </div>

      {branches.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <div>
            <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 6 }}>Phonon Dispersion (cm⁻¹)</div>
            <svg width={W} height={H} style={{ background: '#060d18', borderRadius: 8 }}>
              {/* Zero line */}
              <line x1={pad.l} y1={pad.t + (fMax / fRange) * (H - pad.t - pad.b)} x2={W - pad.r} y2={pad.t + (fMax / fRange) * (H - pad.t - pad.b)} stroke="#334155" strokeDasharray="4" />
              {/* Y-axis labels */}
              {[0, 0.25, 0.5, 0.75, 1].map(f => {
                const val = fMin + f * fRange;
                const y = pad.t + (1 - f) * (H - pad.t - pad.b);
                return <text key={f} x={pad.l - 6} y={y + 4} fill="#64748b" fontSize={10} textAnchor="end">{Math.round(val)}</text>;
              })}
              {/* Branch lines */}
              {branches.map((branch, bi) => (
                <polyline key={bi} fill="none" stroke={bi === 0 ? '#4ade80' : '#38bdf8'} strokeWidth={1.2}
                  points={branch.map((f, qi) => `${pad.l + qDist[qi] * sx},${pad.t + (fMax - f) / fRange * (H - pad.t - pad.b)}`).join(' ')} />
              ))}
              {/* High-symmetry labels */}
              {labels.map((l, i) => (
                <text key={i} x={pad.l + (l.q_dist || 0) * sx} y={H - 8} fill="#94a3b8" fontSize={11} textAnchor="middle">{l.label}</text>
              ))}
            </svg>
          </div>

          {freqs.length > 0 && (
            <div>
              <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 6 }}>Phonon DOS</div>
              <svg width={DW} height={H} style={{ background: '#060d18', borderRadius: 8 }}>
                <polyline fill="none" stroke="#4ade80" strokeWidth={1.2}
                  points={freqs.map((f, i) => `${10 + dos[i] * dsx},${pad.t + (fMax - f) / fRange * (H - pad.t - pad.b)}`).join(' ')} />
              </svg>
            </div>
          )}
        </div>
      )}

      {!data.is_dynamically_stable && (
        <div style={{ color: '#f87171', fontSize: 13, marginTop: 8 }}>
          ⚠ Imaginary phonon frequencies detected — this structure may be dynamically unstable.
        </div>
      )}
      <div style={{ marginTop: 12, fontSize: 11, color: '#64748b' }}>
        Quantum ESPRESSO ph.x — DFPT phonon calculation on q-grid → force constants → dispersion and DOS via matdyn.x.
      </div>
    </div>
  );
}

// ── QE Thermal Result View ────────────────────────────────────────────────
function ThermalResultView({ data }) {
  if (!data) return null;
  const tp = data.thermal_properties || {};
  const temps = tp.temperatures || [];
  const cv = tp.heat_capacity || [];
  const entropy = tp.entropy || [];
  const fe = tp.free_energy || [];

  const W = 600, H = 280, pad = { t: 20, r: 20, b: 40, l: 60 };
  const tMax = temps.length ? temps[temps.length - 1] : 1000;

  // Plot helper: draw one curve
  const plotCurve = (values, color, yLabel, unit) => {
    if (!values.length) return null;
    const vMin = Math.min(...values);
    const vMax = Math.max(...values);
    const vRange = vMax - vMin || 1;
    const sx = (W - pad.l - pad.r) / (tMax || 1);

    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 6 }}>{yLabel} ({unit})</div>
        <svg width={W} height={H} style={{ background: '#060d18', borderRadius: 8 }}>
          {[0, 0.25, 0.5, 0.75, 1].map(f => {
            const val = vMin + f * vRange;
            const y = pad.t + (1 - f) * (H - pad.t - pad.b);
            return <text key={f} x={pad.l - 6} y={y + 4} fill="#64748b" fontSize={10} textAnchor="end">{val.toFixed(1)}</text>;
          })}
          {[0, 250, 500, 750, 1000].filter(t => t <= tMax).map(t => (
            <text key={t} x={pad.l + t * sx} y={H - 8} fill="#64748b" fontSize={10} textAnchor="middle">{t} K</text>
          ))}
          <polyline fill="none" stroke={color} strokeWidth={1.5}
            points={temps.map((t, i) => `${pad.l + t * sx},${pad.t + (vMax - values[i]) / vRange * (H - pad.t - pad.b)}`).join(' ')} />
        </svg>
      </div>
    );
  };

  const card = (label, value) => (
    <div key={label} style={{ background: '#0a1120', borderRadius: 8, padding: '12px 16px' }}>
      <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4 }}>{label}</div>
      <div style={{ color: '#4ade80', fontSize: 15, fontWeight: 600 }}>{value}</div>
    </div>
  );

  // Find room-temperature values (300 K)
  const rt_idx = temps.findIndex(t => t >= 300);
  const cv_300 = rt_idx >= 0 ? cv[rt_idx] : null;
  const s_300 = rt_idx >= 0 ? entropy[rt_idx] : null;
  const fe_300 = rt_idx >= 0 ? fe[rt_idx] : null;

  return (
    <div style={{ padding: 20, color: '#e2e8f0' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 20 }}>
        {data.debye_temperature_K != null && card('Debye Temperature', `${data.debye_temperature_K} K`)}
        {cv_300 != null && card('Cv (300 K)', `${cv_300.toFixed(2)} meV/K`)}
        {s_300 != null && card('Entropy (300 K)', `${s_300.toFixed(2)} meV/K`)}
        {fe_300 != null && card('Free Energy (300 K)', `${fe_300.toFixed(3)} eV`)}
        {card('Dynamically Stable', data.is_dynamically_stable ? '✓ Yes' : '✗ Unstable')}
      </div>

      {plotCurve(cv, '#4ade80', 'Heat Capacity (Cv)', 'meV/K per cell')}
      {plotCurve(entropy, '#38bdf8', 'Vibrational Entropy', 'meV/K per cell')}
      {plotCurve(fe, '#a78bfa', 'Helmholtz Free Energy', 'eV per cell')}

      <div style={{ marginTop: 12, fontSize: 11, color: '#64748b' }}>
        Quantum ESPRESSO — thermal properties from phonon DOS via Bose-Einstein statistics. Temperature range: 10–1000 K.
      </div>
    </div>
  );
}

// ── Elastic Tensor (DFT) result view ────────────────────────────────────────
function ElasticTensorPanel({ data }) {
  const card = (label, value) => (
    <div key={label} style={{ background: '#0a1120', borderRadius: 8, padding: '12px 16px' }}>
      <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4 }}>{label}</div>
      <div style={{ color: '#4ade80', fontSize: 15, fontWeight: 600 }}>{value}</div>
    </div>
  );

  const fmt2 = (v) => (typeof v === 'number' ? v.toFixed(2) : '—');
  const tensor = Array.isArray(data?.elastic_tensor_gpa) ? data.elastic_tensor_gpa : null;

  return (
    <div style={{ padding: 20, color: '#e2e8f0' }}>
      {/* Summary row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 20 }}>
        {card("Young's Modulus", data?.youngs_modulus_gpa != null ? `${fmt2(data.youngs_modulus_gpa)} GPa` : '—')}
        {card('Shear Modulus', data?.shear_modulus_gpa != null ? `${fmt2(data.shear_modulus_gpa)} GPa` : '—')}
        {card('Bulk Modulus (VRH)', data?.bulk_modulus_vrh_gpa != null ? `${fmt2(data.bulk_modulus_vrh_gpa)} GPa` : '—')}
        {card("Poisson's Ratio", data?.poisson_ratio != null ? Number(data.poisson_ratio).toFixed(4) : '—')}
        {card('Speed of Sound', data?.speed_of_sound_ms != null ? `${Number(data.speed_of_sound_ms).toFixed(1)} m/s` : '—')}
      </div>

      {/* Compact 6x6 elastic tensor matrix */}
      {tensor && (
        <div>
          <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 6 }}>Elastic Tensor C_ij (GPa, Voigt notation)</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ padding: '4px 8px', borderBottom: '1px solid #334155' }} />
                  {[1, 2, 3, 4, 5, 6].map(j => (
                    <th key={j} style={{ color: '#94a3b8', padding: '4px 8px', textAlign: 'center', borderBottom: '1px solid #334155' }}>{j}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tensor.map((row, i) => (
                  <tr key={i}>
                    <th style={{ color: '#94a3b8', padding: '4px 8px', textAlign: 'center', borderRight: '1px solid #334155' }}>{i + 1}</th>
                    {(row || []).map((v, j) => (
                      <td key={j} style={{ color: '#e2e8f0', padding: '4px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', background: i === j ? '#0a1120' : 'transparent' }}>
                        {fmt2(v)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ marginTop: 12, fontSize: 11, color: '#64748b' }}>
        GPAW PBE, full elastic tensor from finite-strain stress fitting (normal and shear deformations, Voigt convention). Initial magnetic moments from pymatgen element defaults.
      </div>
    </div>
  );
}

const DFT_WARNINGS = {
  dft_singlepoint: '~2–10 min. Spin-polarized SCF at fixed geometry. Returns energy, Fermi level, magnetic character, and coarse band gap.',
  dft_relax:       '~5–30 min. BFGS geometry optimization. Returns relaxed structure + final energy, forces, band gap, magnetic character.',
  dft_elastic:     '~15–45 min. Three SCF calculations via finite strain. Returns bulk modulus only. Best run on a relaxed structure.',
  dft_elastic_tensor: "~45–120 min. Full finite-strain elastic tensor: normal and shear deformations fit to the 6x6 Voigt matrix. Returns Young's/shear/bulk (VRH) moduli, Poisson ratio, and speed of sound. Best run on a relaxed structure.",
  dft_dos:         '~5–15 min. Full electronic density of states — total, element-projected, orbital-projected. Spin-resolved for magnetic materials. Extracts DFT band gap.',
  dft_bandstructure: '~10–20 min. Energy bands along high-symmetry k-path. Identifies direct/indirect band gap. Spin-resolved for magnetic materials.',
  dft_spin_density: '~5–15 min. 3D spin-up minus spin-down electron density. Exported as cube file for isosurface visualization. Only meaningful for magnetic materials.',
  dft_optical: '~10–30 min. Dielectric function and optical absorption spectrum via GPAW linear response. Returns ε₁(ω), ε₂(ω), absorption coefficient, optical band gap, and static dielectric constant.',
  dft_effective_mass: '~10–25 min. Effective masses at CBM (electron) and VBM (hole) from band curvature. Requires a semiconductor/insulator — metals have no band gap.',
  // Quantum ESPRESSO mirrors
  qe_singlepoint: '~2–10 min. QE pw.x SCF at fixed geometry. Returns energy, Fermi level, magnetic character, band gap. Cross-validate against GPAW.',
  qe_relax:       '~5–30 min. QE vc-relax (variable-cell BFGS). Returns relaxed structure + final energy, forces, band gap.',
  qe_elastic:     '~15–45 min. Three QE SCF calculations via finite strain. Returns bulk modulus.',
  qe_dos:         '~5–20 min. QE SCF → NSCF → dos.x + projwfc.x. Total, element-projected, orbital-projected DOS. Extracts DFT band gap.',
  qe_bandstructure: '~10–25 min. QE bands along high-symmetry k-path via bands.x. Identifies direct/indirect band gap.',
  qe_optical: '~10–30 min. QE epsilon.x — dielectric function and optical absorption. Returns ε₁(ω), ε₂(ω), absorption coefficient, optical band gap.',
  qe_effective_mass: '~10–25 min. QE effective masses from fine k-path band curvature. Requires semiconductor/insulator.',
  // QE-exclusive
  qe_phonon: '~30–120 min. QE ph.x phonon calculation on q-grid → dispersion + phonon DOS via matdyn.x. Identifies dynamical stability and Debye temperature.',
  qe_thermal: '~30–120 min. Phonon-based thermal properties: heat capacity, entropy, free energy, internal energy vs temperature (10–1000 K).',
};

const DFT_LABELS = {
  dft_singlepoint: 'GPAW - Single Point',
  dft_relax:       'GPAW - Relaxation',
  dft_elastic:     'GPAW - Elastic (Bulk Mod.)',
  dft_elastic_tensor: 'Elastic Tensor (DFT)',
  dft_dos:         'GPAW - Density of States',
  dft_bandstructure: 'GPAW - Band Structure',
  dft_spin_density: 'GPAW - Spin Density',
  dft_optical: 'GPAW - Optical Properties',
  dft_effective_mass: 'GPAW - Effective Masses',
  // Quantum ESPRESSO mirrors
  qe_singlepoint: 'QE - Single Point',
  qe_relax:       'QE - Relaxation',
  qe_elastic:     'QE - Elastic (Bulk Mod.)',
  qe_dos:         'QE - Density of States',
  qe_bandstructure: 'QE - Band Structure',
  qe_optical: 'QE - Optical Properties',
  qe_effective_mass: 'QE - Effective Masses',
  // QE-exclusive
  qe_phonon: 'QE - Phonon Dispersion',
  qe_thermal: 'QE - Thermal Properties',
};

function DftAsyncPanel({ kind, data, loading, candidateFingerprint, onSubmit, onCancel, onStop, onPollUpdate }) {
  const { dftBaseUrl, qeBaseUrl, byocActive, byocAuthHeaders } = useAppContext() || {};
  const isQe = kind.startsWith('qe_');
  const base = isQe ? (qeBaseUrl || 'http://localhost:8015') : (dftBaseUrl || 'http://localhost:8013');
  const jobEndpoint = isQe ? 'qe_job' : 'dft_job';
  const authHdrs = byocActive ? byocAuthHeaders : undefined;
  const registryKey = candidateFingerprint ? `${candidateFingerprint}::${kind}` : null;
  // Org-scoped resume (2026-08-22): read through a ref so the one-shot
  // resume effect below never re-fires when the auth context re-renders.
  const lookupScope = useJobLookupScope();
  const lookupScopeRef = React.useRef(lookupScope);
  lookupScopeRef.current = lookupScope;
  const elapsed = useElapsed(data?.queued_at);

  // Cancel gating (2026-08-22): your own job needs jobs.cancel.own, a
  // teammate's needs jobs.cancel.org. submitted_by is stamped at submit
  // (submitDftJob) and on resume (match.uid); a record without it is
  // treated as the current user's own.
  const { user: authUser } = useAuth() || {};
  const ownCancel = useCapability('jobs.cancel.own');
  const orgCancel = useCapability('jobs.cancel.org');
  const isOwnJob = !data?.submitted_by || !authUser?.uid || data.submitted_by === authUser.uid;
  const cancelCap = isOwnJob ? ownCancel : orgCancel;

  const handleStop = async () => {
    if (!cancelCap.allowed) return;
    if (data?.job_id) {
      try { await authedFetch(`${base}/analyze/crystal/${jobEndpoint}/${data.job_id}/cancel`, { method: 'POST', headers: authHdrs }); } catch (_) {}
    }
    // M9: the candidate deep-link page renders this panel read-only (no handlers) —
    // a bare onStop() threw on undefined.
    onStop?.();
  };

  // #41 — Use ref for callback so polling interval isn't torn down on every parent re-render
  const onPollUpdateRef = React.useRef(onPollUpdate);
  React.useEffect(() => { onPollUpdateRef.current = onPollUpdate; }, [onPollUpdate]);

  // Resume-on-reopen, generic across every DFT/QE job kind: this panel
  // mounting with no known job (data is null/undefined, either freshly
  // opened or reopened after whichever surface submitted the job, e.g.
  // LatticeWizard's "Run DFT, then generate" chain, was closed) doesn't
  // necessarily mean no job is running. Resume order: local component state
  // (the `data` prop check above/below -- a job already known to THIS
  // render tree always wins, no lookup needed) -> Firestore (durable,
  // survives a different browser/profile/device, see
  // src/utils/dftJobLookup.js -- dftJobs for dft_* kinds, qeJobs for qe_*
  // kinds, qe-service reached parity with dft-service's persistence
  // 2026-08-20) -> the localStorage dftJobRegistry (dev-fallback only, for
  // when Firebase isn't configured, see resolveFirst below).
  //
  // A non-terminal Firestore match (queued/running/cancelling) is fed
  // through onPollUpdate exactly like the old registry-only path -- the real
  // polling effect below picks up job_id on its next tick once `data` is
  // set, resuming REST polling with the elapsed timer backdated to the
  // job's real submitted_at.
  //
  // A terminal Firestore match hydrates straight into analysisCache via the
  // SAME onPollUpdate path a live poll result uses (AppShell's
  // handleDftPollUpdate persists it to Firestore exactly once regardless of
  // origin) -- no need to wait on a 5s poll tick for something already
  // known. If the doc's result was too large to inline (result_gcs pointer,
  // see persistence.py's offload) there is nothing here to hydrate from
  // directly: one REST poll of the job id reconstructs it server-side
  // (persistence.read_job_doc resolves the GCS pointer). A terminal match,
  // hydrated either way, clears the localStorage registry entry (if any),
  // matching what the real 5s poll effect below already does for a terminal
  // result -- a stale registry entry left behind here would otherwise be
  // re-adopted by a later resume that never checks Firestore (a candidate
  // whose Firestore doc has since been deleted, say).
  //
  // Review gate (Important, pre-deploy): the lookup below is a real network
  // round trip. Until it resolves, `resuming` stays true and the render
  // below shows a disabled "Checking for an existing run..." button instead
  // of the active "Submit DFT Job" CTA -- otherwise a user on a fresh
  // browser/session (nothing in the registry yet, but a real job already in
  // Firestore) could click Submit mid-lookup and fire a second, duplicate,
  // expensive GPAW/QE run, exactly the cross-device scenario this index
  // exists to prevent. `resuming` starts true whenever there is something to
  // actually await (a candidateFingerprint, for any kind, dft_* and qe_*
  // alike); the `finally` below guarantees it clears on every exit path,
  // including an unexpected error, never leaving the button stuck disabled.
  const [resuming, setResuming] = React.useState(() => !data && !!registryKey);
  const resumeAttemptedRef = React.useRef(false);
  React.useEffect(() => {
    if (resumeAttemptedRef.current) return;
    resumeAttemptedRef.current = true;
    if (data || !registryKey) return;
    let cancelled = false;
    // 'dft_singlepoint' -> 'singlepoint', 'qe_singlepoint' -> 'singlepoint':
    // matches payload.type recorded by _init_job in both services.
    const wantedType = kind.replace(/^(dft_|qe_)/, '');
    (async () => {
      try {
        const jobs = await findJobsForCandidate(isQe ? 'qeJobs' : 'dftJobs', candidateFingerprint, lookupScopeRef.current);
        if (cancelled) return;
        const match = jobs.find((j) => j?.payload?.type === wantedType);
        if (match?.job_id && typeof onPollUpdateRef.current === 'function') {
          const submittedMs = match.submitted_at ? Date.parse(match.submitted_at) : Date.now();
          if (['queued', 'running', 'cancelling'].includes(match.status)) {
            onPollUpdateRef.current({ status: match.status, job_id: match.job_id, queued_at: submittedMs, progress: match.progress, submitted_by: match.uid || null });
            return;
          }
          if (match.status === 'complete') {
            if (match.result_gcs && !match.result) {
              // Result was offloaded to GCS: one REST poll reconstructs it
              // (the endpoint resolves the pointer server-side).
              try {
                const r = await authedFetch(`${base}/analyze/crystal/${jobEndpoint}/${match.job_id}`, { headers: authHdrs });
                const polled = await r.json();
                if (!cancelled) {
                  onPollUpdateRef.current(polled);
                  if (registryKey && ['complete', 'error', 'cancelled'].includes(polled?.status)) clearJob(registryKey);
                }
              } catch (_) { /* best-effort resume; falls through to registry below on failure */ }
              return;
            }
            onPollUpdateRef.current({ job_id: match.job_id, status: 'complete', ...(match.result || {}) });
            if (registryKey) clearJob(registryKey);
            return;
          }
          if (match.status === 'error' || match.status === 'cancelled') {
            onPollUpdateRef.current({ job_id: match.job_id, status: match.status, error: match.error });
            if (registryKey) clearJob(registryKey);
            return;
          }
        }
        // No Firestore match (unconfigured Firebase, a job predating this
        // index, or genuinely nothing for this candidate+kind): fall back to
        // the localStorage registry, same behavior as before this resume
        // order existed.
        const job = getJob(registryKey);
        if (!cancelled && job?.jobId && typeof onPollUpdateRef.current === 'function') {
          onPollUpdateRef.current({ status: 'queued', job_id: job.jobId, queued_at: job.submittedAt });
        }
      } finally {
        // Runs on every exit above, including the early `return`s and an
        // unexpected throw -- the disabled "Checking..." state must never
        // stick, even if findJobsForCandidate's own error handling somehow
        // didn't already reduce a failure to [].
        if (!cancelled) setResuming(false);
      }
    })();
    return () => { cancelled = true; };
  }, [data, registryKey, candidateFingerprint, kind, isQe, base, jobEndpoint, authHdrs]);

  React.useEffect(() => {
    if (!data?.job_id || ['complete', 'error', 'cancelled'].includes(data.status)) return;
    let missCount = 0;
    const id = setInterval(async () => {
      // M9: read-only views (candidate deep link) pass no onPollUpdate — polling
      // would only fetch results to drop them (the call threw inside the catch).
      if (typeof onPollUpdateRef.current !== 'function') return;
      try {
        const r = await authedFetch(`${base}/analyze/crystal/${jobEndpoint}/${data.job_id}`, { headers: authHdrs });
        if (r.status === 404 || r.status === 410) {
          // Job expired or cleaned up on the server
          missCount++;
          if (missCount >= 3) {
            clearInterval(id);
            if (registryKey) clearJob(registryKey);
            onPollUpdateRef.current({ status: 'error', error: 'Job no longer exists on the server. It may have completed and been cleaned up, or the service was restarted.' });
          }
          return;
        }
        missCount = 0;
        const result = await r.json();
        if (registryKey && ['complete', 'error', 'cancelled'].includes(result.status)) clearJob(registryKey);
        onPollUpdateRef.current(result);
      } catch (_) {}
    }, 5000);
    return () => clearInterval(id);
  }, [data?.job_id, data?.status, base, jobEndpoint, authHdrs, registryKey]);

  if (loading && !data) {
    return <div style={{ color: '#94a3b8', padding: 24 }}>Submitting DFT job…</div>;
  }

  if (!data) {
    if (resuming) {
      // See the resume effect above: still awaiting the Firestore lookup
      // (or its registry fallback) for this candidate+kind. Rendering the
      // active Submit CTA here would let a user fire a duplicate,
      // expensive job before resume had a chance to find an existing one.
      return (
        <div style={{ padding: 20 }}>
          <div style={{ color: '#fbbf24', fontWeight: 700, marginBottom: 8 }}>{DFT_LABELS[kind]}</div>
          <div style={{ color: '#94a3b8', marginBottom: 16, fontSize: 13 }}>{DFT_WARNINGS[kind]}</div>
          <button disabled style={{ background: '#1e293b', color: '#94a3b8', border: 'none', borderRadius: 6, padding: '8px 20px', cursor: 'not-allowed', marginRight: 8 }}>
            Checking for an existing run…
          </button>
          <button style={{ background: '#1e293b', color: '#94a3b8', border: 'none', borderRadius: 6, padding: '8px 20px', cursor: 'pointer' }} onClick={onCancel}>
            Cancel
          </button>
        </div>
      );
    }
    return (
      <div style={{ padding: 20 }}>
        <div style={{ color: '#fbbf24', fontWeight: 700, marginBottom: 8 }}>{DFT_LABELS[kind]}</div>
        <div style={{ color: '#94a3b8', marginBottom: 16, fontSize: 13 }}>{DFT_WARNINGS[kind]}</div>
        <button style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 20px', cursor: 'pointer', marginRight: 8 }} onClick={onSubmit}>
          Submit DFT Job
        </button>
        <button style={{ background: '#1e293b', color: '#94a3b8', border: 'none', borderRadius: 6, padding: '8px 20px', cursor: 'pointer' }} onClick={onCancel}>
          Cancel
        </button>
      </div>
    );
  }

  if (data.status === 'queued' || data.status === 'running' || data.status === 'cancelling') {
    const statusText = data.status === 'queued'
      ? 'Job queued — waiting for compute slot…'
      : data.status === 'cancelling'
        // D9 (durability audit 2026-08-20): a remote cancel (job owned by a
        // different service instance) is honored by that instance's next
        // heartbeat, up to about two minutes away, not instantly, so this
        // says so rather than implying it already stopped.
        ? 'Cancelling: this can take up to about two minutes to actually stop.'
        : isQe ? 'Running Quantum ESPRESSO calculation…' : 'Running GPAW DFT calculation…';
    return (
      <div style={{ padding: 24, color: '#94a3b8' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <span>⏳</span>
          <span>{statusText}</span>
        </div>
        {kind === 'dft_elastic_tensor' && data.progress && (
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>
            Deformation {data.progress.done} of {data.progress.total}.
          </div>
        )}
        {elapsed && (
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
            Elapsed: <span style={{ color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>{elapsed}</span>
          </div>
        )}
        {data.status !== 'cancelling' && onStop && (
          <button
            style={{ background: '#450a0a', color: '#f87171', border: '1px solid #7f1d1d', borderRadius: 6, padding: '6px 16px', cursor: 'pointer', fontSize: 13, opacity: cancelCap.allowed ? 1 : 0.5 }}
            disabled={!cancelCap.allowed}
            title={cancelCap.allowed ? undefined : cancelCap.message}
            onClick={handleStop}
          >
            Stop Job
          </button>
        )}
        {!onStop && (
          <div style={{ fontSize: 12, color: '#64748b' }}>
            Snapshot view — job control and live progress are available from the Results or Saved page.
          </div>
        )}
      </div>
    );
  }

  if (data.status === 'cancelled') {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ color: '#94a3b8', marginBottom: 14 }}>Job was cancelled.</div>
        <button style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 18px', cursor: 'pointer', marginRight: 8, fontSize: 13 }} onClick={onSubmit}>
          Submit Again
        </button>
        <button style={{ background: '#1e293b', color: '#94a3b8', border: 'none', borderRadius: 6, padding: '7px 18px', cursor: 'pointer', fontSize: 13 }} onClick={onCancel}>
          Close
        </button>
      </div>
    );
  }

  if (data.status === 'error') {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ color: '#f87171', marginBottom: 14 }}>DFT error: {data.error}</div>
        {onStop && (
          <button style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 18px', cursor: 'pointer', marginRight: 8, fontSize: 13 }} onClick={onStop}>
            Retry
          </button>
        )}
        {onCancel && (
          <button style={{ background: '#1e293b', color: '#94a3b8', border: 'none', borderRadius: 6, padding: '7px 18px', cursor: 'pointer', fontSize: 13 }} onClick={onCancel}>
            Close
          </button>
        )}
      </div>
    );
  }

  // ── DOS / Band Structure results ─────────────────────────────────────────
  if (kind === 'dft_dos' || kind === 'qe_dos') return <DftDosResultView data={data} />;
  if (kind === 'dft_bandstructure' || kind === 'qe_bandstructure') return <BandStructureResultView data={data} />;
  if (kind === 'dft_spin_density') return <SpinDensityResultView data={data} />;
  if (kind === 'dft_optical' || kind === 'qe_optical') return <OpticalResultView data={data} />;
  if (kind === 'dft_effective_mass' || kind === 'qe_effective_mass') return <EffectiveMassResultView data={data} />;
  if (kind === 'dft_elastic_tensor') return <ElasticTensorPanel data={data} />;
  // QE-exclusive result views
  if (kind === 'qe_phonon') return <PhononResultView data={data} />;
  if (kind === 'qe_thermal') return <ThermalResultView data={data} />;

  // ── Result grids ──────────────────────────────────────────────────────────
  const card = (label, value) => (
    <div key={label} style={{ background: '#0a1120', borderRadius: 8, padding: '12px 16px' }}>
      <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4 }}>{label}</div>
      <div style={{ color: '#4ade80', fontSize: 15, fontWeight: 600 }}>{value}</div>
    </div>
  );

  const bandGapValue = data.is_metal
    ? <span style={{ background: '#1e3a5f', color: '#38bdf8', borderRadius: 4, padding: '2px 8px', fontSize: 13 }}>Metal</span>
    : (data.band_gap_eV != null ? `${data.band_gap_eV} eV` : '—');

  if (kind === 'dft_elastic' || kind === 'qe_elastic') {
    return (
      <div style={{ padding: 20, color: '#e2e8f0' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, maxWidth: 240 }}>
          {card('Bulk Modulus', data.bulk_modulus_GPa != null ? `${data.bulk_modulus_GPa} GPa` : '—')}
        </div>
        <div style={{ marginTop: 12, fontSize: 11, color: '#64748b' }}>{isQe ? 'QE' : 'GPAW'} PBE — finite-strain elastic constant. Initial magnetic moments from pymatgen element defaults.</div>
      </div>
    );
  }

  const handleDownloadCif = () => {
    if (!data.relaxed_cif) return;
    const blob = new Blob([data.relaxed_cif], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'relaxed.cif'; a.click();
    URL.revokeObjectURL(url);
  };

  const singlepointCards = [
    card('Energy/atom',   data.energy_per_atom  != null ? `${data.energy_per_atom} eV`  : '—'),
    card('Fermi Level',   data.fermi_level_eV   != null ? `${data.fermi_level_eV} eV`   : '—'),
    <div key="band_gap" style={{ background: '#0a1120', borderRadius: 8, padding: '12px 16px' }}>
      <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4 }}>Band Gap</div>
      <div style={{ color: '#4ade80', fontSize: 15, fontWeight: 600 }}>{bandGapValue}</div>
    </div>,
    card('DOS at E_F',    data.dos_at_ef         != null ? `${data.dos_at_ef} states/eV` : '—'),
    card('Magnetic',      data.magnetic_character || '—'),
    card('Total Magmom',  data.total_magmom       != null ? `${data.total_magmom} μB`    : '—'),
  ];

  if (kind === 'dft_singlepoint' || kind === 'qe_singlepoint') {
    return (
      <div style={{ padding: 20, color: '#e2e8f0' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12 }}>
          {singlepointCards}
        </div>
        <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12 }}>
          {card('Max Force',  data.max_force_eV_A  != null ? `${data.max_force_eV_A} eV/Å`  : '—')}
          {card('Max Stress', data.max_stress_GPa  != null ? `${data.max_stress_GPa} GPa`    : '—')}
        </div>
        <div style={{ marginTop: 12, fontSize: 11, color: '#64748b' }}>{isQe ? 'QE' : 'GPAW'} PBE — single-point SCF. Spin-polarized for magnetic elements; initial moments from pymatgen MP defaults.</div>
      </div>
    );
  }

  // dft_relax
  return (
    <div style={{ padding: 20, color: '#e2e8f0' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12 }}>
        {card('Final Energy/atom', data.energy_per_atom != null ? `${data.energy_per_atom} eV` : '—')}
        {card('Max Force',         data.max_force_eV_A  != null ? `${data.max_force_eV_A} eV/Å` : '—')}
        {card('Max Stress',        data.max_stress_GPa  != null ? `${data.max_stress_GPa} GPa`  : '—')}
        <div key="band_gap_r" style={{ background: '#0a1120', borderRadius: 8, padding: '12px 16px' }}>
          <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4 }}>Band Gap</div>
          <div style={{ color: '#4ade80', fontSize: 15, fontWeight: 600 }}>{bandGapValue}</div>
        </div>
        {card('Magnetic',          data.magnetic_character || '—')}
        {card('Relaxation Steps',  data.n_steps != null ? data.n_steps : '—')}
      </div>
      {data.relaxed_cif && (
        <button
          style={{ marginTop: 14, background: '#1e293b', color: '#38bdf8', border: '1px solid #38bdf8', borderRadius: 6, padding: '6px 16px', cursor: 'pointer', fontSize: 13 }}
          onClick={handleDownloadCif}
        >
          Download Relaxed CIF
        </button>
      )}
      <div style={{ marginTop: 12, fontSize: 11, color: '#64748b' }}>{isQe ? 'QE' : 'GPAW'} PBE — BFGS geometry optimization. Initial magnetic moments from pymatgen element defaults.</div>
    </div>
  );
}

function AnalysisPanel({ kind, data, loading, frame, candidateFingerprint, onPatentProceed, onPatentCancel, onPatentRetry, onDftJobSubmit, onDftJobCancel, onDftJobStop, onDftJobPollUpdate }) {
  if (kind === 'symmetry') return <SymmetryPanel data={data} loading={loading} />;
  if (kind === 'voronoi') return <VoronoiPanel data={data} loading={loading} />;
  if (kind === 'bond_distribution') return <BondDistPanel data={data} loading={loading} />;
  if (kind === 'dft_surrogate') return <DftSurrogatePanel data={data} loading={loading} />;
  if (kind === 'phase_stability') return <PhaseStabilityPanel data={data} loading={loading} />;
  if (kind === 'mp_neighbors') return <MpNeighborsPanel data={data} loading={loading} />;
  if (['dft_singlepoint','dft_relax','dft_elastic','dft_elastic_tensor','dft_dos','dft_bandstructure','dft_spin_density','dft_optical','dft_effective_mass',
       'qe_singlepoint','qe_relax','qe_elastic','qe_dos','qe_bandstructure','qe_optical','qe_effective_mass','qe_phonon','qe_thermal'].includes(kind)) return <DftAsyncPanel kind={kind} data={data} loading={loading} candidateFingerprint={candidateFingerprint} onSubmit={onDftJobSubmit} onCancel={onDftJobCancel} onStop={onDftJobStop} onPollUpdate={onDftJobPollUpdate} />;
  if (kind === 'economic_report') return <EconomicReportPanel data={data} loading={loading} />;
  if (kind === 'scientific_doc') return <ScientificDocPanel data={data} loading={loading} />;
  if (kind === 'patent_packet') {
    if (data?.status || loading) {
      return <PatentSearchStatusPanel data={data} loading={loading} onProceed={onPatentProceed} onCancel={onPatentCancel} onRetry={onPatentRetry} />;
    }
    return <PatentPacketPanel data={data} loading={loading} frame={frame} />;
  }
  return null;
}

// ── Tiered Analyze Dropdown ──────────────────────────────────────────────
function AnalyzeDropdown({ fIndex, tiers, analysisCache, activeAnalyses, analysisLoading, onToggle }) {
  const [collapsed, setCollapsed] = React.useState({});
  const toggle = (tierLabel) => setCollapsed(p => ({ ...p, [tierLabel]: !p[tierLabel] }));
  // Org capabilities (2026-08-22): DFT/QE kinds map to dft.*/qe.* ids; a
  // denied kind renders disabled with the admin message. Cheap analyses
  // (symmetry, CHGNet surrogate, ...) map to null and stay ungated.
  const { capabilities: caps } = useAuth() || {};
  const deniedFor = (kind) => { const id = capabilityForAnalysisKind(kind); return id && caps?.denied?.has(id) ? id : null; };
  return (
    <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 20, background: '#1e293b', border: '1px solid #334155', borderRadius: 6, minWidth: 240, boxShadow: '0 4px 12px rgba(0,0,0,0.4)', marginTop: 2, padding: '4px 0' }}>
      {tiers.map(tier => {
        const isCollapsed = !!collapsed[tier.label];
        return (
          <div key={tier.label}>
            <button
              className="btn btn-sm w-100 text-start"
              style={{ color: '#94a3b8', padding: '5px 12px', border: 'none', background: 'transparent', fontSize: 11, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6 }}
              onClick={() => toggle(tier.label)}
            >
              <span style={{ width: 12, textAlign: 'center', fontSize: 10 }}>{isCollapsed ? '▸' : '▾'}</span>
              {tier.label}
            </button>
            {!isCollapsed && tier.items.map(at => {
              const key = `${fIndex}::${at.kind}`;
              const cached = !!analysisCache[key] && analysisCache[key].ok !== false; // M8: a failed result is not "done"
              const active = activeAnalyses.has(key);
              const ldg = !!analysisLoading[key];
              const deniedId = deniedFor(at.kind);
              return (
                <AnalyzeDropdownItem key={at.kind}
                  label={at.label}
                  active={active}
                  cached={cached}
                  loading={ldg}
                  disabled={!!deniedId}
                  title={deniedId ? deniedMessageFor(deniedId) : undefined}
                  onClick={() => { if (!deniedId) onToggle(fIndex, at.kind); }}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

export function ExtxyzTable({ frames, meta, truncated, downloadUrl, downloadText, initialCache, sourceLabels, stageMap, bulkPersist }) {
  const { analyzeBaseUrl, dftBaseUrl, qeBaseUrl, byocActive, byocAuthHeaders, modelsAuthHeaders, saveCandidate, unsaveCandidate, isSaved, savedCandidates, getCandidateNotes, updateCandidateAnalyses, persistGeneratedCandidates, plan } = useAppContext() || {};
  const searchObjectives = Array.isArray(plan?.generation_spec?.targets) ? plan.generation_spec.targets : [];
  const dftAuthHdrs = byocActive ? byocAuthHeaders : undefined;
  const { user: authUser, orgMember, isSuperAdmin } = useAuth() || {};
  const orgId = orgMember?.orgId || (isSuperAdmin ? 'crimsonscan' : null);
  const patentCap = useCapability('reports.patent_packet');
  const latticeCap = useCapability('lattice.generate');

  // Stable fingerprint: formula + lattice params (a, b, c rounded to 2dp).
  // Set at generation time, never changes. Two models producing the same structure
  // with the same lattice share analyses (cross-model accumulation).
  // Space group is an analysis result, not part of identity.
  const _fpFrame = (frame) => {
    if (frame.type === 'molecule') return `mol_${frame.smiles}`;
    const abc = frame.lattice_abc;
    if (abc && abc.length === 3) {
      const [a, b, c] = abc.map(v => (typeof v === 'number' ? v.toFixed(2) : v));
      return `${frame.formula}_L:${a}_${b}_${c}`;
    }
    return `${frame.formula}_N:${frame.n || 0}`;
  };

  // Persist a completed analysis to Firestore for any candidate (saved or not)
  const persistAnalysis = (idx, kind, data) => {
    if (!updateCandidateAnalyses || !data) return;
    const frame = frames.find(f => f.index === idx);
    if (!frame) return;
    updateCandidateAnalyses(_fpFrame(frame), { [kind]: data });
  };

  // Shared write path for a DFT job poll result, regardless of which surface's
  // poll loop produced it (this table's own DftAsyncPanel, or LatticeWizard's
  // independent "Run DFT, then generate" poll — see src/utils/dftJobRegistry.js
  // for why either can be the one to observe a given tick once a job is in
  // flight). Keeps analysisCache — and therefore every consumer of it, the
  // Analyze panel and the wizard's own buildLatticeProps — in sync with
  // whichever surface actually talked to the server last, and persists
  // terminal results to Firestore exactly once regardless of origin.
  const handleDftPollUpdate = (idx, kind, result) => {
    const key = `${idx}::${kind}`;
    // queued_at is never present on a real server poll response; back-fill it
    // from whatever's already cached so the elapsed timer keeps its original
    // start time across polls. A resume-adopted job (dftJobRegistry) DOES set
    // queued_at explicitly on its first synthesized write, before anything is
    // cached yet — prefer that over the (absent) cache value so the timer
    // reflects the job's real submission time instead of losing it.
    const merged = { ...result, queued_at: result.queued_at ?? analysisCacheRef.current[key]?.queued_at };
    setAnalysisCache(p => ({ ...p, [key]: merged }));
    if (result?.status === 'complete') persistAnalysis(idx, kind, merged);
  };

  // Flywheel bulk capture: freshly parsed generation results (bulkPersist is only
  // passed by the fresh-extxyz call site — never for DB-loaded or saved candidates).
  // Consent gating + per-run dedup live in AppProvider.persistGeneratedCandidates.
  React.useEffect(() => {
    if (!bulkPersist || !frames?.length || !persistGeneratedCandidates) return;
    persistGeneratedCandidates(frames, {
      batchKey: bulkPersist.batchKey || '',
      source: bulkPersist.source || null,
      // Pooling gate (spec 2026-10-07 Part 0.5): a missing base is never pooled.
      base: bulkPersist.base || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkPersist?.batchKey]);

  const [expanded, setExpanded] = useState(null);
  const [expandedCif, setExpandedCif] = useState(null);
  const [expandedTraj, setExpandedTraj] = useState(null);
  const [expandedNotes, setExpandedNotes] = useState(null);
  const [cifCache, setCifCache] = useState(() => meta?.cifTexts || {});
  const cifCacheRef = useRef(cifCache);
  useEffect(() => { cifCacheRef.current = cifCache; }, [cifCache]);
  const [cifLoading, setCifLoading] = useState({});
  const [trajCache, setTrajCache] = useState({});
  const [trajLoading, setTrajLoading] = useState({});
  // Fetch wrapper that adds auth headers for relay (BYOC) or custom-model (/models/:id) URLs —
  // I8: custom-model result-file fetches previously got a bare fetch and 401'd silently.
  // CRI-14: every other URL (e.g. a per-crystal CIF artifact served straight off the MatterGen
  // service at /files/:runId/cifs/gen_N.cif) still needs the signed-in user's Firebase ID token,
  // so route through `authedFetch`; relay/custom-model headers, when present, take precedence.
  // Part 1a (B4): /models/ URLs get modelsAuthHeaders (X-Org-Id for the org), so the per-crystal
  // files of an auto-routed custom model load too, not only those of the manual pick.
  const authFetch = (url, opts) => {
    const hdrs = authHeadersForUrl(url, { relayHeaders: dftAuthHdrs, modelsHeaders: modelsAuthHeaders });
    return authedFetch(url, { ...opts, headers: { ...(opts?.headers || {}), ...(hdrs || {}) } });
  };
  const [savingFrames, setSavingFrames] = useState({});
  const [viewer3dFrame, setViewer3dFrame] = useState(null);
  const [viewer3dCif, setViewer3dCif] = useState(null);
  const [latticeCandidateIdx, setLatticeCandidateIdx] = useState(null);
  const [latticeCif, setLatticeCif] = useState(null);
  // Generic analysis state: analysisCache[`${idx}::${kind}`] = data, analysisLoading[key] = bool
  const [analysisCache, setAnalysisCache] = useState(() => initialCache || {});
  // Ref mirrors for async callbacks — avoids stale closure reads (see BUG-AUDIT.md)
  const analysisCacheRef = useRef(analysisCache);
  useEffect(() => { analysisCacheRef.current = analysisCache; }, [analysisCache]);

  // Load persisted analyses from Firestore on mount — prevents re-running analyses
  // that were already computed and saved in a previous session.
  const dbLoadedRef = useRef(false);
  useEffect(() => {
    if (dbLoadedRef.current) return;
    if (frames.length === 0) return;
    // Skip if initialCache was provided (saved page already loads from DB)
    if (initialCache && Object.keys(initialCache).length > 0) {
      dbLoadedRef.current = true;
      // Resume polling for in-flight DFT jobs from initialCache
      const inflightKeys = Object.entries(initialCache)
        .filter(([, d]) => d?.job_id && ['queued', 'running'].includes(d?.status));
      if (inflightKeys.length > 0) {
        setActiveAnalyses(prev => {
          const next = new Set(prev);
          inflightKeys.forEach(([key]) => next.add(key));
          return next;
        });
      }
      runAutoValidate(initialCache);
      return;
    }
    if (!orgId) { dbLoadedRef.current = true; runAutoValidate({}); return; }
    dbLoadedRef.current = true;
    // Build lookup entries: primary fingerprint is formula_L:a_b_c (lattice-based, stable).
    // Also try legacy formats so pre-existing analyses are found and migrated.
    const lookups = []; // { fp, index }[]
    const seen = new Set();
    frames.forEach(f => {
      const fps = [_fpFrame(f)];
      // Legacy fallback 1: old SG-based fingerprint (formula_SG:spaceGroup)
      if (f.formula) {
        if (f.spaceGroup) {
          const sgFp = `${f.formula}_SG:${f.spaceGroup}`;
          if (!fps.includes(sgFp)) fps.push(sgFp);
        }
        // Also try SG:unknown for analyses saved before symmetry ran
        const unknownFp = `${f.formula}_SG:unknown`;
        if (!fps.includes(unknownFp)) fps.push(unknownFp);
      }
      // Legacy fallback 2: reconstruct CRYSTAL::formula::SG=...::a=X,b=Y,c=Z (adapter uniqueKey format)
      if (f.formula) {
        const parts = ['CRYSTAL', f.formula];
        if (f.spaceGroup) parts.push(`SG=${f.spaceGroup}`);
        if (Array.isArray(f.lattice_abc)) parts.push(`a=${f.lattice_abc[0]},b=${f.lattice_abc[1]},c=${f.lattice_abc[2]}`);
        const reconstructed = parts.join('::');
        if (!fps.includes(reconstructed)) fps.push(reconstructed);
      }
      // Legacy fallback 3: exact DB fingerprint if available (from candidatesToFrames)
      if (f._legacyFp && !fps.includes(f._legacyFp)) fps.push(f._legacyFp);
      // Legacy fallback 4: score-based fingerprint from old system
      const scoreFp = `${f.formula}_${f.n}_${String(f.score ?? '').slice(0, 8)}`;
      if (!fps.includes(scoreFp)) fps.push(scoreFp);
      for (const fp of fps) {
        if (!seen.has(fp)) {
          seen.add(fp);
          lookups.push({ fp, index: f.index });
        }
      }
    });
    Promise.all(
      lookups.map(({ fp, index }) =>
        loadAnalyses(orgId, fp)
          .then(analyses => ({ index, analyses, fp }))
          .catch(() => ({ index, analyses: {}, fp }))
      )
    ).then(async (results) => {
      const patch = {};
      // Track analyses found under legacy fingerprints so we can migrate them
      const migrateQueue = []; // { index, kind, data }
      for (const { index, analyses, fp } of results) {
        const frame = frames.find(f => f.index === index);
        const canonFp = frame ? _fpFrame(frame) : null;
        const isLegacy = canonFp && fp !== canonFp;
        for (const [kind, data] of Object.entries(analyses)) {
          if (data && !patch[`${index}::${kind}`]) {
            patch[`${index}::${kind}`] = data;
            if (isLegacy) migrateQueue.push({ index, kind, data });
          }
        }
      }
      if (Object.keys(patch).length > 0) {
        setAnalysisCache(prev => ({ ...patch, ...prev })); // prev wins over DB (in case user re-ran)
      }

      // Resume polling for in-flight DFT jobs loaded from Firestore
      const inflightKeys = Object.entries(patch)
        .filter(([, d]) => d?.job_id && ['queued', 'running'].includes(d?.status));
      if (inflightKeys.length > 0) {
        setActiveAnalyses(prev => {
          const next = new Set(prev);
          inflightKeys.forEach(([key]) => next.add(key));
          return next;
        });
      }

      // Migrate legacy analyses to new fingerprint so future loads find them directly
      if (migrateQueue.length > 0 && updateCandidateAnalyses) {
        for (const { index, kind, data } of migrateQueue) {
          persistAnalysis(index, kind, data);
        }
      }
      // Auto-validate right here — patch contains all DB-loaded analyses,
      // so we can reliably skip anything already persisted.
      runAutoValidate(patch);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, frames.length]);
  const [analysisLoading, setAnalysisLoading] = useState({});
  const [activeAnalyses, setActiveAnalyses] = useState(new Set()); // Set of 'idx::kind' keys — controls panel visibility
  const [minimizedPanels, setMinimizedPanels] = useState(new Set()); // Set of 'idx::kind' keys that are collapsed
  const [analysisMenuOpen, setAnalysisMenuOpen] = useState(null); // crystal index with open menu
  const [reportMenuOpen, setReportMenuOpen] = useState(null);     // crystal index with open reports menu
  const [compareMenuOpen, setCompareMenuOpen] = useState(null);   // crystal index with open compare menu
  // Click-away: close all dropdown menus when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (analysisMenuOpen != null || reportMenuOpen != null || compareMenuOpen != null) {
        const btn = e.target.closest('.position-relative.d-inline-block');
        if (!btn) { setAnalysisMenuOpen(null); setReportMenuOpen(null); setCompareMenuOpen(null); }
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [analysisMenuOpen, reportMenuOpen, compareMenuOpen]);
  const skipPatentSearchRef = useRef(new Set());                  // keys where user has bypassed the prior-art check
  const cifs = Array.isArray(meta?.cifs) ? meta.cifs : [];
  const trajs = Array.isArray(meta?.trajectories) ? meta.trajectories : [];
  // const trajUrl = meta?.trajUrl || null; // reserved for future trajectory viewer

  const ANALYSIS_QUICK = [
    { kind: 'symmetry',          label: 'Symmetry',               endpoint: '/analyze/crystal/symmetry' },
    { kind: 'voronoi',           label: 'Voronoi Neighbors',      endpoint: '/analyze/crystal/voronoi' },
    { kind: 'bond_distribution', label: 'Bond Distribution',      endpoint: '/analyze/crystal/bond_distribution' },
    { kind: 'dft_surrogate',     label: 'DFT Surrogate (CHGNet)', endpoint: '/analyze/crystal/dft_surrogate' },
  ];
  const ANALYSIS_MEDIUM = [
    { kind: 'dft_singlepoint',  label: 'GPAW - Single Point',       custom: true, submitUrl: '/analyze/crystal/dft_singlepoint' },
    { kind: 'dft_dos',          label: 'GPAW - Density of States',  custom: true, submitUrl: '/analyze/crystal/dft_dos' },
    { kind: 'qe_singlepoint',   label: 'QE - Single Point',               custom: true, submitUrl: '/analyze/crystal/qe_singlepoint' },
    { kind: 'qe_dos',           label: 'QE - Density of States',          custom: true, submitUrl: '/analyze/crystal/qe_dos' },
  ];
  const ANALYSIS_LONG = [
    { kind: 'dft_relax',          label: 'GPAW - Relaxation',         custom: true, submitUrl: '/analyze/crystal/dft_relax' },
    { kind: 'dft_elastic',        label: 'GPAW - Elastic (Bulk Mod.)',       custom: true, submitUrl: '/analyze/crystal/dft_elastic' },
    { kind: 'dft_elastic_tensor', label: 'Elastic Tensor (DFT)',      custom: true, submitUrl: '/analyze/crystal/dft_elastic_tensor' },
    { kind: 'dft_bandstructure',  label: 'GPAW - Band Structure',     custom: true, submitUrl: '/analyze/crystal/dft_bandstructure' },
    { kind: 'dft_spin_density',   label: 'GPAW - Spin Density',       custom: true, submitUrl: '/analyze/crystal/dft_spin_density' },
    { kind: 'dft_optical',        label: 'GPAW - Optical Properties', custom: true, submitUrl: '/analyze/crystal/dft_optical' },
    { kind: 'dft_effective_mass', label: 'GPAW - Effective Masses',   custom: true, submitUrl: '/analyze/crystal/dft_effective_mass' },
    { kind: 'qe_relax',           label: 'QE - Relaxation',                 custom: true, submitUrl: '/analyze/crystal/qe_relax' },
    { kind: 'qe_elastic',         label: 'QE - Elastic (Bulk Mod.)',        custom: true, submitUrl: '/analyze/crystal/qe_elastic' },
    { kind: 'qe_bandstructure',   label: 'QE - Band Structure',             custom: true, submitUrl: '/analyze/crystal/qe_bandstructure' },
    { kind: 'qe_optical',         label: 'QE - Optical Properties',         custom: true, submitUrl: '/analyze/crystal/qe_optical' },
    { kind: 'qe_effective_mass',  label: 'QE - Effective Masses',           custom: true, submitUrl: '/analyze/crystal/qe_effective_mass' },
    { kind: 'qe_phonon',          label: 'QE - Phonon Dispersion',          custom: true, submitUrl: '/analyze/crystal/qe_phonon' },
    { kind: 'qe_thermal',         label: 'QE - Thermal Properties',         custom: true, submitUrl: '/analyze/crystal/qe_thermal' },
  ];
  const ANALYSIS_TYPES = [...ANALYSIS_QUICK, ...ANALYSIS_MEDIUM, ...ANALYSIS_LONG];
  const ANALYSIS_TIERS = [
    { label: 'Quick (~seconds)',  items: ANALYSIS_QUICK },
    { label: 'Medium (~minutes)', items: ANALYSIS_MEDIUM },
    { label: 'Long (async jobs)', items: ANALYSIS_LONG },
  ];

  const COMPARE_TYPES = [
    { kind: 'phase_stability', label: 'Phase Stability (Convex Hull)', endpoint: '/analyze/crystal/phase_stability' },
    { kind: 'mp_neighbors',    label: 'MP Nearest Neighbors',          endpoint: '/analyze/crystal/mp_neighbors' },
  ];

  const REPORT_TYPES = [
    { kind: 'economic_report', label: 'Economic Report',     custom: true },
    { kind: 'scientific_doc',  label: 'Scientific Document', custom: true },
    { kind: 'patent_packet',   label: 'Patent Packet',       custom: true },
  ];

  const ALL_TYPES = [...ANALYSIS_TYPES, ...COMPARE_TYPES, ...REPORT_TYPES];

  // Direct submission — uses cifCacheRef to avoid stale-closure reads
  const submitDftJob = async (idx, kind) => {
    const key = `${idx}::${kind}`;
    const at = ALL_TYPES.find(t => t.kind === kind);
    if (!at?.submitUrl) return;
    const isQeJob = kind.startsWith('qe_');
    const base = isQeJob ? (qeBaseUrl || 'http://localhost:8015') : (dftBaseUrl || 'http://localhost:8013');
    const cifUrl = cifs[idx];
    let cifText = cifCacheRef.current[idx];
    if (!cifText && cifUrl) {
      try {
        const cifResp = await authFetch(cifUrl);
        cifText = cifResp.ok ? await cifResp.text() : null;
        if (cifText) setCifCache(p => ({ ...p, [idx]: cifText }));
      } catch (_) {}
    }
    if (!cifText) {
      setAnalysisCache(p => ({ ...p, [key]: { status: 'error', error: 'No CIF available for this crystal' } }));
      setAnalysisLoading(p => ({ ...p, [key]: false }));
      return;
    }
    if (!cifText) {
      setAnalysisCache(p => ({ ...p, [key]: { status: 'error', error: 'Could not fetch CIF' } }));
      setAnalysisLoading(p => ({ ...p, [key]: false }));
      return;
    }
    // candidate_fingerprint is recorded server-side by both dft-service and
    // qe-service (parity reached 2026-08-20, see dftJobLookup.js) so a
    // resuming UI can find this job via Firestore instead of depending
    // solely on the localStorage registry below, which stays as the
    // local-dev fallback for when Firebase isn't configured.
    const submittedFrame = frames.find(fr => fr.index === idx);
    const submitFingerprint = submittedFrame ? _fpFrame(submittedFrame) : null;
    try {
      const r = await authedFetch(`${base}${at.submitUrl}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...dftAuthHdrs },
        body: JSON.stringify({
          cif: cifText,
          candidate_fingerprint: submitFingerprint,
        }),
      });
      if (!r.ok) {
        let body = null;
        try { body = await r.json(); } catch (_) {}
        throw new Error(capabilityDeniedMessage(body) || `HTTP ${r.status}`);
      }
      const d = await r.json();
      if (!d.job_id) throw new Error('Service returned no job_id');
      const inflight = { status: 'queued', job_id: d.job_id, queued_at: Date.now(), submitted_by: authUser?.uid || null };
      setAnalysisCache(p => ({ ...p, [key]: inflight }));
      // Persist in-flight state so polling can resume after page reload
      persistAnalysis(idx, kind, inflight);
      // Register in the shared dftJobRegistry (see src/utils/dftJobRegistry.js)
      // so LatticeWizard, if it's the surface that later reopens for this
      // candidate, finds this job and resumes it instead of showing the
      // objective picker and risking a second, duplicate, expensive run.
      // Written unconditionally alongside the server-side candidate_fingerprint
      // above -- this stays the dev fallback for when Firebase is unconfigured.
      if (submittedFrame) registerJob(`${submitFingerprint}::${kind}`, d.job_id);
    } catch (e) {
      setAnalysisCache(p => ({ ...p, [key]: { status: 'error', error: e.message } }));
    }
    setAnalysisLoading(p => ({ ...p, [key]: false }));
  };

  const AUTO_VALIDATE_THRESHOLD = 0.7; // auto-run analysis on crystals scoring ≥70%
  const autoValidatedRef = useRef(new Set()); // track which crystals already triggered auto-validation

  // Core fetch — populates cache, does NOT toggle visibility
  // NOTE: all reads of analysisCache / cifCache inside this async function use refs
  // to avoid stale closures (see BUG-AUDIT.md stale closure section).
  const fetchAnalysis = async (idx, kind) => {
    const key = `${idx}::${kind}`;
    const _cache = analysisCacheRef.current;
    // Allow re-entry for patent_packet while it's in an intermediate state (searching/clear/warning)
    // For scientific_doc: regenerate if new analysis data (symmetry/voronoi/bond) has appeared
    // since the doc was last built, so it always reflects the latest analyses.
    // M8: a failed result (ok === false) never blocks a retry — clicking the menu
    // item again re-runs instead of dead-ending on the cached error.
    if (_cache[key] && !_cache[key].status && _cache[key].ok !== false) {
      if (kind === 'scientific_doc') {
        const doc = _cache[key];
        const hasSym  = !!doc.integrated_analyses?.symmetry;
        const hasVor  = !!doc.integrated_analyses?.voronoi_summary;
        const hasBond = !!doc.integrated_analyses?.bond_stats;
        const hasDftS = !!doc.integrated_analyses?.dft_surrogate;
        const symNow  = !!_cache[`${idx}::symmetry`]?.ok;
        const vorNow  = !!_cache[`${idx}::voronoi`]?.ok;
        const bondNow = !!_cache[`${idx}::bond_distribution`]?.ok;
        const dftSNow = !!_cache[`${idx}::dft_surrogate`]?.ok;
        // Only regenerate if new data appeared that wasn't in the cached doc
        if (!(!hasSym && symNow) && !(!hasVor && vorNow) && !(!hasBond && bondNow) && !(!hasDftS && dftSNow)) return;
      } else {
        return;
      }
    }
    setAnalysisLoading((p) => ({ ...p, [key]: true }));

    // Scientific Document — synchronous, no external calls
    if (kind === 'scientific_doc') {
      const frame = frames.find(f => f.index === idx);
      const cc = analysisCacheRef.current;
      const symData  = cc[`${idx}::symmetry`];
      const vorData  = cc[`${idx}::voronoi`];
      const bondData = cc[`${idx}::bond_distribution`];
      const dftSData = cc[`${idx}::dft_surrogate`];
      const qualityClass = frame.score >= 0.8 ? 'Excellent' : frame.score >= 0.5 ? 'Good' : 'Fair';
      const doc = {
        ok: true,
        formula: frame.formula,
        title: `Crystal Structure and Physical Properties of ${frame.formula}`,
        generated_at: new Date().toISOString(),
        summary: {
          formula: frame.formula,
          rank: frame.rank,
          score: frame.score,
          score_pct: (frame.score * 100).toFixed(1),
          quality_class: qualityClass,
          atom_count: frame.n,
        },
        structural_properties: {
          density: frame.density,
          packing_fraction: frame.packingFraction,
          min_interatomic_distance: frame.minDist,
        },
        integrated_analyses: {
          symmetry: symData?.ok ? {
            space_group: symData.space_group_symbol,
            space_group_number: symData.space_group_number,
            crystal_system: symData.crystal_system,
            point_group: symData.point_group,
            hall_symbol: symData.hall_symbol,
            n_symmetry_ops: symData.n_symmetry_ops,
            is_laue: symData.is_laue,
            lattice: symData.lattice || null,
            wyckoff_sites: symData.wyckoff_sites || [],
          } : null,
          voronoi_summary: vorData?.ok && vorData.coordination_summary ? vorData.coordination_summary : null,
          bond_stats: bondData?.ok && bondData.stats ? bondData.stats : null,
          bond_pairs: bondData?.ok && bondData.bond_pairs ? bondData.bond_pairs : null,
          dft_surrogate: dftSData?.ok ? {
            stability_label: dftSData.stability_label,
            formation_energy_per_atom: dftSData.formation_energy_per_atom,
            energy_per_atom: dftSData.energy_per_atom,
            forces_max_eV_A: dftSData.forces_max_eV_A,
            stress_GPa: dftSData.stress_GPa,
            magnetic_character: dftSData.magnetic_character,
            magmom_per_atom: dftSData.magmom_per_atom,
          } : null,
        },
        comment: frame.comment || '',
      };
      setAnalysisCache((p) => ({ ...p, [key]: doc }));
      setAnalysisLoading((p) => ({ ...p, [key]: false }));
      persistAnalysis(idx, kind, doc);
      return;
    }

    // Economic Report — fetch live metrics first, then pass them to the LLM so both outputs are grounded in the same data
    if (kind === 'economic_report') {
      const frame = frames.find(f => f.index === idx);
      const symData = analysisCacheRef.current[`${idx}::symmetry`];
      const base = analyzeBaseUrl || 'http://localhost:8012';

      // Step 1: fetch backend metrics
      let economicMetrics = null;
      try {
        const mResp = await authedFetch(`${base}/economic/metrics`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ formula: frame.formula }),
        });
        if (mResp.ok) {
          const m = await mResp.json();
          if (m.ok) {
            economicMetrics = {
              cost_low: m.bom_cost_low,
              cost_high: m.bom_cost_high,
              cost_raw: m.bom_cost_raw,
              energy_label: m.energy_label,
              energy_range: m.energy_range,
              volatility_level: m.volatility_level,
              volatility_dominant: m.volatility_dominant,
              recyclability_level: m.recyclability_level,
              recyclability_score: m.recyclability_score,
              supply_concentration_index: m.supply_concentration_index,
              supply_concentration_level: m.supply_concentration_level,
              element_details: m.element_details || {},
              data_sources: m.data_sources || [],
            };
          }
        }
      } catch (_) {}

      // Step 2: build LLM prompt, including quantitative metrics so qualitative output aligns
      const systemInstruction = `You are a materials economics analyst. Given a newly discovered crystal material and its quantitative economic metrics, return ONLY valid JSON with no markdown, matching exactly:{"executive_summary":"string","elemental_economics":{"assessment":"string","concerns":["string"],"advantages":["string"]},"synthesis_viability":{"complexity":"low|medium|high","assessment":"string"},"market_potential":{"applications":["string"],"assessment":"string"},"risk_assessment":{"overall":"low|medium|high","factors":["string"]}}. Your qualitative assessments must be consistent with the provided quantitative data.`;
      const lines = [
        `Formula: ${frame.formula}`,
        `Density: ${frame.density != null ? frame.density.toFixed(3) + ' g/cm\u00b3' : 'unknown'}`,
        `Packing fraction: ${frame.packingFraction != null ? (frame.packingFraction * 100).toFixed(1) + '%' : 'unknown'}`,
        `Min interatomic distance: ${frame.minDist != null ? frame.minDist.toFixed(3) + ' \u00c5' : 'unknown'}`,
        `Structural quality score: ${(frame.score * 100).toFixed(1)}%`,
        `Rank: ${frame.rank} in set`,
        `Atom count: ${frame.n}`,
      ];
      if (symData?.ok) {
        if (symData.space_group_symbol) lines.push(`Space group: ${symData.space_group_symbol}`);
        if (symData.crystal_system) lines.push(`Crystal system: ${symData.crystal_system}`);
      }
      if (economicMetrics) {
        lines.push('--- Quantitative economic metrics (ground truth — your qualitative output must align with these) ---');
        lines.push(`Estimated BOM cost: ${economicMetrics.cost_low} – ${economicMetrics.cost_high} / kg`);
        lines.push(`Energy intensity: ${economicMetrics.energy_label} (${economicMetrics.energy_range})`);
        lines.push(`Cost volatility: ${economicMetrics.volatility_level}${economicMetrics.volatility_dominant ? ` (${economicMetrics.volatility_dominant} dominant)` : ''}`);
        lines.push(`Recyclability: ${economicMetrics.recyclability_level} (score ${economicMetrics.recyclability_score})`);
        lines.push(`Supply concentration index: ${economicMetrics.supply_concentration_index} — ${economicMetrics.supply_concentration_level} risk`);
      }

      // Step 3: call LLM
      let llmData = { ok: false };
      try {
        let raw = await askLLM({ systemInstruction, userText: lines.join('\n'), response_format: { type: 'json_object' } });
        raw = raw.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '').trim();
        const match = raw.match(/\{[\s\S]*\}/);
        if (match) raw = match[0];
        llmData = { ok: true, ...JSON.parse(raw) };
      } catch (e) {
        llmData = { ok: false, error: e.message };
      }

      const econResult = { formula: frame.formula, economic_metrics: economicMetrics, ...llmData };
      setAnalysisCache((p) => ({ ...p, [key]: econResult }));
      setAnalysisLoading((p) => ({ ...p, [key]: false }));
      // M8: never persist a failed report org-wide — a transient LLM hiccup was
      // being written to /analyses and every teammate loaded the error as "done".
      if (econResult.ok) persistAnalysis(idx, kind, econResult);
      return;
    }

    // Patent Packet — prior-art search (Phase 1) then parallel LLM calls (Phase 2)
    if (kind === 'patent_packet') {
      const base = analyzeBaseUrl || 'http://localhost:8012';
      const frame = frames.find(f => f.index === idx);

      // Override the initial loading=true — managed manually below
      setAnalysisLoading(p => ({ ...p, [key]: false }));

      // D7 (durability audit 2026-08-20): a partial packet persisted mid-
      // Phase-2 (status 'generating', see buildPartialPacket below) means
      // the prior-art search already ran and its LLM-prompt context is
      // embedded in the packet itself (prior_art_context): treat this
      // exactly like the user already clicked Generate/Proceed, so a
      // browser-close-and-reopen never re-runs a search that already
      // succeeded. planPatentPacketGeneration also says which of the five
      // sections still need an LLM call (only the ones still null/empty).
      const cachedForResume = analysisCacheRef.current[key];
      const { resuming, sections: sectionPlan } = planPatentPacketGeneration(cachedForResume);
      if (resuming) skipPatentSearchRef.current.add(key);

      // ── Phase 1: Patent search ──
      // If user has already clicked Generate/Proceed (or we're resuming a
      // partial packet, above), skip directly to Phase 2.
      if (!skipPatentSearchRef.current.has(key)) {
        // If already waiting for user action (clear/warning), don't re-run the search
        const existingStatus = analysisCacheRef.current[key]?.status;
        if (existingStatus === 'clear' || existingStatus === 'warning') return;

        setAnalysisCache(p => ({ ...p, [key]: { status: 'searching' } }));
        // D7: durable trace of an in-flight prior-art search. Previously
        // this marker lived only in React state, so a browser closed during
        // the search (which, per the durability audit, "can itself take
        // real wall-clock time") left zero record it was ever attempted.
        persistAnalysis(idx, kind, { status: 'searching' });
        let hits = [];
        let risk = 'low';
        let riskDetail = null;
        let mpContext = null;
        let sourcesQueried = [];
        let searchStatus = 'ok';
        let sourcesUnavailable = [];
        let searchFailed = false;
        let searchErrorMessage = null;
        try {
          const formula  = frame?.formula || '';
          const elements = frame?.elements || (frame?.composition ? Object.keys(frame.composition) : []); // #48 — derive from composition
          const symData  = analysisCacheRef.current[`${idx}::symmetry`];
          const spaceGroup = symData?.space_group_symbol || frame?.spaceGroup || null;
          const r = await authedFetch(`${base}/patent/search`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ domain: 'inorganic_crystal', formula, elements, space_group: spaceGroup }),
          });
          if (r.ok) {
            const d = await r.json();
            hits = d.ok ? (d.hits || []) : [];
            risk = d.risk_level || 'low';
            riskDetail = d.risk_detail || null;
            mpContext = d.mp_context || null;
            sourcesQueried = d.sources_queried || [];
            searchStatus = d.search_status || 'ok';
            sourcesUnavailable = d.sources_unavailable || [];
          } else {
            // I7b: a non-OK response is a FAILED search, not a clean empty one.
            searchFailed = true;
            searchErrorMessage = `Search service returned HTTP ${r.status}`;
          }
        } catch (e) {
          // I7b: a network error is a FAILED search — previously swallowed here and treated as
          // "clear" (no prior art), a materially wrong claim for a legal document.
          searchFailed = true;
          searchErrorMessage = e?.message || 'Network error';
        }
        // H1: a 200 whose every upstream source failed is a failed search too.
        const status = patentSearchStatus({ searchFailed, hits, searchStatus });
        const searchResultDoc = { status, hits, risk, risk_detail: riskDetail, mp_context: mpContext, sources_queried: sourcesQueried, sources_unavailable: sourcesUnavailable, search_error_message: searchFailed ? searchErrorMessage : (searchStatus === 'unavailable' ? 'All prior-art sources unavailable' : null) };
        setAnalysisCache(p => ({ ...p, [key]: searchResultDoc }));
        // D7: durable trace of the completed search too, not just that one
        // was attempted. A browser closed before Generate/Proceed is
        // clicked now resumes straight into the same clear/warning/
        // search_error state instead of silently re-running the search.
        persistAnalysis(idx, kind, searchResultDoc);
        return; // wait for user to click Retry / Generate / Proceed Anyway
      }

      // ── Phase 2: LLM generation, user clicked Generate / Proceed Anyway, or resuming a partial packet ──
      setAnalysisLoading(p => ({ ...p, [key]: true }));
      const cc2 = analysisCacheRef.current;
      // I7b: when the search failed and the user clicked "Proceed anyway", the disclosure must
      // say the search was UNAVAILABLE — never the false "no prior art found".
      let priorArtContext;
      if (resuming) {
        priorArtContext = cachedForResume.prior_art_context || '';
      } else {
        const priorArtHits = cc2[key]?.hits || [];
        const cachedSearch = cc2[key] || {};
        priorArtContext = patentPriorArtContext({
          status: cachedSearch.status,
          hits: priorArtHits,
          sourcesQueried: cachedSearch.sources_queried,
          sourcesUnavailable: cachedSearch.sources_unavailable,
        });
        if (cachedSearch.mp_context?.is_known_material) {
          priorArtContext += ` Note: composition matches Materials Project entry ${cachedSearch.mp_context.mp_material_id}${cachedSearch.mp_context.mp_is_stable ? ' (stable)' : ''}, material is catalogued in scientific literature.`;
        }
      }

      const symData  = cc2[`${idx}::symmetry`];
      const vorData  = cc2[`${idx}::voronoi`];
      const bondData = cc2[`${idx}::bond_distribution`];
      const econData = cc2[`${idx}::economic_report`];

      // Shared material context sent to every section call
      const contextLines = [
        `Formula: ${frame.formula}`,
        `Atom count: ${frame.n}`,
        `Density: ${frame.density != null ? frame.density.toFixed(3) + ' g/cm\u00b3' : 'unknown'}`,
        `Packing fraction: ${frame.packingFraction != null ? (frame.packingFraction * 100).toFixed(1) + '%' : 'unknown'}`,
        `Min interatomic distance: ${frame.minDist != null ? frame.minDist.toFixed(3) + ' \u00c5' : 'unknown'}`,
        `Computational quality score: ${(frame.score * 100).toFixed(1)}%`,
      ];
      if (symData?.ok) {
        contextLines.push(`Space group: ${symData.space_group_symbol} (No. ${symData.space_group_number})`);
        contextLines.push(`Crystal system: ${symData.crystal_system}`);
        contextLines.push(`Point group: ${symData.point_group}`);
        if (symData.lattice) {
          const l = symData.lattice;
          contextLines.push(`Lattice: a=${l.a} b=${l.b} c=${l.c} \u00c5, \u03b1=${l.alpha} \u03b2=${l.beta} \u03b3=${l.gamma}\u00b0, V=${l.volume} \u00c5\u00b3`);
        }
      }
      if (bondData?.ok && bondData.stats) {
        contextLines.push(`Bond count: ${bondData.stats.count}, range ${bondData.stats.min}\u2013${bondData.stats.max} \u00c5, mean ${bondData.stats.mean} \u00c5`);
      }
      if (vorData?.ok && vorData.coordination_summary?.length) {
        const avgCN = (vorData.coordination_summary.reduce((a, c) => a + c.coordination_number, 0) / vorData.coordination_summary.length).toFixed(1);
        contextLines.push(`Average coordination number: ${avgCN}`);
      }
      if (econData?.economic_metrics) {
        const em = econData.economic_metrics;
        contextLines.push(`Estimated BOM cost: ${em.cost_low}\u2013${em.cost_high}/kg`);
        contextLines.push(`Energy intensity: ${em.energy_label} (${em.energy_range})`);
        contextLines.push(`Supply concentration: ${em.supply_concentration_level} risk (index ${em.supply_concentration_index})`);
      }
      contextLines.push(`Prior art search result: ${priorArtContext}`);
      const context = contextLines.join('\n');
      const BASE_SYS = `You are a patent attorney specializing in inorganic materials science. You are drafting one section of a formal patent disclosure for a newly discovered crystalline compound. Use formal patent language. Return ONLY valid JSON with the exact keys specified — no markdown, no preamble.`;

      // Helper: call LLM and parse a section JSON safely
      const callSection = async (sysExtra, keys) => {
        try {
          let raw = await askLLM({ systemInstruction: `${BASE_SYS} ${sysExtra}`, userText: context, response_format: { type: 'json_object' }, purpose: 'patent_packet' });
          raw = raw.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '').trim();
          const match = raw.match(/\{[\s\S]*\}/);
          if (match) raw = match[0];
          const parsed = JSON.parse(raw);
          // verify expected keys are present
          for (const k of keys) if (!(k in parsed)) throw new Error(`Missing key: ${k}`);
          return { ok: true, ...parsed };
        } catch (e) {
          return { ok: false, error: e.message };
        }
      };

      const appendix = {
        structural: { formula: frame.formula, atom_count: frame.n, density: frame.density, packing_fraction: frame.packingFraction, min_interatomic_distance: frame.minDist, score: frame.score, rank: frame.rank },
        symmetry: symData?.ok ? { space_group: symData.space_group_symbol, space_group_number: symData.space_group_number, crystal_system: symData.crystal_system, point_group: symData.point_group, hall_symbol: symData.hall_symbol, n_symmetry_ops: symData.n_symmetry_ops, lattice: symData.lattice || null, wyckoff_sites: symData.wyckoff_sites || [] } : null,
        bonding: bondData?.ok ? { stats: bondData.stats, bond_pairs: bondData.bond_pairs } : null,
        coordination: vorData?.ok ? vorData.coordination_summary : null,
        economic: econData?.economic_metrics || null,
      };
      // A resume keeps the ORIGINAL generated_at (when drafting first began),
      // not the moment this particular resume happened: the disclosure's
      // own timestamp should reflect when generation was first attempted.
      const generatedAt = (resuming && cachedForResume.generated_at) ? cachedForResume.generated_at : new Date().toISOString();

      // D7: persisted (analysisCache + Firestore, via persistAnalysis) after
      // EACH section settles below, not just once at the very end. A
      // browser closed mid-Phase-2 previously lost the whole in-progress
      // packet, including sections that had already finished, with zero
      // durable trace. sectionsSoFar seeds from the resumed packet's already-
      // -complete sections (if any) so an in-flight persist while resuming
      // still carries them forward, not just whichever section just landed.
      let sectionsSoFar = resuming
        ? { title: cachedForResume.title, abstract: cachedForResume.abstract, field_of_invention: cachedForResume.field_of_invention, background: cachedForResume.background, summary_of_invention: cachedForResume.summary_of_invention, detailed_description: cachedForResume.detailed_description, claims: cachedForResume.claims }
        : { title: null, abstract: null, field_of_invention: null, background: null, summary_of_invention: null, detailed_description: null, claims: [] };
      const persistSectionProgress = (patch) => {
        sectionsSoFar = { ...sectionsSoFar, ...patch };
        const partial = buildPartialPacket({ formula: frame.formula, generatedAt, appendix, priorArtContext, sections: sectionsSoFar });
        setAnalysisCache(p => ({ ...p, [key]: partial }));
        persistAnalysis(idx, kind, partial);
      };
      // Resolves to a cached-and-reused result without any LLM call for a
      // section planPatentPacketGeneration already found complete on
      // resume; otherwise fires callSection() and persists the moment it
      // settles, ok or not (a failed section simply stays null/[] in the
      // persisted doc, exactly the "still missing" shape a later resume
      // would regenerate).
      const sectionPromise = (section, sysExtra, keys) => {
        const plan = sectionPlan.find(s => s.id === section.id);
        if (!plan.needed) return Promise.resolve(reuseSectionResult(cachedForResume, section));
        return callSection(sysExtra, keys).then((r) => {
          if (r.ok) persistSectionProgress(Object.fromEntries(section.fields.map(f => [f, r[f]])));
          return r;
        });
      };

      // Fire all 5 section calls simultaneously (skipping any already
      // complete on resume, see sectionPromise above)
      const [r1, r2, r3, r4, r5] = await Promise.all([
        sectionPromise(
          PATENT_PACKET_SECTIONS[0],
          `Return JSON with exactly these keys: ` +
          `{"title": "formal IUPAC-style compound title suitable for a patent filing", ` +
          `"abstract": "patent abstract (150 words max) that names the compound, describes its key structural features, and explicitly states its primary theoretical or practical purpose — e.g. what device, process, or application it enables", ` +
          `"field_of_invention": "3-5 sentences: (1) the technical field (materials class, crystal system, composition), (2) the specific functional properties this compound is expected to exhibit based on its structure and constituent elements, and (3) the concrete theoretical applications or end-use domains those properties make it a candidate for (e.g. solid-state electrolyte, catalytic substrate, dielectric layer, thermoelectric element — be specific, not generic)"}.`,
          ['title', 'abstract', 'field_of_invention']
        ),
        sectionPromise(
          PATENT_PACKET_SECTIONS[1],
          `Return JSON with exactly this key: {"background": "4-5 paragraphs: (1) overview of the relevant materials class and why it matters, (2) what specific functional need or technological gap exists in that space, (3) limitations of current state-of-the-art compounds that attempt to fill that gap, (4) how the structural or compositional features of this compound (based on the provided data) suggest it may overcome those limitations, (5) conclude with the object of the invention"}.`,
          ['background']
        ),
        sectionPromise(
          PATENT_PACKET_SECTIONS[2],
          `Return JSON with exactly this key: {"summary_of_invention": "3 paragraphs: (1) what the invention is — the compound, its structure, and its defining properties, (2) the primary theoretical or practical purpose the invention is intended to serve and what technical problem it solves or what capability it uniquely enables, (3) key advantages over prior art and a brief statement of the claim scope"}.`,
          ['summary_of_invention']
        ),
        sectionPromise(
          PATENT_PACKET_SECTIONS[3],
          `Return JSON with exactly this key: {"detailed_description": "5-7 paragraphs covering: (1) crystal structure and atomic arrangement in detail, (2) how the structural features give rise to the expected functional properties (electronic, ionic, magnetic, thermal, catalytic — whichever are relevant to this composition), (3) the primary intended application or use-case and why this compound is well-suited for it, (4) secondary or alternative applications supported by the compound's properties, (5) synthesis route and processing considerations, (6) performance metrics or figures of merit expected based on composition and structure, (7) optional — comparison to analogous compounds"}.`,
          ['detailed_description']
        ),
        sectionPromise(
          PATENT_PACKET_SECTIONS[4],
          `Return JSON with exactly this key: {"claims": ["claim string", ...]}. ` +
          `Generate 8-10 numbered formal patent claims. ` +
          `Claim 1 must be a broad independent composition-of-matter claim covering the crystalline compound and its essential structural features. ` +
          `Claims 2-4 must be independent method or use claims covering (a) a method of making the compound, (b) a method of using it in its primary application, and (c) a device or system incorporating it. ` +
          `Claims 5-10 are dependent claims that narrow earlier claims by specifying crystallographic parameters, dopant ranges, synthesis conditions, or specific device configurations. ` +
          `Each claim is a single grammatically complete sentence beginning with "A crystalline compound...", "A method of...", "A device comprising...", or "The compound/method/device of claim N...".`,
          ['claims']
        ),
      ]);

      // Final assembly. Partial success is acceptable; mark ok=true if at
      // least one section succeeded. Identical shape to before D7 (no
      // `status` field, no `prior_art_context`): this is what routes
      // AnalysisPanel to PatentPacketPanel instead of
      // PatentSearchStatusPanel, see assemblePatentPacket's docstring.
      const patentResult = assemblePatentPacket({ formula: frame.formula, generatedAt, appendix, r1, r2, r3, r4, r5 });
      setAnalysisCache(p => ({ ...p, [key]: patentResult }));
      setAnalysisLoading(p => ({ ...p, [key]: false }));
      // M8: persist only when at least one section generated (ok=anyOk) — an
      // all-sections-failed packet must not be cached org-wide as complete.
      if (patentResult.ok) persistAnalysis(idx, kind, patentResult);
      return;
    }

    // Async DFT job — initial call only: set null so panel renders with warning + Submit button.
    // Actual submission is handled by submitDftJob() to avoid stale-closure issues.
    const _asyncAt = ALL_TYPES.find(t => t.kind === kind);
    if (_asyncAt?.submitUrl) {
      if (!analysisCacheRef.current[key]) {
        setAnalysisCache(p => ({ ...p, [key]: null }));
      }
      setAnalysisLoading(p => ({ ...p, [key]: false }));
      return;
    }

    // Phase stability — needs formula + formation energy, not CIF
    if (kind === 'phase_stability') {
      const frame = frames.find(f => f.index === idx);
      if (!frame?.formula) { setAnalysisLoading(p => ({ ...p, [key]: false })); return; }
      const base = analyzeBaseUrl || 'http://localhost:8012';
      // Only a real formation energy may go on the MP hull: the CHGNet
      // surrogate's, never a DFT total energy (#57, see phaseStabilityInput.js).
      const { formation_energy_per_atom: fePerAtom, energy_source: energySource } =
        phaseStabilityEnergy(analysisCacheRef.current, idx);
      try {
        const resp = await authedFetch(`${base}/analyze/crystal/phase_stability`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ formula: frame.formula, formation_energy_per_atom: fePerAtom, energy_source: energySource }),
        });
        const data = resp.ok ? await resp.json() : { ok: false, error: `HTTP ${resp.status}` };
        setAnalysisCache(p => ({ ...p, [key]: data }));
        if (data?.ok) persistAnalysis(idx, kind, data);
      } catch (e) {
        setAnalysisCache(p => ({ ...p, [key]: { ok: false, error: e.message } }));
      } finally {
        setAnalysisLoading(p => ({ ...p, [key]: false }));
      }
      return;
    }

    // MP Nearest Neighbors — needs formula + optional CIF
    if (kind === 'mp_neighbors') {
      const frame = frames.find(f => f.index === idx);
      if (!frame?.formula) { setAnalysisLoading(p => ({ ...p, [key]: false })); return; }
      const base = analyzeBaseUrl || 'http://localhost:8012';
      let cifText = cifCacheRef.current[idx];
      if (!cifText && cifs[idx]) {
        try { const r = await authFetch(cifs[idx]); if (r.ok) { cifText = await r.text(); setCifCache(p => ({ ...p, [idx]: cifText })); } } catch (_) {}
      }
      try {
        const resp = await authedFetch(`${base}/analyze/crystal/mp_neighbors`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ formula: frame.formula, cif: cifText || null }),
        });
        const data = resp.ok ? await resp.json() : { ok: false, error: `HTTP ${resp.status}` };
        setAnalysisCache(p => ({ ...p, [key]: data }));
        if (data?.ok) persistAnalysis(idx, kind, data);
      } catch (e) {
        setAnalysisCache(p => ({ ...p, [key]: { ok: false, error: e.message } }));
      } finally {
        setAnalysisLoading(p => ({ ...p, [key]: false }));
      }
      return;
    }

    // Standard analyze-service path (symmetry / voronoi / bond_distribution / dft_surrogate)
    const cifUrl = cifs[idx];
    if (!cifUrl && !cifCacheRef.current[idx]) { setAnalysisLoading((p) => ({ ...p, [key]: false })); return; }
    try {
      let cifText = cifCacheRef.current[idx];
      if (!cifText && cifUrl) {
        const cifResp = await authFetch(cifUrl);
        cifText = cifResp.ok ? await cifResp.text() : null;
        if (cifText) setCifCache((p) => ({ ...p, [idx]: cifText }));
      }
      if (!cifText) { setAnalysisCache((p) => ({ ...p, [key]: { ok: false, error: 'Could not fetch CIF' } })); return; }
      const base = analyzeBaseUrl || 'http://localhost:8012';
      const at = ANALYSIS_TYPES.find(a => a.kind === kind);
      const resp = await authedFetch(`${base}${at.endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cif: cifText }),
      });
      const data = resp.ok ? await resp.json() : { ok: false, error: `HTTP ${resp.status}` };
      setAnalysisCache((p) => ({ ...p, [key]: data }));
      if (data?.ok) persistAnalysis(idx, kind, data);
    } catch (e) {
      setAnalysisCache((p) => ({ ...p, [key]: { ok: false, error: e.message } }));
    } finally {
      setAnalysisLoading((p) => ({ ...p, [key]: false }));
    }
  };

  // Toggle panel visibility + trigger fetch if needed
  const toggleAnalysis = async (idx, kind) => {
    const key = `${idx}::${kind}`;
    setActiveAnalyses(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    await fetchAnalysis(idx, kind);
  };

  // Force re-run: clear cached result and re-fetch from backend
  const recalculateAnalysis = async (idx, kind) => {
    const key = `${idx}::${kind}`;
    setAnalysisCache(p => { const n = { ...p }; delete n[key]; return n; });
    analysisCacheRef.current = { ...analysisCacheRef.current }; delete analysisCacheRef.current[key];
    await fetchAnalysis(idx, kind);
  };

  // Auto-validate high-scoring crystals — called from DB load callback with the
  // loaded analyses map so it can reliably skip anything already persisted.
  // No useEffect dependency on analysisCache — knownCache is passed directly.
  const runAutoValidate = (knownCache) => {
    const eligible = frames.filter(f => f.score >= AUTO_VALIDATE_THRESHOLD && cifs[f.index]);
    if (eligible.length === 0) return;
    const toRun = eligible.filter(f => !autoValidatedRef.current.has(f.index));
    if (toRun.length === 0) return;
    toRun.forEach(f => autoValidatedRef.current.add(f.index));

    const CONCURRENCY = 5;
    const runParallel = async (tasks) => {
      const queue = [...tasks];
      const workers = Array.from({ length: Math.min(CONCURRENCY, queue.length) }, async () => {
        while (queue.length > 0) {
          const task = queue.shift();
          if (task) await task();
        }
      });
      await Promise.allSettled(workers);
    };

    // Defer so results page renders immediately
    setTimeout(async () => {
      // 1) Batch DFT surrogate — single HTTP call for all eligible CIFs
      const base = analyzeBaseUrl || 'http://localhost:8012';
      try {
        const cifMap = {};
        for (const f of toRun) {
          const key = `${f.index}::dft_surrogate`;
          if (!knownCache[key]) {
            let cText = cifCacheRef.current[f.index];
            if (!cText && cifs[f.index]) {
              const r = await authFetch(cifs[f.index]);
              cText = r.ok ? await r.text() : null;
              if (cText) setCifCache(p => ({ ...p, [f.index]: cText }));
            }
            if (cText) cifMap[String(f.index)] = cText;
          }
        }
        if (Object.keys(cifMap).length > 0) {
          const loadingUpdate = {};
          for (const idx of Object.keys(cifMap)) loadingUpdate[`${idx}::dft_surrogate`] = true;
          setAnalysisLoading(p => ({ ...p, ...loadingUpdate }));

          const resp = await authedFetch(`${base}/analyze/crystal/dft_surrogate_batch`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ cifs: cifMap }),
          });
          if (resp.ok) {
            const batch = await resp.json();
            if (batch.ok && batch.results) {
              const cacheUpdate = {};
              const loadDone = {};
              for (const [idx, data] of Object.entries(batch.results)) {
                cacheUpdate[`${idx}::dft_surrogate`] = data;
                loadDone[`${idx}::dft_surrogate`] = false;
                if (data?.ok) persistAnalysis(Number(idx), 'dft_surrogate', data);
                // Update knownCache so step 2 sees batch results
                knownCache[`${idx}::dft_surrogate`] = data;
              }
              setAnalysisCache(p => ({ ...p, ...cacheUpdate }));
              setAnalysisLoading(p => ({ ...p, ...loadDone }));
            }
          }
        }
      } catch (e) {
        console.warn('[auto-validate] batch dft_surrogate failed, falling back to individual:', e.message);
      }

      // 2) Run remaining analysis types (symmetry, voronoi, bond_distribution) in parallel
      const nonDft = ANALYSIS_TYPES.filter(a => !a.custom && a.kind !== 'dft_surrogate');
      const tasks = [];
      for (const f of toRun) {
        for (const at of nonDft) {
          const key = `${f.index}::${at.kind}`;
          if (!knownCache[key]) {
            tasks.push(() => fetchAnalysis(f.index, at.kind));
          }
        }
        const dftKey = `${f.index}::dft_surrogate`;
        if (!knownCache[dftKey]) {
          tasks.push(() => fetchAnalysis(f.index, 'dft_surrogate'));
        }
      }
      await runParallel(tasks);
    }, 100);
  };

  const toggleCif = async (idx) => {
    if (expandedCif === idx) { setExpandedCif(null); return; }
    setExpandedCif(idx);
    if (cifCacheRef.current[idx]) return;
    const url = cifs[idx];
    if (!url) return;
    setCifLoading((p) => ({ ...p, [idx]: true }));
    try {
      const resp = await authFetch(url);
      const text = resp.ok ? await resp.text() : `Error ${resp.status}`;
      setCifCache((p) => ({ ...p, [idx]: text }));
    } catch (e) {
      setCifCache((p) => ({ ...p, [idx]: `Fetch failed: ${e.message}` }));
    } finally {
      setCifLoading((p) => ({ ...p, [idx]: false }));
    }
  };

  const toggleTraj = async (idx) => {
    if (expandedTraj === idx) { setExpandedTraj(null); return; }
    setExpandedTraj(idx);
    if (trajCache[idx]) return;
    const url = trajs[idx];
    if (!url) return;
    setTrajLoading((p) => ({ ...p, [idx]: true }));
    try {
      const resp = await authFetch(url);
      const text = resp.ok ? await resp.text() : `Error ${resp.status}`;
      setTrajCache((p) => ({ ...p, [idx]: text }));
    } catch (e) {
      setTrajCache((p) => ({ ...p, [idx]: `Fetch failed: ${e.message}` }));
    } finally {
      setTrajLoading((p) => ({ ...p, [idx]: false }));
    }
  };

  return (
    <>
      <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}>
        {frames.length} crystal{frames.length !== 1 ? 's' : ''} generated
        {(() => {
          const exact = frames.filter(f => f.mp_match && (f.mp_match.match_type === 'exact_sg' || f.mp_match.match_type === 'formula_only')).length;
          const similar = frames.filter(f => f.mp_match && f.mp_match.match_type === 'neighbor_only').length;
          const novel = frames.filter(f => f.mp_match && f.mp_match.novelty_score >= 0.7).length;
          if (!exact && !similar && !novel) return null;
          return (
            <span style={{ marginLeft: 8, fontSize: 11 }}>
              {exact > 0 && <span style={{ color: '#4ade80' }}> · {exact} known in MP</span>}
              {similar > 0 && <span style={{ color: '#fbbf24' }}> · {similar} with similar neighbors</span>}
              {novel > 0 && <span style={{ color: '#a78bfa' }}> · {novel} potentially novel</span>}
            </span>
          );
        })()}
      </div>
      {/* Spec 0.7: a job whose file could not be fetched is reported; the rest are shown. */}
      {Array.isArray(meta?.failedJobs) && meta.failedJobs.length > 0 && (
        <div style={{ color: '#f59e0b', fontSize: 12, marginBottom: 8 }}>
          {`${meta.failedJobs.length} of ${(meta.extxyzUrls || []).length} result files could not be loaded. Showing the crystals from the other ${(meta.extxyzUrls || []).length - meta.failedJobs.length}.`}
        </div>
      )}
      <div>
        <table className="table table-dark table-striped table-hover align-middle mb-0 text-light" style={{ fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ width: sourceLabels ? 160 : 45 }}>{sourceLabels ? 'Source' : 'Rank'}</th>
              <th>Composition</th>
              <th style={{ width: 55 }}>Atoms</th>
              <th style={{ width: 60 }}>Score</th>
              <th style={{ width: 80 }}>Density</th>
              <th style={{ width: 70 }}>d_min</th>
              <th>Files</th>
            </tr>
          </thead>
          <tbody>
            {frames.map((f) => (
              <React.Fragment key={f.index}>
                <tr>
                  <td>{sourceLabels
                    ? <div style={{ lineHeight: 1.4 }}>
                        {sourceLabels[f.index]?.source
                          ? <div style={{ fontSize: 11, color: '#94a3b8' }}>{sourceLabels[f.index].source}</div>
                          : <div style={{ fontSize: 11, color: '#475569' }}>—</div>}
                        {sourceLabels[f.index]?.runTs &&
                          <div style={{ fontSize: 10, color: '#475569' }}>{new Date(sourceLabels[f.index].runTs).toLocaleString()}</div>}
                      </div>
                    : <span style={{ fontWeight: 700, color: f.rank <= 3 ? '#38bdf8' : undefined }}>{f.rank}</span>
                  }</td>
                  <td style={{ fontWeight: 600 }}>
                    {f.formula || '—'}
                    {f.mp_match && (() => {
                      const mt = f.mp_match.match_type;
                      const ns = f.mp_match.novelty_score;
                      const nn = f.mp_match.nearest_neighbor;
                      let label, bg, fg;
                      if (mt === 'exact_sg') {
                        label = 'MP EXACT'; bg = 'rgba(74,222,128,0.15)'; fg = '#4ade80';
                      } else if (mt === 'formula_only') {
                        label = 'MP KNOWN'; bg = 'rgba(56,189,248,0.15)'; fg = '#38bdf8';
                      } else if (mt === 'neighbor_only' && ns != null && ns < 0.7) {
                        label = 'MP ~SIM'; bg = 'rgba(251,191,36,0.15)'; fg = '#fbbf24';
                      } else {
                        label = 'MP NOVEL'; bg = 'rgba(167,139,250,0.15)'; fg = '#a78bfa';
                      }
                      const titleParts = [];
                      if (mt === 'exact_sg' || mt === 'formula_only') {
                        titleParts.push(`Known material — ${f.mp_match.material_id}`);
                        if (f.mp_match.is_stable) titleParts.push('Thermodynamically stable');
                      }
                      if (nn) titleParts.push(`Nearest neighbor: ${nn.formula} (${nn.material_id}, dist ${nn.distance.toFixed(3)})`);
                      if (ns != null) titleParts.push(`Novelty: ${(ns * 100).toFixed(0)}%`);
                      if (f.mp_match.neighbor_count) titleParts.push(`${f.mp_match.neighbor_count} similar materials in MP`);
                      return (
                        <span title={titleParts.join(' · ')}
                          style={{ marginLeft: 6, fontSize: 9, padding: '1px 5px', borderRadius: 3, cursor: 'help', fontWeight: 600,
                            background: bg, color: fg, border: `1px solid ${fg}` }}>
                          {label}
                        </span>
                      );
                    })()}
                    {f.mp_match && (f.mp_match.match_type === 'exact_sg' || f.mp_match.match_type === 'formula_only') && (
                      <div style={{ fontSize: 10, color: '#fbbf24', marginTop: 2, fontWeight: 400 }}>
                        Already characterized in MP ({f.mp_match.material_id}) — analysis may be redundant
                      </div>
                    )}
                    {f.mp_match && f.mp_match.match_type === 'neighbor_only' && f.mp_match.nearest_neighbor && (
                      <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2, fontWeight: 400 }}>
                        Nearest analog: {f.mp_match.nearest_neighbor.formula} (dist {f.mp_match.nearest_neighbor.distance.toFixed(3)})
                      </div>
                    )}
                  </td>
                  <td>{f.n}</td>
                  <td>
                    <span
                      title={'Scoring metric: Absolute structural quality composite\nScore weights: Min. interatomic distance 40%, packing fraction 35%, density 25%\nRanking method: Gaussian proximity to ideal structural values → weighted sum'}
                      style={{ color: f.score >= 0.8 ? '#4ade80' : f.score >= 0.5 ? '#facc15' : '#f87171', cursor: 'help', borderBottom: '1px dotted currentColor' }}
                    >{(f.score * 100).toFixed(0)}%</span>
                    {(() => {
                      const baseTypes = ANALYSIS_TYPES.filter(a => !a.custom);
                      const cachedCount = baseTypes.filter(at => analysisCache[`${f.index}::${at.kind}`]).length;
                      const loadingAny = baseTypes.some(at => analysisLoading[`${f.index}::${at.kind}`]);
                      if (loadingAny) return <span style={{ fontSize: 10, marginLeft: 4, color: '#facc15' }} title="Auto-validating…">⏳</span>;
                      if (cachedCount === baseTypes.length) return <span style={{ fontSize: 10, marginLeft: 4, color: '#4ade80' }} title="Validated (all analyses cached)">✔</span>;
                      return null;
                    })()}
                  </td>
                  <td>{f.density != null ? `${f.density} g/cm³` : '—'}</td>
                  <td>{f.minDist != null ? `${f.minDist} Å` : '—'}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                    {/* ── left: file views + analysis + notes ───── */}
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                      <button
                        className={`btn btn-sm ${expanded === f.index ? 'btn-info' : 'btn-outline-info'}`}
                        onClick={() => setExpanded(expanded === f.index ? null : f.index)}
                      >extxyz</button>
                      {(cifs[f.index] || cifCache[f.index]) ? (
                        <button
                          className={`btn btn-sm ${expandedCif === f.index ? 'btn-info' : 'btn-outline-info'}`}
                          onClick={() => toggleCif(f.index)}
                        >{cifLoading[f.index] ? '.cif…' : '.cif'}</button>
                      ) : (
                        <button className="btn btn-outline-secondary btn-sm" disabled>.cif</button>
                      )}
                      {trajs[f.index] ? (
                        <button
                          className={`btn btn-sm ${expandedTraj === f.index ? 'btn-info' : 'btn-outline-info'}`}
                          onClick={() => toggleTraj(f.index)}
                        >{trajLoading[f.index] ? 'traj…' : 'trajectory'}</button>
                      ) : (
                        <button className="btn btn-outline-secondary btn-sm" disabled>trajectory</button>
                      )}
                      <button
                        className="btn btn-sm btn-outline-warning"
                        disabled={cifLoading[f.index]}
                        onClick={async () => {
                          setViewer3dFrame(f);
                          // Load CIF text for proper periodic rendering
                          let cText = cifCacheRef.current[f.index];
                          if (!cText && cifs[f.index]) {
                            setCifLoading(p => ({ ...p, [f.index]: true }));
                            try {
                              const r = await authFetch(cifs[f.index]);
                              if (r.ok) { cText = await r.text(); setCifCache(p => ({ ...p, [f.index]: cText })); }
                            } catch (_) {}
                            setCifLoading(p => ({ ...p, [f.index]: false }));
                          }
                          setViewer3dCif(cText || null);
                        }}
                      >{cifLoading[f.index] ? '3D…' : '3D'}</button>
                      {(cifs[f.index] || cifCache[f.index]) ? (
                        <div className="position-relative d-inline-block" {...(analysisMenuOpen === f.index ? { 'data-dropdown-open': '' } : {})}>
                          <button
                            className={`btn btn-sm ${ANALYSIS_TYPES.some(at => activeAnalyses.has(`${f.index}::${at.kind}`)) ? 'btn-success' : 'btn-outline-success'}`}
                            onClick={() => { setAnalysisMenuOpen(analysisMenuOpen === f.index ? null : f.index); setReportMenuOpen(null); setCompareMenuOpen(null); }}
                          >{ANALYSIS_TYPES.some(at => analysisLoading[`${f.index}::${at.kind}`]) ? 'analyzing…' : 'Analyze ▾'}</button>
                          {analysisMenuOpen === f.index && (
                            <AnalyzeDropdown fIndex={f.index} tiers={ANALYSIS_TIERS} analysisCache={analysisCache} activeAnalyses={activeAnalyses} analysisLoading={analysisLoading} onToggle={toggleAnalysis} />
                          )}
                        </div>
                      ) : (
                        <button className="btn btn-outline-secondary btn-sm" disabled>Analyze</button>
                      )}
                      <div className="position-relative d-inline-block" {...(compareMenuOpen === f.index ? { 'data-dropdown-open': '' } : {})}>
                        <button
                          className="btn btn-sm"
                          style={{ color: COMPARE_TYPES.some(ct => activeAnalyses.has(`${f.index}::${ct.kind}`)) ? '#fff' : '#f59e0b', background: COMPARE_TYPES.some(ct => activeAnalyses.has(`${f.index}::${ct.kind}`)) ? '#d97706' : 'transparent', border: '1px solid #f59e0b' }}
                          onClick={() => { setCompareMenuOpen(compareMenuOpen === f.index ? null : f.index); setAnalysisMenuOpen(null); setReportMenuOpen(null); }}
                        >{COMPARE_TYPES.some(ct => analysisLoading[`${f.index}::${ct.kind}`]) ? 'comparing…' : 'Compare ▾'}</button>
                        {compareMenuOpen === f.index && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 20, background: '#1e293b', border: '1px solid #334155', borderRadius: 6, minWidth: 220, boxShadow: '0 4px 12px rgba(0,0,0,0.4)', marginTop: 2, padding: '4px 0' }}>
                            {COMPARE_TYPES.map(ct => {
                              const key = `${f.index}::${ct.kind}`;
                              const cached = !!analysisCache[key] && analysisCache[key].ok !== false; // M8: a failed result is not "done"
                              const active = activeAnalyses.has(key);
                              const ldg = !!analysisLoading[key];
                              return (
                                <button key={ct.kind}
                                  className="btn btn-sm w-100 text-start d-flex align-items-center gap-2"
                                  style={{ color: (active || cached) ? '#f59e0b' : '#e2e8f0', padding: '6px 12px', border: 'none', background: active ? 'rgba(245,158,11,0.12)' : 'transparent', fontSize: 12, opacity: ldg ? 0.6 : 1 }}
                                  disabled={ldg}
                                  onClick={() => toggleAnalysis(f.index, ct.kind)}
                                >
                                  <span style={{ width: 16, textAlign: 'center', fontSize: 11 }}>{ldg ? '⏳' : cached ? '☑' : '☐'}</span>
                                  <span>{ct.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <div className="position-relative d-inline-block" {...(reportMenuOpen === f.index ? { 'data-dropdown-open': '' } : {})}>
                        <button
                          className="btn btn-sm"
                          style={{ color: REPORT_TYPES.some(rt => activeAnalyses.has(`${f.index}::${rt.kind}`)) ? '#fff' : '#a78bfa', background: REPORT_TYPES.some(rt => activeAnalyses.has(`${f.index}::${rt.kind}`)) ? '#7c3aed' : 'transparent', border: '1px solid #7c3aed' }}
                          onClick={() => { setReportMenuOpen(reportMenuOpen === f.index ? null : f.index); setAnalysisMenuOpen(null); setCompareMenuOpen(null); }}
                        >{REPORT_TYPES.some(rt => analysisLoading[`${f.index}::${rt.kind}`]) ? 'generating…' : 'Reports ▾'}</button>
                        {reportMenuOpen === f.index && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 20, background: '#1e293b', border: '1px solid #334155', borderRadius: 6, minWidth: 200, boxShadow: '0 4px 12px rgba(0,0,0,0.4)', marginTop: 2, padding: '4px 0' }}>
                            {REPORT_TYPES.map(rt => {
                              const key = `${f.index}::${rt.kind}`;
                              const cached = !!analysisCache[key] && analysisCache[key].ok !== false; // M8: a failed result is not "done"
                              const active = activeAnalyses.has(key);
                              const ldg = !!analysisLoading[key];
                              const blocked = rt.kind === 'patent_packet' && !patentCap.allowed;
                              return (
                                <button key={rt.kind}
                                  className="btn btn-sm w-100 text-start d-flex align-items-center gap-2"
                                  style={{ color: blocked ? '#475569' : (active || cached) ? '#a78bfa' : '#e2e8f0', padding: '6px 12px', border: 'none', background: active ? 'rgba(124,58,237,0.15)' : 'transparent', fontSize: 12, opacity: ldg ? 0.6 : 1 }}
                                  disabled={ldg || blocked}
                                  title={blocked ? patentCap.message : undefined}
                                  onClick={() => { if (!blocked) toggleAnalysis(f.index, rt.kind); }}
                                >
                                  <span style={{ width: 16, textAlign: 'center', fontSize: 11 }}>{ldg ? '⏳' : cached ? '☑' : '☐'}</span>
                                  <span>{rt.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      {/* Env-gated off in production until lattice-service is provisioned there:
                          REACT_APP_LATTICE_BASE_URL is unset in .env.production (Cloud Run service
                          decision belongs to the repo owner), so the button would otherwise point at
                          localhost:8016 for every deployed user. Gate on the env var directly, not on
                          AppProvider's `defaultLatticeUrl`, which falls back to localhost:8016 and is
                          always truthy. Local dev: set REACT_APP_LATTICE_BASE_URL in .env (see
                          .env.example) to see the button. */}
                      {!!process.env.REACT_APP_LATTICE_BASE_URL && (!latticeCap.allowed ? (
                        <button className="btn btn-outline-secondary btn-sm" disabled title={latticeCap.message}>Lattice</button>
                      ) : (cifs[f.index] || cifCache[f.index]) ? (
                        <button
                          className="btn btn-sm"
                          disabled={cifLoading[f.index]}
                          style={{ color: latticeCandidateIdx === f.index ? '#fff' : '#22d3ee', background: latticeCandidateIdx === f.index ? '#0e7490' : 'transparent', border: '1px solid #22d3ee' }}
                          onClick={async () => {
                            let cText = cifCacheRef.current[f.index];
                            if (!cText && cifs[f.index]) {
                              setCifLoading(p => ({ ...p, [f.index]: true }));
                              try {
                                const r = await authFetch(cifs[f.index]);
                                if (r.ok) { cText = await r.text(); setCifCache(p => ({ ...p, [f.index]: cText })); }
                              } catch (_) {}
                              setCifLoading(p => ({ ...p, [f.index]: false }));
                            }
                            setLatticeCif(cText || null);
                            setLatticeCandidateIdx(f.index);
                          }}
                        >{cifLoading[f.index] ? 'Lattice…' : 'Lattice'}</button>
                      ) : (
                        <button className="btn btn-outline-secondary btn-sm" disabled>Lattice</button>
                      ))}
                      <button
                        className={`btn btn-sm ${expandedNotes === f.index ? 'btn-info' : 'btn-outline-info'}`}
                        onClick={() => setExpandedNotes(expandedNotes === f.index ? null : f.index)}
                      >
                        Notes{getCandidateNotes?.(_fpFrame(f))?.length ? ' ●' : ''}
                      </button>
                      {stageMap && stageMap[f.index] !== undefined && (
                        <>
                          <StageDropdown fingerprint={_fpFrame(f)} currentStage={stageMap[f.index]} />
                          <CopyLinkButton fingerprint={_fpFrame(f)} />
                        </>
                      )}
                    </div>
                    {/* ── right: save + download (pushed to far right) ── */}
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginLeft: 'auto' }}>
                      <button
                        title={isSaved?.(f) ? 'Remove from saved' : 'Save for further review'}
                        disabled={!!savingFrames[f.index]}
                        onClick={async () => {
                          if (savingFrames[f.index]) return;
                          setSavingFrames(p => ({ ...p, [f.index]: true }));
                          try {
                            const ca = Object.fromEntries([...ALL_TYPES].map(at => [at.kind, analysisCache[`${f.index}::${at.kind}`] || null]).filter(([, v]) => v));
                            if (isSaved?.(f)) {
                              await unsaveCandidate?.(savedCandidates?.find(c => c.fingerprint === _fpFrame(f))?.id);
                            } else {
                              // Inject cached CIF text into the frame so it persists to Firestore.
                              // Without this, CIF/Analyze are lost on reload for URL-fetch candidates.
                              let cifText = cifCacheRef.current[f.index];
                              if (!cifText && cifs[f.index]) {
                                try { const r = await authFetch(cifs[f.index]); if (r.ok) { cifText = await r.text(); setCifCache(p => ({ ...p, [f.index]: cifText })); } } catch (_) {}
                              }
                              const frameToSave = cifText && !f.cif ? { ...f, cif: cifText } : f;
                              await saveCandidate?.(frameToSave, cifs[f.index] || null, trajs[f.index] || null, (meta?.serviceLabel || meta?.modelLabel || meta?.runTs) ? { service: meta.serviceLabel, model: meta.modelLabel, runTs: meta.runTs } : null, ca);
                            }
                          } finally {
                            setSavingFrames(p => ({ ...p, [f.index]: false }));
                          }
                        }}
                        style={{ background: 'transparent', border: '1px solid', borderColor: isSaved?.(f) ? '#f43f5e' : '#334155', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 14, color: isSaved?.(f) ? '#f43f5e' : '#475569', lineHeight: 1, opacity: savingFrames[f.index] ? 0.5 : 1 }}
                      >
                        {isSaved?.(f) ? '★' : '☆'}
                      </button>
                      <CrystalDownloadBtn
                        frame={f}
                        cifUrl={cifs[f.index] || null}
                        cifText={cifCache[f.index] || null}
                        trajUrl={trajs[f.index] || null}
                        cachedAnalyses={Object.fromEntries(
                          [...ALL_TYPES].map(at => [at.kind, analysisCache[`${f.index}::${at.kind}`] || null]).filter(([, v]) => v)
                        )}
                        fetchFn={authFetch}
                      />
                    </div>{/* end right group */}
                    </div>{/* end outer flex */}
                  </td>
                </tr>
                {expanded === f.index && (
                  <tr>
                    <td colSpan={7} style={{ padding: 0 }}>
                      <pre className="font-monospace" style={{ padding: '12px 16px', fontSize: 12, lineHeight: 1.5, whiteSpace: 'pre', overflowX: 'auto', color: '#cbd5e1', margin: 0, background: '#0f1923' }}>{f.comment}{'\n'}{(f.atomLines || []).join('\n')}</pre>
                    </td>
                  </tr>
                )}
                {expandedCif === f.index && (
                  <tr>
                    <td colSpan={7} style={{ padding: 0 }}>
                      <pre className="font-monospace" style={{ padding: '12px 16px', fontSize: 12, lineHeight: 1.5, whiteSpace: 'pre', overflowX: 'auto', color: '#cbd5e1', margin: 0, background: '#0f1923' }}>
                        {cifLoading[f.index] ? 'Loading CIF…' : (cifCache[f.index] || 'No content available.')}
                      </pre>
                    </td>
                  </tr>
                )}
                {expandedTraj === f.index && (
                  <tr>
                    <td colSpan={7} style={{ padding: 0 }}>
                      <pre className="font-monospace" style={{ padding: '12px 16px', fontSize: 12, lineHeight: 1.5, whiteSpace: 'pre', overflowX: 'auto', color: '#cbd5e1', margin: 0, background: '#0f1923', maxHeight: 400, overflowY: 'auto' }}>
                        {trajLoading[f.index] ? 'Loading trajectory…' : (trajCache[f.index] || 'No trajectory available.')}
                      </pre>
                    </td>
                  </tr>
                )}
                {expandedNotes === f.index && (
                  <tr>
                    <td colSpan={7} style={{ padding: 0 }}>
                      <CandidateNotes fingerprint={_fpFrame(f)} />
                    </td>
                  </tr>
                )}
                {ALL_TYPES.filter(at => activeAnalyses.has(`${f.index}::${at.kind}`)).map(at => {
                  const key = `${f.index}::${at.kind}`;
                  const minimized = minimizedPanels.has(key);
                  const accent = REPORT_TYPES.some(rt => rt.kind === at.kind) ? '#7c3aed' : COMPARE_TYPES.some(ct => ct.kind === at.kind) ? '#f59e0b' : '#4ade80';
                  return (
                    <tr key={key}>
                      <td colSpan={7} style={{ padding: 0 }}>
                        <div style={{ borderLeft: `3px solid ${accent}`, marginBottom: 1 }}>
                          <div style={{ padding: '4px 12px', fontSize: 11, color: '#94a3b8', background: '#0f1923', borderBottom: minimized ? 'none' : '1px solid #1e293b', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                            onClick={() => setMinimizedPanels(prev => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; })}
                          >
                            <span style={{ color: accent, fontWeight: 700, fontSize: 13, width: 14, textAlign: 'center' }}>{minimized ? '+' : '−'}</span>
                            <strong style={{ color: accent }}>{at.label}</strong>
                            {minimized && analysisCache[key]?.ok && <span style={{ color: '#94a3b8', marginLeft: 4 }}>({analysisCache[key]?.formula || ''})</span>}
                            {analysisCache[key] && !analysisLoading[key] && (
                              <button
                                title="Recalculate this analysis"
                                onClick={e => { e.stopPropagation(); recalculateAnalysis(f.index, at.kind); }}
                                style={{ marginLeft: 'auto', background: 'transparent', border: '1px solid #f59e0b', borderRadius: 4, color: '#f59e0b', fontSize: 10, padding: '1px 6px', cursor: 'pointer', lineHeight: '16px' }}
                              >
                                Recalculate
                              </button>
                            )}
                          </div>
                          {!minimized && <AnalysisPanel
                            kind={at.kind}
                            data={analysisCache[key]}
                            loading={analysisLoading[key]}
                            frame={f}
                            candidateFingerprint={_fpFrame(f)}
                            onPatentProceed={() => { skipPatentSearchRef.current.add(key); fetchAnalysis(f.index, at.kind); }}
                            onPatentCancel={() => {
                              setAnalysisCache(p => { const n = { ...p }; delete n[key]; return n; });
                              setActiveAnalyses(prev => { const n = new Set(prev); n.delete(key); return n; });
                            }}
                            onPatentRetry={() => fetchAnalysis(f.index, at.kind)}
                            onDftJobSubmit={() => { setAnalysisLoading(p => ({ ...p, [key]: true })); submitDftJob(f.index, at.kind); }}
                            onDftJobCancel={() => {
                              setAnalysisCache(p => { const n = { ...p }; delete n[key]; return n; });
                              setActiveAnalyses(prev => { const n = new Set(prev); n.delete(key); return n; });
                              clearJob(`${_fpFrame(f)}::${at.kind}`);
                            }}
                            onDftJobStop={() => {
                              setAnalysisCache(p => ({ ...p, [key]: null }));
                              clearJob(`${_fpFrame(f)}::${at.kind}`);
                            }}
                            onDftJobPollUpdate={(result) => handleDftPollUpdate(f.index, at.kind, result)}
                          />}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {truncated && <div className="text-warning small mt-2">Preview truncated. Download for full contents.</div>}
      {downloadUrl && <a className="btn btn-outline-light btn-sm mt-2" href={downloadUrl} target="_blank" rel="noreferrer">Download full extxyz</a>}
      {/* Spec 0.7: a merged multi-job view saves the merged text it parsed (every job). */}
      {!downloadUrl && downloadText && (
        <button
          type="button"
          className="btn btn-outline-light btn-sm mt-2"
          onClick={() => saveAs(new Blob([downloadText], { type: 'text/plain;charset=utf-8' }), 'generated_crystals.extxyz')}
        >Download full extxyz</button>
      )}
      <Modal
        open={!!viewer3dFrame}
        title={viewer3dFrame ? `3D Structure — ${viewer3dFrame.formula || 'Crystal'} (#${viewer3dFrame.rank})` : '3D Viewer'}
        onClose={() => { setViewer3dFrame(null); setViewer3dCif(null); }}
      >
        {viewer3dFrame && (
          <div className="d-flex flex-column gap-2">
            <CrystalViewer3D frame={viewer3dFrame} cifText={viewer3dCif} customBonds />
            <div className="d-flex gap-3" style={{ color: '#94a3b8', fontSize: 12 }}>
              <span><strong>Formula:</strong> {viewer3dFrame.formula || '—'}</span>
              <span><strong>Atoms:</strong> {viewer3dFrame.n}</span>
              {viewer3dFrame.density != null && <span><strong>Density:</strong> {viewer3dFrame.density} g/cm³</span>}
              {viewer3dFrame.minDist != null && <span><strong>d_min:</strong> {viewer3dFrame.minDist} Å</span>}
              {viewer3dFrame.packingFraction != null && <span><strong>Packing:</strong> {(viewer3dFrame.packingFraction * 100).toFixed(1)}%</span>}
            </div>
          </div>
        )}
      </Modal>

      {latticeCandidateIdx != null && (() => {
        const latticeFrame = frames.find(f => f.index === latticeCandidateIdx);
        if (!latticeFrame) return null;
        const cachedAnalyses = {
          dft_elastic: analysisCache[`${latticeCandidateIdx}::dft_elastic`],
          dft_elastic_tensor: analysisCache[`${latticeCandidateIdx}::dft_elastic_tensor`],
        };
        const latticeCandidate = {
          cif: latticeCif || cifCache[latticeCandidateIdx] || latticeFrame.cif || null,
          props: buildLatticeProps({ density: latticeFrame.density }, cachedAnalyses),
        };
        return (
          <LatticeWizard
            candidate={latticeCandidate}
            candidateFingerprint={_fpFrame(latticeFrame)}
            searchObjectives={searchObjectives}
            onClose={() => { setLatticeCandidateIdx(null); setLatticeCif(null); }}
            onDftPollUpdate={(result) => handleDftPollUpdate(latticeCandidateIdx, 'dft_elastic_tensor', result)}
          />
        );
      })()}

    </>
  );
}

const CANDIDATE_STAGES = [
  { value: 'discovered',        label: 'Discovered',       color: '#94a3b8' },
  { value: 'shortlisted',       label: 'Shortlisted',      color: '#38bdf8' },
  { value: 'sent_to_synthesis', label: 'Sent to Synthesis', color: '#f59e0b' },
  { value: 'validated',         label: 'Validated',         color: '#4ade80' },
  { value: 'published',         label: 'Published',        color: '#7c3aed' },
];

function StageDropdown({ fingerprint, currentStage }) {
  const { updateCandidateStage } = useAppContext() || {};
  const stage = CANDIDATE_STAGES.find(s => s.value === currentStage) || CANDIDATE_STAGES[0];
  return (
    <select
      value={stage.value}
      onChange={e => updateCandidateStage?.(fingerprint, e.target.value)}
      onClick={e => e.stopPropagation()}
      style={{
        background: '#0a1120',
        border: `1px solid ${stage.color}44`,
        borderRadius: 4,
        color: stage.color,
        fontSize: 11,
        padding: '2px 6px',
        cursor: 'pointer',
        outline: 'none',
      }}
    >
      {CANDIDATE_STAGES.map(s => (
        <option key={s.value} value={s.value} style={{ color: '#e2e8f0' }}>{s.label}</option>
      ))}
    </select>
  );
}

function CopyLinkButton({ fingerprint }) {
  const [copied, setCopied] = React.useState(false);
  const url = `${window.location.origin}/candidate/${encodeURIComponent(fingerprint)}`;
  return (
    <button
      title="Copy shareable link"
      onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      style={{
        background: 'transparent', border: '1px solid #334155', borderRadius: 4,
        color: copied ? '#4ade80' : '#64748b', fontSize: 11, padding: '2px 6px',
        cursor: 'pointer', transition: 'color 0.2s',
      }}
    >
      {copied ? 'Copied!' : 'Link'}
    </button>
  );
}

function SavedCrystalsPage() {
  const navigate = useNavigate();
  const { savedCandidates = [] } = useAppContext() || {};
  const [stageFilter, setStageFilter] = useState(null);
  // #40 — Memoize filtered arrays so downstream useMemo deps are stable
  const allCrystals = React.useMemo(() => savedCandidates.filter(c => c.frame?.type !== 'molecule'), [savedCandidates]);
  const allMolecules = React.useMemo(() => savedCandidates.filter(c => c.frame?.type === 'molecule'), [savedCandidates]);
  const crystalCandidates = React.useMemo(() => stageFilter ? allCrystals.filter(c => (c.stage || 'discovered') === stageFilter) : allCrystals, [allCrystals, stageFilter]);
  const moleculeCandidates = React.useMemo(() => stageFilter ? allMolecules.filter(c => (c.stage || 'discovered') === stageFilter) : allMolecules, [allMolecules, stageFilter]);

  const { frames, meta, initialCache, sourceLabels, stageMap } = React.useMemo(() => {
    const frames = crystalCandidates.map((c, idx) => {
      const f = { ...c.frame };
      if (c.mp_match) f.mp_match = c.mp_match;
      // Re-index to guarantee unique indices across saved candidates from different runs
      f.index = idx;
      f.rank = idx + 1;
      // Reconstruct atomLines/comment if missing (loaded from Firestore with raw elements/positions)
      if (!f.atomLines && f.elements?.length && f.positions?.length) {
        f.atomLines = f.elements.map((el, j) => {
          const pos = f.positions[j] || [0, 0, 0];
          return `${el}  ${pos[0]}  ${pos[1]}  ${pos[2]}`;
        });
      }
      if (!f.comment && f.lattice_matrix?.length === 3) {
        f.comment = `Lattice="${f.lattice_matrix.flat().join(' ')}" Properties=species:S:1:pos:R:3`;
      }
      // Recompute structural metrics if missing (older saves without full frame)
      if (f.density == null && f.lattice_matrix?.length === 3 && f.elements?.length) {
        const vol = _cellVolume(f.lattice_matrix);
        if (vol > 0) {
          let totalMass = 0, sphereVol = 0;
          for (const el of f.elements) { totalMass += _AM[el] || 0; const r = _AR[el]; if (r) sphereVol += (4/3)*Math.PI*r*r*r; }
          f.density = Math.round((totalMass * 1.66054e-24) / (vol * 1e-24) * 1000) / 1000;
          if (sphereVol > 0) f.packingFraction = Math.round((sphereVol / vol) * 10000) / 10000;
        }
      }
      if (f.minDist == null && f.positions?.length) f.minDist = _minDist(f.positions);
      if (!f.n) f.n = f.elements?.length || f.atomLines?.length || 0;
      return f;
    });
    const cifs = [];
    const trajs = [];
    const cifTexts = {};
    crystalCandidates.forEach((c, idx) => {
      if (c.cifUrl != null) cifs[idx] = c.cifUrl;
      if (c.trajUrl != null) trajs[idx] = c.trajUrl;
      // Inline CIF text (persisted in frame or candidate doc)
      if (!c.cifUrl && c.frame?.cif) cifTexts[idx] = c.frame.cif;
    });
    const initialCache = {};
    const sourceLabels = {};
    crystalCandidates.forEach((c, idx) => {
      const analyses = c.cachedAnalyses || {};
      Object.entries(analyses).forEach(([kind, data]) => {
        if (data) initialCache[`${idx}::${kind}`] = data;
      });
      sourceLabels[idx] = {
        source: [c.source?.service, c.source?.model].filter(Boolean).join(' · ') || null,
        runTs: c.source?.runTs || null,
      };
    });
    const stageMap = {};
    crystalCandidates.forEach((c, idx) => { stageMap[idx] = c.stage || 'discovered'; });
    return { frames, meta: { cifs, trajectories: trajs, cifTexts }, initialCache, sourceLabels, stageMap };
  }, [crystalCandidates]);

  if (savedCandidates.length === 0) {
    return (
      <div style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center', padding: '80px 24px' }}>
        <div style={{ fontSize: 36, marginBottom: 16 }}>☆</div>
        <p style={{ color: '#94a3b8', marginBottom: 12 }}>No saved candidates yet.</p>
        <p style={{ color: '#64748b', fontSize: 13, marginBottom: 28 }}>
          Click the ★ button on any crystal or molecule in the results to save it here for further review.
        </p>
        <button className="btn btn-outline-success btn-sm" onClick={() => navigate('/results')}>Go to Results</button>
      </div>
    );
  }

  const stageCounts = {};
  savedCandidates.forEach(c => { const s = c.stage || 'discovered'; stageCounts[s] = (stageCounts[s] || 0) + 1; });

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: '0 24px 32px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 0 16px' }}>
        <span style={{ color: '#94a3b8', fontSize: 14 }}>Saved Candidates</span>
        <span style={{ color: '#64748b', fontSize: 13 }}>{savedCandidates.length} saved</span>
      </div>

      {/* Stage filter chips */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
        <button
          onClick={() => setStageFilter(null)}
          style={{
            background: !stageFilter ? '#1e293b' : 'transparent',
            border: `1px solid ${!stageFilter ? '#475569' : '#1e293b'}`,
            borderRadius: 12, padding: '3px 10px', fontSize: 11,
            color: !stageFilter ? '#e2e8f0' : '#64748b', cursor: 'pointer',
          }}
        >
          All ({savedCandidates.length})
        </button>
        {CANDIDATE_STAGES.map(s => {
          const count = stageCounts[s.value] || 0;
          if (!count) return null;
          const active = stageFilter === s.value;
          return (
            <button key={s.value}
              onClick={() => setStageFilter(active ? null : s.value)}
              style={{
                background: active ? `${s.color}22` : 'transparent',
                border: `1px solid ${active ? s.color : '#1e293b'}`,
                borderRadius: 12, padding: '3px 10px', fontSize: 11,
                color: active ? s.color : '#64748b', cursor: 'pointer',
              }}
            >
              {s.label} ({count})
            </button>
          );
        })}
      </div>

      {crystalCandidates.length > 0 && (
        <>
          {moleculeCandidates.length > 0 && (
            <div style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Crystals</div>
          )}
          <div className="results-full-width">
            <ExtxyzTable key={extxyzRemountKey(stageFilter, crystalCandidates)} frames={frames} meta={meta} initialCache={initialCache} sourceLabels={sourceLabels} stageMap={stageMap} />
          </div>
        </>
      )}

      {moleculeCandidates.length > 0 && (
        <>
          {crystalCandidates.length > 0 && (
            <div style={{ borderTop: '1px solid #1e3a5f', margin: '24px 0 16px' }} />
          )}
          <div style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Molecules</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {moleculeCandidates.map(c => (
              <div key={c.id} style={{ background: '#0a1120', border: '1px solid #1e3a5f', borderRadius: 8 }}>
                <div style={{ padding: '8px 14px', borderBottom: '1px solid #1e293b', display: 'flex', gap: 12, alignItems: 'center' }}>
                  {c.source?.service && <span style={{ fontSize: 11, color: '#94a3b8' }}>{[c.source.service, c.source.model].filter(Boolean).join(' · ')}</span>}
                  {c.source?.runTs && <span style={{ fontSize: 10, color: '#475569' }}>{new Date(c.source.runTs).toLocaleString()}</span>}
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                    <StageDropdown fingerprint={c.fingerprint} currentStage={c.stage || 'discovered'} />
                    <CopyLinkButton fingerprint={c.fingerprint} />
                  </span>
                </div>
                <MoleculeTable
                  smiles={[c.frame.smiles]}
                  savedMode
                  initialCache={Object.fromEntries(
                    Object.entries(c.cachedAnalyses || {})
                      .map(([kind, data]) => [`0::${kind}`, data])
                  )}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const _ANALYSIS_KIND_LABELS = {
  symmetry: 'Symmetry', voronoi: 'Voronoi Coordination', bond_distribution: 'Bond Distribution',
  dft_surrogate: 'DFT Surrogate (CHGNet)', phase_stability: 'Phase Stability', mp_neighbors: 'MP Neighbors',
  dft_singlepoint: 'DFT Single-Point', dft_relax: 'DFT Relaxation', dft_elastic: 'DFT Elastic',
  dft_elastic_tensor: 'Elastic Tensor (DFT)',
  dft_dos: 'DFT DOS', dft_bandstructure: 'DFT Band Structure', dft_spin_density: 'Spin Density',
  dft_optical: 'DFT Optical', dft_effective_mass: 'Effective Mass',
  qe_singlepoint: 'QE Single-Point', qe_relax: 'QE Relaxation', qe_elastic: 'QE Elastic',
  qe_dos: 'QE DOS', qe_bandstructure: 'QE Band Structure', qe_optical: 'QE Optical',
  qe_effective_mass: 'QE Effective Mass', qe_phonon: 'QE Phonon', qe_thermal: 'QE Thermal',
  economic_report: 'Economic Report', scientific_doc: 'Scientific Document', patent_packet: 'Patent Packet',
};
const _COMPARE_KINDS = new Set(['phase_stability', 'mp_neighbors']);
const _REPORT_KINDS = new Set(['economic_report', 'scientific_doc', 'patent_packet']);

function CandidateDetailPage() {
  const { encodedFingerprint } = useParams();
  const navigate = useNavigate();
  const { orgMember, isSuperAdmin } = useAuth() || {};
  const orgId = orgMember?.orgId || (isSuperAdmin ? 'crimsonscan' : null);

  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orgId || !encodedFingerprint) return;
    let cancelled = false;
    setLoading(true);
    const fp = decodeURIComponent(encodedFingerprint);
    dbLoadCandidateByFingerprint(orgId, fp).then(result => {
      if (cancelled) return;
      if (!result) { setError('Candidate not found or not saved.'); setLoading(false); return; }
      setCandidate(result);
      setLoading(false);
    }).catch(e => {
      if (cancelled) return;
      console.error('[CandidateDetailPage] load failed:', e);
      setError('Failed to load candidate.');
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [orgId, encodedFingerprint]);

  if (loading) return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '80px 24px', textAlign: 'center' }}>
      <span style={{ color: '#94a3b8', fontSize: 14 }}>Loading candidate...</span>
    </div>
  );

  if (error || !candidate) return (
    <div style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center', padding: '80px 24px' }}>
      <p style={{ color: '#f87171', marginBottom: 16 }}>{error || 'Candidate not found.'}</p>
      <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/saved')}>Back to Saved</button>
    </div>
  );

  const fp = candidate.fingerprint;
  const analyses = candidate.analyses || {};

  // ── Reconstruct frame from DB data (same logic as SavedCrystalsPage) ──
  const f = { ...(candidate.frame || {}) };
  // Merge top-level candidate fields as fallbacks (these are always stored reliably)
  if (!f.formula && candidate.formula) f.formula = candidate.formula;
  if (!f.smiles && candidate.smiles) f.smiles = candidate.smiles;
  if (!f.elements && candidate.elements) f.elements = candidate.elements;
  if (!f.positions && candidate.positions) f.positions = candidate.positions;
  if (!f.lattice_matrix && candidate.lattice_matrix) f.lattice_matrix = candidate.lattice_matrix;
  if (!f.lattice_abc && candidate.lattice_abc) f.lattice_abc = candidate.lattice_abc;
  if (!f.cif && candidate.cif) f.cif = candidate.cif;
  if (!f.props && candidate.props) f.props = candidate.props;
  if (!f.n) f.n = candidate.n || f.elements?.length || 0;
  if (candidate.score != null && f.score == null) f.score = candidate.score;
  if (candidate.mp_match) f.mp_match = candidate.mp_match;

  // Rebuild atomLines + comment if missing (needed for extxyz viewer path)
  if (!f.atomLines && f.elements?.length && f.positions?.length) {
    f.atomLines = f.elements.map((el, j) => {
      const pos = f.positions[j] || [0, 0, 0];
      return `${el}  ${pos[0]}  ${pos[1]}  ${pos[2]}`;
    });
  }
  if (!f.comment && f.lattice_matrix?.length === 3) {
    f.comment = `Lattice="${f.lattice_matrix.flat().join(' ')}" Properties=species:S:1:pos:R:3`;
  }
  // Recompute structural metrics if missing
  if (f.density == null && f.lattice_matrix?.length === 3 && f.elements?.length) {
    const vol = _cellVolume(f.lattice_matrix);
    if (vol > 0) {
      let totalMass = 0, sphereVol = 0;
      for (const el of f.elements) { totalMass += _AM[el] || 0; const r = _AR[el]; if (r) sphereVol += (4/3)*Math.PI*r*r*r; }
      f.density = Math.round((totalMass * 1.66054e-24) / (vol * 1e-24) * 1000) / 1000;
      if (sphereVol > 0) f.packingFraction = Math.round((sphereVol / vol) * 10000) / 10000;
    }
  }
  if (f.minDist == null && f.positions?.length) f.minDist = _minDist(f.positions);

  const isMolecule = f.type === 'molecule';

  // Source info
  const sourceLabel = [candidate.source?.service, candidate.source?.model].filter(Boolean).join(' · ') || null;

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 24px 48px' }}>
      {/* Header bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '20px 0 16px', flexWrap: 'wrap' }}>
        <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/saved')}>&larr; Saved</button>
        <span style={{ color: '#e2e8f0', fontSize: 18, fontWeight: 600 }}>
          {f.formula || f.smiles || fp}
        </span>
        <StageDropdown fingerprint={fp} currentStage={candidate.stage || 'discovered'} />
        <CopyLinkButton fingerprint={fp} />
        {sourceLabel && <span style={{ color: '#64748b', fontSize: 12, marginLeft: 4 }}>{sourceLabel}</span>}
      </div>

      {/* 3D Viewer for crystals */}
      {!isMolecule && (f.atomLines?.length || (typeof f.cif === 'string' && f.cif)) && (
        <div style={{ background: '#0f1923', borderRadius: 8, border: '1px solid #1e3a5f', padding: 12, marginBottom: 20 }}>
          <CrystalViewer3D
            frame={f.atomLines?.length ? f : undefined}
            cifText={!f.atomLines?.length && typeof f.cif === 'string' ? f.cif : undefined}
            customBonds
          />
        </div>
      )}

      {/* Properties summary */}
      {f.props && Object.keys(f.props).length > 0 && (
        <div style={{ background: '#0a1120', borderRadius: 8, border: '1px solid #1e3a5f', padding: '12px 16px', marginBottom: 20 }}>
          <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', marginBottom: 8 }}>Properties</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            {Object.entries(f.props).map(([k, v]) => (
              <div key={k} style={{ minWidth: 120 }}>
                <span style={{ color: '#64748b', fontSize: 11 }}>{k.replace(/_/g, ' ')}: </span>
                <span style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 500 }}>{typeof v === 'number' ? v.toFixed(4) : String(v)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Structural info for crystals */}
      {!isMolecule && (
        <div style={{ background: '#0a1120', borderRadius: 8, border: '1px solid #1e3a5f', padding: '12px 16px', marginBottom: 20 }}>
          <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', marginBottom: 8 }}>Structure</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, color: '#e2e8f0', fontSize: 13 }}>
            {f.n > 0 && <span><span style={{ color: '#64748b' }}>Atoms:</span> {f.n}</span>}
            {f.density != null && <span><span style={{ color: '#64748b' }}>Density:</span> {f.density} g/cm³</span>}
            {f.packingFraction != null && <span><span style={{ color: '#64748b' }}>Packing:</span> {f.packingFraction}</span>}
            {f.minDist != null && <span><span style={{ color: '#64748b' }}>Min dist:</span> {f.minDist} Å</span>}
            {f.score != null && <span><span style={{ color: '#64748b' }}>Score:</span> {f.score.toFixed(3)}</span>}
            {f.lattice_abc && <span><span style={{ color: '#64748b' }}>Lattice:</span> {f.lattice_abc.map(v => typeof v === 'number' ? v.toFixed(2) : v).join(' × ')} Å</span>}
          </div>
        </div>
      )}

      {/* Analyses — rendered through AnalysisPanel for proper formatting */}
      {Object.keys(analyses).length > 0 && (
        <div style={{ marginBottom: 20 }}>
          {Object.entries(analyses).map(([kind, data]) => {
            const borderColor = _REPORT_KINDS.has(kind) ? '#7c3aed' : _COMPARE_KINDS.has(kind) ? '#f59e0b' : '#4ade80';
            const label = _ANALYSIS_KIND_LABELS[kind] || kind.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            return (
              <div key={kind} style={{ background: '#0f1923', borderRadius: 8, border: `1px solid ${borderColor}`, marginBottom: 10, overflow: 'hidden' }}>
                <details>
                  <summary style={{ padding: '10px 14px', cursor: 'pointer', color: borderColor, fontSize: 13, fontWeight: 600 }}>
                    {label}
                  </summary>
                  <div style={{ padding: '0 14px 14px' }}>
                    <AnalysisPanel kind={kind} data={data} loading={false} frame={f} candidateFingerprint={fp} />
                  </div>
                </details>
              </div>
            );
          })}
        </div>
      )}

      {/* Notes — live chat thread */}
      <div style={{ background: '#0a1120', borderRadius: 8, border: '1px solid #1e3a5f', overflow: 'hidden' }}>
        <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', padding: '12px 16px 0' }}>Notes</div>
        <CandidateNotes fingerprint={fp} />
      </div>
    </div>
  );
}

// Requires the current user to hold the 'seat' role.
// Non-seat members (admin/billing/IT only) are redirected to the admin portal.
function SeatGuard({ children }) {
  const { isSeat, isSuperAdmin, orgMember } = useOrgRole();
  // Allow through if: Firebase not configured (local dev), superadmin, or has seat role
  if (!orgMember && !isSuperAdmin) return children;
  if (isSuperAdmin || isSeat) return children;
  return <Navigate to="/admin" replace />;
}

function NotFoundPage() {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: '60vh', padding: 24, textAlign: 'center',
    }}>
      <div style={{ fontSize: 64, fontWeight: 700, color: '#1e3a5f', marginBottom: 8 }}>404</div>
      <h2 style={{ color: '#e2e8f0', fontWeight: 600, fontSize: '1.2rem', marginBottom: 10 }}>Page not found</h2>
      <p style={{ color: '#94a3b8', fontSize: 14, marginBottom: 24, maxWidth: 400 }}>
        The page you're looking for doesn't exist or has been moved.
      </p>
      <a href="/" style={{
        background: 'transparent', border: '1px solid #1e3a5f', borderRadius: 6,
        color: '#38bdf8', fontSize: 14, fontWeight: 600, padding: '10px 24px',
        textDecoration: 'none',
      }}>Back to Home</a>
    </div>
  );
}

function MobileNudge() {
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('mobile_nudge_dismissed') === '1'; } catch { return false; }
  });
  if (dismissed) return null;
  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem('mobile_nudge_dismissed', '1'); } catch {}
  };
  return (
    <>
      <style>{`
        .mobile-nudge { display: none; }
        @media (max-width: 768px) { .mobile-nudge { display: flex; } }
      `}</style>
      <div
        className="mobile-nudge"
        style={{
          alignItems: 'center', justifyContent: 'space-between', gap: 12,
          padding: '10px 16px', background: '#1e293b', borderBottom: '1px solid #334155',
          fontSize: 13, color: '#cbd5e1', lineHeight: 1.4,
        }}
      >
        <span>For the best experience, open CrimsonScan on a desktop browser. Some features may be limited on mobile.</span>
        <button
          onClick={dismiss}
          style={{
            background: 'transparent', border: '1px solid #475569', borderRadius: 4,
            color: '#94a3b8', fontSize: 12, padding: '4px 12px', cursor: 'pointer',
            whiteSpace: 'nowrap', flexShrink: 0,
          }}
        >Got it</button>
      </div>
    </>
  );
}

function NotProvisionedScreen({ user, signOut }) {
  return (
    <div style={{ minHeight: '100vh', background: '#060d18', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ maxWidth: 440, textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>🔒</div>
        <h2 style={{ color: '#e2e8f0', fontWeight: 700, fontSize: '1.3rem', marginBottom: 10 }}>Access Not Provisioned</h2>
        <p style={{ color: '#94a3b8', fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
          Your account (<strong style={{ color: '#cbd5e1' }}>{user.email}</strong>) hasn't been added to an organisation yet.
          Contact your organisation admin to get access.
        </p>
        <button
          onClick={signOut}
          style={{ background: 'transparent', border: '1px solid #334155', borderRadius: 6, color: '#64748b', fontSize: 13, padding: '8px 20px', cursor: 'pointer' }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

function AppChrome() {
  const ctx = useAppContext();
  const { user, authLoading, provisioned, signOut } = useAuth() || {};

  // Public pages ship prerendered HTML in #prerender (scripts/prerender.mjs) so
  // crawlers that skip JavaScript still see the content. It stays on screen while
  // auth resolves, then goes before the first paint of the live page.
  useLayoutEffect(() => {
    if (!authLoading) document.getElementById('prerender')?.remove();
  }, [authLoading]);

  if (!ctx) return null;

  // Show blank screen while Firebase resolves auth state (avoids flash)
  if (authLoading) return null;

  // Unauthenticated — public marketing site + login/enroll/docs
  if (!user) {
    return (
      <Routes>
        <Route path="/"             element={<PublicLanding />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/pricing"      element={<PricingPage />} />
        <Route path="/signup"       element={<Navigate to="/enroll?intent=pilot" replace />} />
        <Route path="/signup/complete" element={<SignupCompletePage />} />
        <Route path="/login"        element={<LoginPage />} />
        <Route path="/enroll"       element={<EnrollPage />} />
        <Route path="/docs/*"       element={<DocsPage />} />
        <Route path="/search"       element={<Navigate to="/login" replace />} />
        <Route path="/results"      element={<Navigate to="/login" replace />} />
        <Route path="/saved"        element={<Navigate to="/login" replace />} />
        <Route path="/admin"        element={<Navigate to="/login" replace />} />
        <Route path="/candidate/*"  element={<Navigate to="/login" replace />} />
        <Route path="*"             element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Signed in but not provisioned: public pages, the pilot form and the Stripe
  // return page (/signup/complete, for checkouts started before the self-serve
  // plan was retired) stay reachable; everything else gets the lock screen.
  if (!provisioned) {
    return (
      <Routes>
        <Route path="/signup" element={<Navigate to="/enroll?intent=pilot" replace />} />
        <Route path="/signup/complete" element={<SignupCompletePage />} />
        <Route path="/enroll" element={<EnrollPage />} />
        <Route path="/docs/*" element={<DocsPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="*" element={<NotProvisionedScreen user={user} signOut={signOut} />} />
      </Routes>
    );
  }

  const {
    jsonModalOpen,
    setJsonModalOpen,
    jsonModalTitle,
    jsonModalText,
    fileModalOpen,
    setFileModalOpen,
    fileModalTitle,
    fileModalBody,
    fileModalUrl,
    fileModalMode,
    fileModalTruncated,
    fileModalMeta,
  } = ctx;

  const renderFileBody = () => {
    switch (fileModalMode) {
      case 'loading':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 0', gap: 16 }}>
            <div className="spinner-border" role="status" style={{ color: '#f43f5e', width: 40, height: 40 }}>
              <span className="visually-hidden">Loading…</span>
            </div>
            <div style={{ color: '#94a3b8', fontSize: 14 }}>Loading crystals…</div>
          </div>
        );
      case 'binary':
        return (
          <div className="d-flex flex-column gap-2">
            <div>This file can be downloaded for offline inspection.</div>
            {fileModalUrl ? (
              <a className="btn btn-outline-light btn-sm align-self-start" href={fileModalUrl} target="_blank" rel="noreferrer">
                Download
              </a>
            ) : (
              <div className="text-warning">No download URL available.</div>
            )}
          </div>
        );
      case 'error':
        return (
          <div className="d-flex flex-column gap-2">
            <div style={{ color: '#f87171' }}>Couldn't load this file — {fileModalBody || 'unknown error'}.</div>
            {fileModalUrl && (
              <a className="btn btn-outline-light btn-sm align-self-start" href={fileModalUrl} target="_blank" rel="noreferrer">
                Try direct download
              </a>
            )}
          </div>
        );
      case 'info':
        return <p className="mb-0">{fileModalBody || 'No additional details provided.'}</p>;
      case 'text':
      default: {
        // FM4M molecule viewer
        if (fileModalMeta?.molecules && Array.isArray(fileModalMeta?.smiles) && fileModalMeta.smiles.length > 0) {
          return <MoleculeTable
            smiles={fileModalMeta.smiles}
            features={fileModalMeta.features}
            featureNames={fileModalMeta.featureNames}
            featureModel={fileModalMeta.featureModel}
            // GT4SD RT (Part D): per-molecule measurements from the run that produced
            // these molecules, same length and order as smiles. Absent for every other
            // service and for an RT run that measured nothing.
            measurements={fileModalMeta.measurements}
            targetValue={fileModalMeta.targetValue}
            bulkPersist={{
              batchKey: `${fileModalTitle}::${fileModalMeta.smiles.length}::${fileModalMeta.smiles[0] || ''}`,
              canonicalSmiles: fileModalMeta.canonicalSmiles || null,
              base: fileModalMeta.dispatchBase || null,
              source: {
                service: fileModalMeta.service || null,
                run_id: fileModalMeta.serviceRunId || null,
                model: fileModalMeta.featureModel || null,
              },
            }}
          />;
        }
        // Crystal viewer — DB candidates (instant, no network fetch)
        if (Array.isArray(fileModalMeta?.dbCandidates) && fileModalMeta.dbCandidates.length > 0) {
          const frames = candidatesToFrames(fileModalMeta.dbCandidates);
          // Build CIF text map from DB candidates (avoids fetching CIF URLs)
          const cifTexts = {};
          frames.forEach(f => { if (f.cif) cifTexts[f.index] = f.cif; });
          // Spec 0.7: per-frame file lists from each candidate's own URLs, in this sorted
          // order (frames[i].index === i), never the caller's job-order lists.
          const meta = { ...fileModalMeta, cifTexts, cifs: frames.map(f => f.cifUrl), trajectories: frames.map(f => f.trajUrl) };
          return <ExtxyzTable frames={frames} meta={meta} />;
        }
        // Crystal extxyz viewer — fallback (fetches file from service URL)
        const isExtxyz = String(fileModalTitle).toLowerCase().includes('extxyz')
          || String(fileModalTitle).toLowerCase().includes('crystal')
          || /^\d+\s*\nLattice=/m.test(fileModalBody || '');
        if (isExtxyz && fileModalBody) {
          const frames = parseExtxyzFrames(fileModalBody);
          if (frames.length > 0) {
            // Spec 0.7: each frame carries its own CIF and trajectory URL, looked up by its
            // parse index in the (merged, re-indexed) file lists, so bulk capture stores them
            // per candidate and a reopened run lines each crystal up with its own files.
            const frameCifs = Array.isArray(fileModalMeta?.cifs) ? fileModalMeta.cifs : [];
            const frameTrajs = Array.isArray(fileModalMeta?.trajectories) ? fileModalMeta.trajectories : [];
            frames.forEach((f) => { f.cifUrl = frameCifs[f.index] || null; f.trajUrl = frameTrajs[f.index] || null; });
            const mergedJobs = Array.isArray(fileModalMeta?.extxyzUrls);
            return <ExtxyzTable
              frames={frames} meta={fileModalMeta} truncated={fileModalTruncated}
              downloadUrl={mergedJobs ? null : fileModalUrl} downloadText={mergedJobs ? fileModalBody : null}
              bulkPersist={{
                batchKey: fileModalUrl || fileModalTitle,
                base: fileModalMeta?.dispatchBase || null,
                source: {
                  service: 'mattergen',
                  run_ids: fileModalMeta?.mgRunIds || null,
                  model: fileModalMeta?.modelLabel || null,
                },
              }}
            />;
          }
        }
        return (
          <>
            <pre className="json-pre font-monospace" style={{ textAlign: 'left', whiteSpace: 'pre', overflowX: 'auto' }}>
              {fileModalBody || 'No preview available.'}
            </pre>
            {fileModalTruncated && (
              <div className="text-warning small">
                Preview truncated for large files. Use the download button to view the full contents.
              </div>
            )}
            {fileModalUrl && (
              <a className="btn btn-outline-light btn-sm mt-2 align-self-start" href={fileModalUrl} target="_blank" rel="noreferrer">
                Download
              </a>
            )}
          </>
        );
      }
    }
  };

  return (
    <>
      {/* Always-mounted service runners */}
      <MattergenRunner ref={ctx.mattergenRef} onStatus={ctx.setLoadingMessage} baseUrl={ctx.mgBaseUrl} authHeaders={ctx.mgAuthHeaders} />
      <FM4MRunner ref={ctx.fm4mRef} onStatus={ctx.setLoadingMessage} baseUrl={ctx.ibmBaseUrl} authHeaders={ctx.byocActive ? ctx.byocAuthHeaders : undefined} />
      <GT4SDRunner ref={ctx.gt4sdRef} onStatus={ctx.setLoadingMessage} baseUrl={ctx.gt4sdBaseUrl} authHeaders={ctx.byocActive ? ctx.byocAuthHeaders : undefined} />

      {/* Main layout */}
      <div className="App">
        <MobileNudge />
        <Header />
        <PilotExpirationBanner />
        <main style={{ flex: 1 }}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/enroll" element={<EnrollPage />} />
            <Route path="/admin" element={<AdminPortal />} />
            <Route path="/docs/*" element={<DocsPage />} />
            <Route path="/signup" element={<Navigate to="/enroll?intent=pilot" replace />} />
            <Route path="/signup/complete" element={<SignupCompletePage />} />
            <Route path="/search" element={<SeatGuard><SearchPage /></SeatGuard>} />
            <Route path="/results" element={<SeatGuard><ResultsPage /></SeatGuard>} />
            <Route path="/saved" element={<SeatGuard><SavedCrystalsPage /></SeatGuard>} />
            <Route path="/candidate/:encodedFingerprint" element={<SeatGuard><CandidateDetailPage /></SeatGuard>} />
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>
        <Footer />
      </div>

      {/* Modals */}
      <Modal open={jsonModalOpen} title={jsonModalTitle || 'Plan JSON'} onClose={() => setJsonModalOpen(false)}>
        <CodeJson text={jsonModalText} />
      </Modal>
      <Modal open={fileModalOpen} title={fileModalTitle || 'File preview'} onClose={() => setFileModalOpen(false)}>
        <div className="d-flex flex-column gap-2 text-start">
          {renderFileBody()}
        </div>
      </Modal>

      {/* Floating feedback widget (bug report / feature request → Linear) */}
      <FeedbackWidget />

      {/* Loading overlay */}
      <LoadingOverlay loading={ctx.loading} loadingMessage={ctx.loadingMessage} progress={ctx.progress} onCancel={ctx.cancelAll} />
    </>
  );
}

export default function AppShell() {
  return (
    <AppProvider>
      <AppChrome />
    </AppProvider>
  );
}
